import type { DrillId } from '../game/drills';
import type { FeedbackContext, FeedbackKind } from '../state/feedback';
import type { TierRank } from '../game/types';
import type { Topology } from '../game/topology';
import type { Locale } from './locale';
import { locale } from './locale';

/**
 * Every player-facing string, one dictionary per language.
 *
 * Nothing the player can read is written anywhere else. The rules layer holds
 * ids, keys and numbers; the words for them live here, so adding a language is
 * adding a dictionary and never a hunt through `src/`. The copy obeys
 * STYLEGUIDE §10 in every language: operator voice, sentence case, no
 * em-dashes, the game's own vocabulary.
 *
 * Entries are plain strings, or functions where a value rides inside the
 * sentence — message functions localize, concatenation doesn't.
 *
 * What stays outside, deliberately:
 * - index.html's authored copy. The STATIC list below overwrites it at boot,
 *   so the markup text is a pre-JS fallback, never a second source of truth.
 * - The share text's emoji (the share is the one place emoji belong, §6), the
 *   wordmark, and the footer dedication (identity, not information).
 * - Stable keys that happen to be English words: the tier ranks and drill ids
 *   that storage and the usage stream file results under. Those are data, and
 *   `ranks` below is where they turn back into words.
 */

/** One drill's words: the yard's row and the lesson line above the board. */
interface DrillCopy {
  /** Player-facing name. */
  name: string;
  /** The lesson, stated as a fact of the physics, in one line. */
  teaches: string;
}

// ---------------------------------------------------------------- English --

const EN_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Light the houses',
    teaches:
      'Tap dry ground to raise a tower. Power flows to everything in reach. Light all three houses.',
  },
  'tuning-rearrange': {
    name: 'Move towers, raise the score',
    teaches:
      'The grid works, but the towers sit in poor spots. Take one down, place it somewhere better, and watch the score rise.',
  },
  'diagonal-tax-weave': {
    name: 'Diagonals cost more',
    teaches:
      'A diagonal wire reaches no farther than a straight one, but costs 41% more. Straighten the route to save wire.',
  },
  'triangle-trap-fan': {
    name: 'Avoid triangles',
    teaches:
      'A wire appears between every pair of towers in reach, even when it helps nothing. Three towers in a triangle get three wires. Break the triangle and one of those wires disappears.',
  },
  'triangle-trap-delta': {
    name: 'One tower can replace two',
    teaches:
      "Two towers close together get a wire between them on top of their own lines. One tower in the middle can do the pair's job without the extra wire.",
  },
  'exact-spacing-footings': {
    name: 'Two cells apart',
    teaches:
      'Towers spaced exactly two cells apart connect only to their neighbours. Any closer and extra wires appear. Keep the spacing exact.',
  },
  'junction-neck': {
    name: 'One tower, three houses',
    teaches:
      'A single tower within reach of all three houses can power them all. Find that spot instead of building a branch to each house.',
  },
  'junction-approach': {
    name: 'Separate paths pay separately',
    teaches:
      'A separate path to each house uses its own wire the whole way. Run one line toward all three, and let the last tower feed them.',
  },
  'junction-late-split': {
    name: 'Split as late as possible',
    teaches:
      'When the houses pull in two directions, the line has to split. Carry one shared line as far as it can go, then split close to the houses. An early split pays for two long lines.',
  },
  'lake-narrows': {
    name: 'Pick the right crossing',
    teaches:
      'Wires can cross water only where it is one cell wide. This water narrows in two places. Crossing in line with the plant and the houses makes the shortest route.',
  },
  'lake-west-arm': {
    name: 'Go around the water',
    teaches:
      'The water ends before the map edge. Going around the end is shorter than any crossing.',
  },
};

/**
 * The named shape of a reference layout (GDD §3.5).
 *
 * The classification is a pure function of the puzzle, so every device names
 * the same shape; these are the words that shape wears, and they translate
 * like any other copy. The definite article is part of the name in English:
 * "🏆 Superconductor · the river kiss" is a name for a thing, not a title.
 */
const EN_TOPOLOGIES: Record<Topology, string> = {
  spine: 'the spine',
  'y-split': 'the Y-split',
  backbranch: 'the backbranch',
  'river-kiss': 'the river kiss',
  crossbar: 'the crossbar',
};

/** The rank a reading earns, as one word — the interface's own register. */
const EN_RANKS: Record<TierRank, string> = {
  superconductor: 'Superconductor',
  master: 'Master',
  journeyman: 'Journeyman',
  apprentice: 'Apprentice',
};

/**
 * The drop box's kind chips (GDD §13).
 *
 * The ids are data — they go on the wire and the backend holds the same four —
 * so they stay English keys like the tier ranks do. These are the words.
 */
const EN_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Something broke',
  idea: 'An idea',
  grid: 'This grid',
  other: 'Something else',
};

/** The mode a message was sent from, as the word the panel prints. */
const EN_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'daily',
  archive: 'archive',
  practice: 'practice',
  drill: 'drill',
};

/** The rank spelled out in full, which is the register the share speaks in. */
const EN_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Superconductor',
  master: 'Master Lineworker',
  journeyman: 'Journeyman Lineworker',
  apprentice: 'Apprentice Lineworker',
};

const EN = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'Towers need dry ground. Wires can span the water.',
  denyMtn: 'Too steep for a tower. Wires can clear the peak.',
  denyLimit: 'Out of towers. Take one down first.',
  gridFailed: 'The grid failed to start. Try reloading.',
  newBest: (mw: string) => `New best: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Ready to play offline.',
  resultCopied: 'Result copied.',
  copyBlocked: 'Copy blocked. A screenshot works too.',

  // ---------- the result card ----------
  perfectGrid: 'PERFECT GRID!',
  lineBeaten: 'LINE BEATEN',
  noteBeatEngineers: 'You beat the engineers.',
  noteGridDark: 'Grid dark. Rebuild to keep tuning.',
  takeN: (n: number) => `TAKE ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW to ${rank}. Keep tuning.`,
  noteFlawless: 'Flawless. Never took a tower down.',
  noteLessWire: (pct: number) => `${pct}% less wire than the naive line.`,
  noteLessWireTuning: (pct: number) => `${pct}% less wire than the naive line. Keep tuning.`,
  legendReveal: "Once the day is over, the engineers' own grid opens for study.",
  chipBest: 'best',
  chipRank: 'rank',
  chipMoves: 'moves',
  chipNaive: 'naive line',
  firstTake: 'first take',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Next grid in <b>${wait}</b>.`,
  newGridReady: 'New grid ready.',
  /** The units `formatWait` builds "9h 41m" out of. */
  waitHours: 'h',
  waitMinutes: 'm',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Daily grid <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Practice</strong> · anything goes',
  daytagDrill: '<strong>Drill</strong>',

  // ---------- drills ----------
  drills: EN_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` The naive line reads <b>${mw}</b> MW. Beat it.`,
  nextDrill: 'Next drill',
  todaysGrid: "Today's grid",

  // ---------- the study reveal ----------
  revealShow: "Reveal the engineers' grid",
  revealHide: "Hide the engineers' grid",

  // ---------- archive, drills list, records ----------
  rowPlay: 'play',
  rowStudied: 'studied',
  recordsEmpty: 'Nothing logged yet. Energize a grid to open the record.',
  recStreak: 'streak',
  recBestRun: 'best run',
  recGrids: 'grids',
  recPerfect: 'perfect',
  recFlawless: 'flawless',
  recBeatEngineers: 'beat the engineers',
  recMedian: 'median',
  recTunedPast: 'tuned past first take',
  recStudied: 'studied',
  recDrills: 'drills',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'the field',
  /**
   * The head line's two branches, each a whole clause rather than a label and
   * a number: the readout's words have to scan as a sentence even though its
   * numbers are set in mono. Every language builds its own word order, and the
   * `<b>` marks which spans are data.
   */
  fieldPlayed: (players: string) => `<b>${players}</b> played`,
  fieldStanding: (pct: number, players: string) =>
    `you beat <b>${pct}%</b> of <b>${players}</b> players`,
  /** Screen-reader marker on the player's own row, beside the sighted ▸. */
  fieldYou: 'you',
  fieldAverage: 'average',
  fieldYoung: 'Standings still coming in. Final tomorrow.',
  /**
   * The bar panel's two sections, and the marks the second one counts. The
   * words are the records block's, said of the field rather than of one
   * player, which is a different sentence in most languages.
   */
  fieldRanks: 'ranks',
  yesterdayStanding: (pct: number, players: string) =>
    `Yesterday: you beat ${pct}% of ${players} players.`,
  yesterdayPlayed: (players: string, avg: string) => `Yesterday: ${players} played${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW average`,

  // ---------- the reference's own vocabulary ----------
  topologies: EN_TOPOLOGIES,
  ranks: EN_RANKS,
  ranksFull: EN_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'practice',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · first take`,
  shareBeatRef: 'beat the best known grid!',
  shareFlawless: '✨ flawless',
  shareStudied: '📖 studied',
  shareStreak: (days: number) => `🔥 ${days}-day streak`,
  shareChain: (n: number) => `🔗 ${n} grids in a row`,

  // ---------- share actions ----------
  copyResult: 'Copy result',
  shareResult: 'Share result',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Flawless · 1 in ${oneIn} today`,
  claimChain: (n: number) => `${n} grids in a row`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · the daily power-line puzzle',

  // ---------- the language picker ----------
  settingLanguage: 'Language',
  langTitle: 'LANGUAGE',
  langLabel: 'Language',

  // ---------- the controls row ----------
  helpBtn: 'How to play',
  practiceBtn: 'Practice',
  newMapBtn: 'New map',
  archiveBtn: 'Archive',
  yardBtn: 'Drills',
  todayBtn: 'Today',
  navProfile: 'Profile',
  profileTitle: 'PROFILE',
  navLabel: 'Sections',
  yesterdayBtn: "Yesterday's grid",
  nextGridBtn: 'One more grid',
  moveOnTitle: 'STILL ROOM ON THIS ONE',
  moveOnIntro: (gap: string, rank: string) => `You are ${gap} MW from ${rank} on this grid.`,
  moveOnCancel: 'Keep tuning',
  moveOnConfirm: 'Next grid',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Wirespan puzzle board. Arrow keys move the survey cursor; Enter or Space places or removes a tower.',

  // ---------- the operator readout ----------
  hudTowers: 'Towers',
  hudHouses: 'Houses',
  hudOutput: 'Output',
  hudStreak: 'Streak',

  // ---------- archive ----------
  archiveLabel: 'Archive',
  archiveTitle: 'ARCHIVE',
  close: 'Close',

  // ---------- drills overlay ----------
  yardLabel: 'Drills',
  yardTitle: 'DRILLS',
  yardIntro: 'Lessons to improve your game.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Study the reference',
  studyTitle: "STUDY THE ENGINEERS' GRID",
  studyIntro:
    'This grid is not perfect yet, so studying it marks the day <b>📖 studied</b> ' +
    'in your archive and in its share. A studied perfect is still a perfect. It just says so.',
  studyCancel: 'Not yet',
  studyConfirm: 'Study it',

  // ---------- settings ----------
  settingsLabel: 'Settings',
  settingsTitle: 'SETTINGS',
  setSound: 'Sound',
  setHaptics: 'Haptics',
  setMotion: 'Reduce motion',
  setContrast: 'High contrast',
  setText: 'Larger text',
  setUsage: 'Usage data',
  privacyBtn: 'Privacy policy',
  settingsDone: 'Done',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Send feedback',
  feedbackLabel: 'Send feedback',
  feedbackTitle: 'SEND FEEDBACK',
  feedbackIntro: 'One message to the person who built this. It goes nowhere else.',
  feedbackKinds: EN_FB_KINDS,
  feedbackPlaceholder: 'Say what happened.',
  /** The textarea's accessible name — it has no visible label of its own. */
  feedbackMessageLabel: 'Your message',
  feedbackLeft: (n: number) => `${n} left`,
  feedbackContact: 'Email, if you want an answer',
  feedbackContactHint: 'you@example.com',
  feedbackModes: EN_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Sent with it: grid #${grid} · ${mode} · build ${build}. Nothing else.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Sent with it: ${mode} · build ${build}. Nothing else.`,
  feedbackSend: 'Send',
  feedbackSending: 'Sending…',
  feedbackDone: 'Sent. Thank you.',
  feedbackFailOffline: 'It did not send, and your message is still here. Try again in a moment.',
  feedbackFailThrottled: 'That is five messages today. The box opens again tomorrow.',
  feedbackFailInvalid: 'That did not go through. Check the address, if you typed one.',

  // ---------- how to play ----------
  helpLabel: 'How to play',
  helpTitle: 'HOW TO PLAY WIRESPAN',
  helpIntro: 'Route power from the plant to <b>every house</b> using the least wire possible.',
  helpRuleBuild: '<b>Tap to build or remove a tower.</b> Wires connect anything within 2 squares.',
  helpRuleGround: '<b>Towers must be placed on open ground.</b> Wires can cross rivers and ridges.',
  helpRuleShorter:
    "<b>Shorter wires deliver more MW.</b> Keep rearranging after everything's lit " +
    'to get your best score.',
  helpRuleDaily:
    '<b>Same grid for the whole world, new at midnight.</b> Practice deals a fresh ' +
    'grid anytime.',
};

/** The shape every language fills. English is the reference dictionary. */
export type Strings = typeof EN;

// ------------------------------------------------------- Spanish (es-419) --

const ES_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Enciende las casas',
    teaches:
      'Toca suelo seco para levantar una torre. La energía fluye hacia todo lo que esté al alcance. Enciende las tres casas.',
  },
  'tuning-rearrange': {
    name: 'Mueve torres, sube el marcador',
    teaches:
      'La red funciona, pero las torres están mal ubicadas. Quita una, colócala en un mejor lugar y mira subir el marcador.',
  },
  'diagonal-tax-weave': {
    name: 'Las diagonales cuestan más',
    teaches:
      'Un cable diagonal no llega más lejos que uno recto, pero cuesta 41% más. Endereza la ruta para ahorrar cable.',
  },
  'triangle-trap-fan': {
    name: 'Evita los triángulos',
    teaches:
      'Aparece un cable entre cada par de torres al alcance, aunque no sirva de nada. Tres torres en triángulo reciben tres cables. Rompe el triángulo y uno de esos cables desaparece.',
  },
  'triangle-trap-delta': {
    name: 'Una torre puede reemplazar a dos',
    teaches:
      'Dos torres juntas reciben un cable entre ellas además de sus propias líneas. Una sola torre en medio hace el trabajo del par sin ese cable de más.',
  },
  'exact-spacing-footings': {
    name: 'A dos casillas de distancia',
    teaches:
      'Las torres separadas exactamente dos casillas se conectan solo con sus vecinas. Más cerca aparecen cables de más. Mantén la separación exacta.',
  },
  'junction-neck': {
    name: 'Una torre, tres casas',
    teaches:
      'Una sola torre al alcance de las tres casas puede alimentarlas a todas. Encuentra ese punto en lugar de construir un ramal hacia cada casa.',
  },
  'junction-approach': {
    name: 'Cada camino aparte se paga aparte',
    teaches:
      'Un camino separado hacia cada casa gasta su propio cable todo el trayecto. Lleva una sola línea hacia las tres y deja que la última torre las alimente.',
  },
  'junction-late-split': {
    name: 'Divide lo más tarde posible',
    teaches:
      'Cuando las casas jalan en dos direcciones, la línea tiene que dividirse. Lleva una línea compartida lo más lejos que puedas y divídela cerca de las casas. Dividir temprano paga dos líneas largas.',
  },
  'lake-narrows': {
    name: 'Elige el cruce correcto',
    teaches:
      'Los cables cruzan el agua solo donde mide una casilla de ancho. Esta agua se angosta en dos lugares. Cruzar en línea con la planta y las casas da la ruta más corta.',
  },
  'lake-west-arm': {
    name: 'Rodea el agua',
    teaches:
      'El agua termina antes del borde del mapa. Rodear la punta es más corto que cualquier cruce.',
  },
};

const ES_TOPOLOGIES: Record<Topology, string> = {
  spine: 'la espina',
  'y-split': 'la Y',
  backbranch: 'el ramal trasero',
  'river-kiss': 'el beso del río',
  crossbar: 'el travesaño',
};

const ES_RANKS: Record<TierRank, string> = {
  superconductor: 'Superconductor',
  master: 'Maestro',
  journeyman: 'Oficial',
  apprentice: 'Aprendiz',
};

const ES_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Algo se rompió',
  idea: 'Una idea',
  grid: 'Esta red',
  other: 'Otra cosa',
};

const ES_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'diaria',
  archive: 'archivo',
  practice: 'práctica',
  drill: 'ejercicio',
};

const ES_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Superconductor',
  master: 'Maestro linero',
  journeyman: 'Oficial linero',
  apprentice: 'Aprendiz linero',
};

const ES: Strings = {
  // ---------- board nudges ----------
  denyWater: 'Las torres necesitan suelo seco. Los cables sí cruzan el agua.',
  denyMtn: 'Demasiado empinado para una torre. Los cables sí pasan sobre la cima.',
  denyLimit: 'No quedan torres. Quita una primero.',
  gridFailed: 'La red no arrancó. Intenta recargar.',
  newBest: (mw: string) => `Nueva marca: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Listo para jugar sin conexión.',
  resultCopied: 'Resultado copiado.',
  copyBlocked: 'No se pudo copiar. Una captura de pantalla también sirve.',

  // ---------- the result card ----------
  perfectGrid: '¡RED PERFECTA!',
  lineBeaten: 'LÍNEA SUPERADA',
  noteBeatEngineers: 'Superaste a los ingenieros.',
  noteGridDark: 'Red apagada. Reconstruye para seguir ajustando.',
  takeN: (n: number) => `INTENTO ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW para ${rank}. Sigue ajustando.`,
  noteFlawless: 'Impecable. Nunca quitaste una torre.',
  noteLessWire: (pct: number) => `${pct}% menos cable que la línea obvia.`,
  noteLessWireTuning: (pct: number) => `${pct}% menos cable que la línea obvia. Sigue ajustando.`,
  legendReveal: 'Al terminar el día, la red de los ingenieros se abre para estudiarla.',
  chipBest: 'mejor',
  chipRank: 'rango',
  chipMoves: 'movimientos',
  chipNaive: 'línea obvia',
  firstTake: 'primer intento',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Próxima red en <b>${wait}</b>.`,
  newGridReady: 'Nueva red lista.',
  waitHours: ' h',
  waitMinutes: ' min',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Red diaria <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Práctica</strong> · vale todo',
  daytagDrill: '<strong>Ejercicio</strong>',

  // ---------- drills ----------
  drills: ES_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` La línea obvia marca <b>${mw}</b> MW. Supérala.`,
  nextDrill: 'Siguiente ejercicio',
  todaysGrid: 'La red de hoy',

  // ---------- the study reveal ----------
  revealShow: 'Ver la red de los ingenieros',
  revealHide: 'Ocultar la red de los ingenieros',

  // ---------- archive, drills list, records ----------
  rowPlay: 'jugar',
  rowStudied: 'estudiada',
  recordsEmpty: 'Aún no hay registros. Energiza una red para abrir el historial.',
  recStreak: 'racha',
  recBestRun: 'mejor racha',
  recGrids: 'redes',
  recPerfect: 'perfectas',
  recFlawless: 'impecables',
  recBeatEngineers: 'superó a los ingenieros',
  recMedian: 'mediana',
  recTunedPast: 'mejoró su primer intento',
  recStudied: 'estudiadas',
  recDrills: 'ejercicios',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'el campo',
  fieldPlayed: (players: string) => `<b>${players}</b> jugaron`,
  fieldStanding: (pct: number, players: string) =>
    `superaste al <b>${pct}%</b> de <b>${players}</b> jugadores`,
  fieldYou: 'tú',
  fieldAverage: 'promedio',
  fieldYoung: 'Los resultados siguen llegando. Definitivo mañana.',
  fieldRanks: 'rangos',
  yesterdayStanding: (pct: number, players: string) =>
    `Ayer: superaste al ${pct}% de ${players} jugadores.`,
  yesterdayPlayed: (players: string, avg: string) => `Ayer: ${players} jugaron${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW de promedio`,

  // ---------- the reference's own vocabulary ----------
  topologies: ES_TOPOLOGIES,
  ranks: ES_RANKS,
  ranksFull: ES_RANKS_FULL,

  // ---------- the share text (GDD §6.3) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'práctica',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · primer intento`,
  shareBeatRef: '¡superó la mejor red conocida!',
  shareFlawless: '✨ impecable',
  shareStudied: '📖 estudiada',
  shareStreak: (days: number) => `🔥 racha de ${days} días`,
  shareChain: (n: number) => `🔗 ${n} redes seguidas`,

  // ---------- share actions ----------
  copyResult: 'Copiar resultado',
  shareResult: 'Compartir resultado',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Impecable · 1 de cada ${oneIn} hoy`,
  claimChain: (n: number) => `${n} redes seguidas`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · el rompecabezas diario de líneas eléctricas',

  // ---------- the language picker ----------
  settingLanguage: 'Idioma',
  langTitle: 'IDIOMA',
  langLabel: 'Idioma',

  // ---------- the controls row ----------
  helpBtn: 'Cómo se juega',
  practiceBtn: 'Práctica',
  newMapBtn: 'Nuevo mapa',
  archiveBtn: 'Archivo',
  yardBtn: 'Ejercicios',
  todayBtn: 'Hoy',
  navProfile: 'Perfil',
  profileTitle: 'PERFIL',
  navLabel: 'Secciones',
  yesterdayBtn: 'La red de ayer',
  nextGridBtn: 'Una red más',
  moveOnTitle: 'AÚN QUEDA MARGEN',
  moveOnIntro: (gap: string, rank: string) => `Te faltan ${gap} MW para ${rank} en esta red.`,
  moveOnCancel: 'Sigue ajustando',
  moveOnConfirm: 'Siguiente red',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Tablero de Wirespan. Las flechas mueven el cursor de trazado; Enter o Espacio coloca o quita una torre.',

  // ---------- the operator readout ----------
  hudTowers: 'Torres',
  hudHouses: 'Casas',
  hudOutput: 'Entrega',
  hudStreak: 'Racha',

  // ---------- archive ----------
  archiveLabel: 'Archivo',
  archiveTitle: 'ARCHIVO',
  close: 'Cerrar',

  // ---------- drills overlay ----------
  yardLabel: 'Ejercicios',
  yardTitle: 'EJERCICIOS',
  yardIntro: 'Lecciones para mejorar tu juego.',

  // ---------- the one confirmation in the game ----------
  studyLabel: 'Estudiar la referencia',
  studyTitle: 'ESTUDIAR LA RED DE LOS INGENIEROS',
  studyIntro:
    'Esta red aún no es perfecta, así que estudiarla marca el día como <b>📖 estudiada</b> ' +
    'en tu archivo y en lo que compartas. Una perfecta estudiada sigue siendo perfecta. Solo lo dice.',
  studyCancel: 'Todavía no',
  studyConfirm: 'Estudiarla',

  // ---------- settings ----------
  settingsLabel: 'Ajustes',
  settingsTitle: 'AJUSTES',
  setSound: 'Sonido',
  setHaptics: 'Vibración',
  setMotion: 'Reducir movimiento',
  setContrast: 'Alto contraste',
  setText: 'Texto más grande',
  setUsage: 'Datos de uso',
  privacyBtn: 'Política de privacidad',
  settingsDone: 'Listo',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Enviar comentarios',
  feedbackLabel: 'Enviar comentarios',
  feedbackTitle: 'ENVIAR COMENTARIOS',
  feedbackIntro: 'Un mensaje para quien construyó esto. No va a ninguna otra parte.',
  feedbackKinds: ES_FB_KINDS,
  feedbackPlaceholder: 'Cuenta qué pasó.',
  feedbackMessageLabel: 'Tu mensaje',
  feedbackLeft: (n: number) => `quedan ${n}`,
  feedbackContact: 'Correo, si quieres respuesta',
  feedbackContactHint: 'tu@ejemplo.com',
  feedbackModes: ES_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Se envía junto: red #${grid} · ${mode} · versión ${build}. Nada más.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Se envía junto: ${mode} · versión ${build}. Nada más.`,
  feedbackSend: 'Enviar',
  feedbackSending: 'Enviando…',
  feedbackDone: 'Enviado. Gracias.',
  feedbackFailOffline: 'No se envió, y tu mensaje sigue aquí. Vuelve a intentarlo en un momento.',
  feedbackFailThrottled: 'Son cinco mensajes hoy. El buzón abre de nuevo mañana.',
  feedbackFailInvalid: 'No pasó. Revisa la dirección, si escribiste una.',

  // ---------- how to play ----------
  helpLabel: 'Cómo se juega',
  helpTitle: 'CÓMO SE JUEGA WIRESPAN',
  helpIntro:
    'Lleva la energía desde la planta hasta <b>todas las casas</b> usando el menor cable posible.',
  helpRuleBuild:
    '<b>Toca para construir o quitar una torre.</b> Los cables conectan todo lo que esté a 2 casillas.',
  helpRuleGround:
    '<b>Las torres van en terreno despejado.</b> Los cables sí cruzan ríos y cordilleras.',
  helpRuleShorter:
    '<b>Menos cable entrega más MW.</b> Sigue reacomodando cuando ya esté todo encendido ' +
    'para lograr tu mejor marca.',
  helpRuleDaily:
    '<b>La misma red para todo el mundo, nueva a medianoche.</b> La práctica reparte una red ' +
    'nueva cuando quieras.',
};

// ------------------------------------------------------------ pt-BR --

// ----------------------------------------------------- Portuguese (pt-BR) --

const PT_BR_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Acenda as casas',
    teaches:
      'Toque em solo seco para levantar uma torre. A energia flui para tudo que estiver ao alcance. Acenda as três casas.',
  },
  'tuning-rearrange': {
    name: 'Mova torres, suba a pontuação',
    teaches:
      'A rede funciona, mas as torres estão em lugares ruins. Remova uma, coloque num lugar melhor e veja a pontuação subir.',
  },
  'diagonal-tax-weave': {
    name: 'As diagonais custam mais',
    teaches:
      'Um cabo diagonal não alcança mais longe que um reto, mas custa 41% a mais. Endireite a rota para economizar cabo.',
  },
  'triangle-trap-fan': {
    name: 'Evite os triângulos',
    teaches:
      'Aparece um cabo entre cada par de torres ao alcance, mesmo quando não serve para nada. Três torres em triângulo recebem três cabos. Quebre o triângulo e um desses cabos some.',
  },
  'triangle-trap-delta': {
    name: 'Uma torre pode substituir duas',
    teaches:
      'Duas torres próximas recebem um cabo entre elas além das suas próprias linhas. Uma torre no meio faz o trabalho da dupla sem esse cabo a mais.',
  },
  'exact-spacing-footings': {
    name: 'Duas células de distância',
    teaches:
      'Torres separadas por exatamente duas células se conectam só com as vizinhas. Mais perto que isso, aparecem cabos a mais. Mantenha o espaçamento exato.',
  },
  'junction-neck': {
    name: 'Uma torre, três casas',
    teaches:
      'Uma única torre ao alcance das três casas alimenta todas elas. Ache esse ponto em vez de construir um ramal até cada casa.',
  },
  'junction-approach': {
    name: 'Caminho separado, custo separado',
    teaches:
      'Um caminho separado até cada casa gasta cabo próprio o trajeto inteiro. Leve uma linha só na direção das três e deixe a última torre alimentá-las.',
  },
  'junction-late-split': {
    name: 'Divida o mais tarde possível',
    teaches:
      'Quando as casas puxam para dois lados, a linha precisa se dividir. Leve uma linha compartilhada o mais longe que der e divida perto das casas. Dividir cedo paga duas linhas longas.',
  },
  'lake-narrows': {
    name: 'Escolha a travessia certa',
    teaches:
      'Os cabos atravessam a água só onde ela tem uma célula de largura. Esta água estreita em dois pontos. Atravessar alinhado com a usina e as casas dá a rota mais curta.',
  },
  'lake-west-arm': {
    name: 'Contorne a água',
    teaches:
      'A água termina antes da borda do mapa. Contornar a ponta é mais curto que qualquer travessia.',
  },
};

const PT_BR_TOPOLOGIES: Record<Topology, string> = {
  spine: 'a espinha dorsal',
  'y-split': 'o Y',
  backbranch: 'o ramal traseiro',
  'river-kiss': 'o beijo do rio',
  crossbar: 'a cruzeta',
};

const PT_BR_RANKS: Record<TierRank, string> = {
  superconductor: 'Supercondutor',
  master: 'Mestre',
  journeyman: 'Oficial',
  apprentice: 'Aprendiz',
};

const PT_BR_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Algo quebrou',
  idea: 'Uma ideia',
  grid: 'Esta rede',
  other: 'Outra coisa',
};

const PT_BR_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'diária',
  archive: 'arquivo',
  practice: 'treino',
  drill: 'exercício',
};

const PT_BR_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Supercondutor',
  master: 'Mestre linheiro',
  journeyman: 'Oficial linheiro',
  apprentice: 'Aprendiz linheiro',
};

const PT_BR: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'As torres precisam de solo seco. Os cabos atravessam a água.',
  denyMtn: 'Íngreme demais para uma torre. Os cabos passam por cima do pico.',
  denyLimit: 'Acabaram as torres. Remova uma primeiro.',
  gridFailed: 'A rede não iniciou. Tente recarregar.',
  newBest: (mw: string) => `Novo recorde: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Pronto para jogar offline.',
  resultCopied: 'Resultado copiado.',
  copyBlocked: 'Cópia bloqueada. Uma captura de tela também serve.',

  // ---------- the result card ----------
  perfectGrid: 'REDE PERFEITA!',
  lineBeaten: 'LINHA SUPERADA',
  noteBeatEngineers: 'Você superou os engenheiros.',
  noteGridDark: 'Rede apagada. Reconstrua para continuar ajustando.',
  takeN: (n: number) => `TENTATIVA ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW para ${rank}. Continue ajustando.`,
  noteFlawless: 'Impecável. Nunca removeu uma torre.',
  noteLessWire: (pct: number) => `${pct}% menos cabo que a linha óbvia.`,
  noteLessWireTuning: (pct: number) => `${pct}% menos cabo que a linha óbvia. Continue ajustando.`,
  legendReveal: 'Quando o dia termina, a rede dos engenheiros fica aberta para estudo.',
  chipBest: 'recorde',
  chipRank: 'nível',
  chipMoves: 'jogadas',
  chipNaive: 'linha óbvia',
  firstTake: 'primeira leitura',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Próxima rede em <b>${wait}</b>.`,
  newGridReady: 'Nova rede pronta.',
  /** The units `formatWait` builds "9h 41m" out of. */
  waitHours: 'h',
  waitMinutes: 'min',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Rede diária <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Treino</strong> · vale tudo',
  daytagDrill: '<strong>Exercício</strong>',

  // ---------- drills ----------
  drills: PT_BR_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` A linha óbvia marca <b>${mw}</b> MW. Supere isso.`,
  nextDrill: 'Próximo exercício',
  todaysGrid: 'A rede de hoje',

  // ---------- the study reveal ----------
  revealShow: 'Ver a rede dos engenheiros',
  revealHide: 'Ocultar a rede dos engenheiros',

  // ---------- archive, drills list, records ----------
  rowPlay: 'jogar',
  rowStudied: 'estudada',
  recordsEmpty: 'Nada registrado ainda. Energize uma rede para abrir o histórico.',
  recStreak: 'série',
  recBestRun: 'maior série',
  recGrids: 'redes',
  recPerfect: 'perfeitas',
  recFlawless: 'impecáveis',
  recBeatEngineers: 'superou os engenheiros',
  recMedian: 'mediana',
  recTunedPast: 'melhorou a primeira leitura',
  recStudied: 'estudadas',
  recDrills: 'exercícios',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'o campo',
  fieldPlayed: (players: string) => `<b>${players}</b> jogaram`,
  fieldStanding: (pct: number, players: string) =>
    `você superou <b>${pct}%</b> de <b>${players}</b> jogadores`,
  fieldYou: 'você',
  fieldAverage: 'média',
  fieldYoung: 'Os resultados ainda estão chegando. Definitivo amanhã.',
  fieldRanks: 'níveis',
  yesterdayStanding: (pct: number, players: string) =>
    `Ontem: você superou ${pct}% de ${players} jogadores.`,
  yesterdayPlayed: (players: string, avg: string) => `Ontem: ${players} jogaram${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW de média`,

  // ---------- the reference's own vocabulary ----------
  topologies: PT_BR_TOPOLOGIES,
  ranks: PT_BR_RANKS,
  ranksFull: PT_BR_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'treino',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · primeira leitura`,
  shareBeatRef: 'superou a melhor rede conhecida!',
  shareFlawless: '✨ impecável',
  shareStudied: '📖 estudada',
  shareStreak: (days: number) => `🔥 série de ${days} dias`,
  shareChain: (n: number) => `🔗 ${n} redes seguidas`,

  // ---------- share actions ----------
  copyResult: 'Copiar resultado',
  shareResult: 'Compartilhar resultado',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Impecável · 1 em cada ${oneIn} hoje`,
  claimChain: (n: number) => `${n} redes seguidas`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · o quebra-cabeça diário de linhas de transmissão',

  // ---------- the language picker ----------
  settingLanguage: 'Idioma',
  langTitle: 'IDIOMA',
  langLabel: 'Idioma',

  // ---------- the controls row ----------
  helpBtn: 'Como jogar',
  practiceBtn: 'Treino',
  newMapBtn: 'Novo mapa',
  archiveBtn: 'Arquivo',
  yardBtn: 'Exercícios',
  todayBtn: 'Hoje',
  navProfile: 'Perfil',
  profileTitle: 'PERFIL',
  navLabel: 'Seções',
  yesterdayBtn: 'A rede de ontem',
  nextGridBtn: 'Mais uma rede',
  moveOnTitle: 'AINDA HÁ MARGEM',
  moveOnIntro: (gap: string, rank: string) => `Faltam ${gap} MW para ${rank} nesta rede.`,
  moveOnCancel: 'Continue ajustando',
  moveOnConfirm: 'Próxima rede',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Tabuleiro do Wirespan. As setas movem o cursor de traçado; Enter ou Espaço coloca ou remove uma torre.',

  // ---------- the operator readout ----------
  hudTowers: 'Torres',
  hudHouses: 'Casas',
  hudOutput: 'Entrega',
  hudStreak: 'Série',

  // ---------- archive ----------
  archiveLabel: 'Arquivo',
  archiveTitle: 'ARQUIVO',
  close: 'Fechar',

  // ---------- drills overlay ----------
  yardLabel: 'Exercícios',
  yardTitle: 'EXERCÍCIOS',
  yardIntro: 'Lições para melhorar seu jogo.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Estudar a referência',
  studyTitle: 'ESTUDAR A REDE DOS ENGENHEIROS',
  studyIntro:
    'Esta rede ainda não está perfeita, então estudá-la marca o dia como <b>📖 estudada</b> ' +
    'no seu arquivo e no que você compartilhar. Uma perfeita estudada continua perfeita. Só fica registrado.',
  studyCancel: 'Ainda não',
  studyConfirm: 'Estudar',

  // ---------- settings ----------
  settingsLabel: 'Ajustes',
  settingsTitle: 'AJUSTES',
  setSound: 'Som',
  setHaptics: 'Vibração',
  setMotion: 'Reduzir movimento',
  setContrast: 'Alto contraste',
  setText: 'Texto maior',
  setUsage: 'Dados de uso',
  privacyBtn: 'Política de privacidade',
  settingsDone: 'Pronto',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Enviar comentários',
  feedbackLabel: 'Enviar comentários',
  feedbackTitle: 'ENVIAR COMENTÁRIOS',
  feedbackIntro: 'Uma mensagem para quem construiu isto. Não vai para nenhum outro lugar.',
  feedbackKinds: PT_BR_FB_KINDS,
  feedbackPlaceholder: 'Conte o que aconteceu.',
  feedbackMessageLabel: 'Sua mensagem',
  feedbackLeft: (n: number) => `faltam ${n}`,
  feedbackContact: 'E-mail, se quiser resposta',
  feedbackContactHint: 'voce@exemplo.com',
  feedbackModes: PT_BR_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Vai junto: rede #${grid} · ${mode} · versão ${build}. Nada mais.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Vai junto: ${mode} · versão ${build}. Nada mais.`,
  feedbackSend: 'Enviar',
  feedbackSending: 'Enviando…',
  feedbackDone: 'Enviado. Obrigado.',
  feedbackFailOffline: 'Não enviou, e sua mensagem continua aqui. Tente de novo em um instante.',
  feedbackFailThrottled: 'São cinco mensagens hoje. A caixa abre de novo amanhã.',
  feedbackFailInvalid: 'Não passou. Confira o endereço, se escreveu um.',

  // ---------- how to play ----------
  helpLabel: 'Como jogar',
  helpTitle: 'COMO JOGAR WIRESPAN',
  helpIntro: 'Leve a energia da usina até <b>todas as casas</b> usando o mínimo de cabo possível.',
  helpRuleBuild:
    '<b>Toque para construir ou remover uma torre.</b> Os cabos conectam tudo dentro de 2 células.',
  helpRuleGround: '<b>As torres só ficam em terreno aberto.</b> Os cabos atravessam rios e serras.',
  helpRuleShorter:
    '<b>Cabos mais curtos entregam mais MW.</b> Continue reorganizando depois que tudo estiver aceso ' +
    'para chegar ao seu recorde.',
  helpRuleDaily:
    '<b>A mesma rede para o mundo todo, nova à meia-noite.</b> O treino dá uma rede nova ' +
    'a qualquer hora.',
};

// ------------------------------------------------------------ fr-FR --

// -------------------------------------------------------- French (fr-FR) --

const FR_FR_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Allumez les maisons',
    teaches:
      'Touchez un sol sec pour dresser un pylône. Le courant va vers tout ce qui est à portée. Allumez les trois maisons.',
  },
  'tuning-rearrange': {
    name: 'Déplacez les pylônes, montez le score',
    teaches:
      'Le réseau fonctionne, mais les pylônes sont mal placés. Démontez-en un, posez-le à un meilleur endroit et regardez le score monter.',
  },
  'diagonal-tax-weave': {
    name: 'Les diagonales coûtent plus cher',
    teaches:
      "Un câble en diagonale ne va pas plus loin qu'un câble droit, mais il coûte 41% de plus. Redressez le tracé pour économiser du câble.",
  },
  'triangle-trap-fan': {
    name: 'Évitez les triangles',
    teaches:
      "Un câble apparaît entre chaque paire de pylônes à portée, même quand il ne sert à rien. Trois pylônes en triangle reçoivent trois câbles. Brisez le triangle et l'un de ces câbles disparaît.",
  },
  'triangle-trap-delta': {
    name: 'Un pylône peut en remplacer deux',
    teaches:
      'Deux pylônes proches reçoivent un câble entre eux en plus de leurs propres lignes. Un seul pylône au milieu fait le travail de la paire sans ce câble en trop.',
  },
  'exact-spacing-footings': {
    name: "À deux cases d'écart",
    teaches:
      "Des pylônes espacés d'exactement deux cases ne se relient qu'à leurs voisins. Plus près, des câbles en trop apparaissent. Gardez l'écart exact.",
  },
  'junction-neck': {
    name: 'Un pylône, trois maisons',
    teaches:
      'Un seul pylône à portée des trois maisons peut toutes les alimenter. Trouvez ce point au lieu de construire une branche vers chaque maison.',
  },
  'junction-approach': {
    name: 'Chaque chemin séparé se paie à part',
    teaches:
      'Un chemin séparé vers chaque maison dépense son propre câble sur tout le trajet. Menez une seule ligne vers les trois et laissez le dernier pylône les alimenter.',
  },
  'junction-late-split': {
    name: 'Divisez le plus tard possible',
    teaches:
      'Quand les maisons tirent dans deux directions, la ligne doit se diviser. Menez une ligne commune aussi loin que possible, puis divisez près des maisons. Une division précoce paie deux longues lignes.',
  },
  'lake-narrows': {
    name: 'Choisissez le bon passage',
    teaches:
      "Les câbles ne franchissent l'eau que là où elle fait une case de large. Cette eau se resserre à deux endroits. Franchir dans l'axe de la centrale et des maisons donne le tracé le plus court.",
  },
  'lake-west-arm': {
    name: "Contournez l'eau",
    teaches:
      "L'eau s'arrête avant le bord de la carte. Contourner la pointe est plus court que n'importe quel passage.",
  },
};

const FR_FR_TOPOLOGIES: Record<Topology, string> = {
  spine: 'la dorsale',
  'y-split': 'la fourche en Y',
  backbranch: 'la branche arrière',
  'river-kiss': 'le baiser de la rivière',
  crossbar: 'la traverse',
};

const FR_FR_RANKS: Record<TierRank, string> = {
  superconductor: 'Supraconducteur',
  master: 'Maître',
  journeyman: 'Compagnon',
  apprentice: 'Apprenti',
};

const FR_FR_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Quelque chose est cassé',
  idea: 'Une idée',
  grid: 'Ce réseau',
  other: 'Autre chose',
};

const FR_FR_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'du jour',
  archive: 'archives',
  practice: 'essai',
  drill: 'exercice',
};

const FR_FR_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Supraconducteur',
  master: 'Maître monteur',
  journeyman: 'Compagnon monteur',
  apprentice: 'Apprenti monteur',
};

const FR_FR: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: "Les pylônes exigent un sol sec. Les câbles franchissent l'eau.",
  denyMtn: 'Trop escarpé pour un pylône. Les câbles franchissent le sommet.',
  denyLimit: "Plus de pylônes. Démontez-en un d'abord.",
  gridFailed: "Le réseau n'a pas démarré. Rechargez la page.",
  newBest: (mw: string) => `Nouveau record : ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Prêt à jouer hors ligne.',
  resultCopied: 'Résultat copié.',
  copyBlocked: "Copie bloquée. Une capture d'écran marche aussi.",

  // ---------- the result card ----------
  perfectGrid: 'RÉSEAU PARFAIT !',
  lineBeaten: 'LIGNE BATTUE',
  noteBeatEngineers: 'Vous avez battu les ingénieurs.',
  noteGridDark: 'Réseau éteint. Reconstruisez pour continuer à ajuster.',
  takeN: (n: number) => `ESSAI ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW pour ${rank}. Continuez à ajuster.`,
  noteFlawless: 'Sans faute. Aucun pylône démonté.',
  noteLessWire: (pct: number) => `${pct}% de câble en moins que la ligne évidente.`,
  noteLessWireTuning: (pct: number) =>
    `${pct}% de câble en moins que la ligne évidente. Continuez à ajuster.`,
  legendReveal: "Une fois la journée finie, le réseau des ingénieurs s'ouvre à l'étude.",
  chipBest: 'record',
  chipRank: 'grade',
  chipMoves: 'coups',
  chipNaive: 'ligne évidente',
  firstTake: 'premier jet',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Prochain réseau dans <b>${wait}</b>.`,
  newGridReady: 'Nouveau réseau prêt.',
  /** The units `formatWait` builds "9h 41m" out of. */
  waitHours: 'h',
  waitMinutes: 'min',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Réseau du jour <strong>n° ${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Essai</strong> · tout est permis',
  daytagDrill: '<strong>Exercice</strong>',

  // ---------- drills ----------
  drills: FR_FR_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name} :</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` La ligne évidente indique <b>${mw}</b> MW. Battez-la.`,
  nextDrill: 'Exercice suivant',
  todaysGrid: 'Le réseau du jour',

  // ---------- the study reveal ----------
  revealShow: 'Voir le réseau des ingénieurs',
  revealHide: 'Masquer le réseau des ingénieurs',

  // ---------- archive, drills list, records ----------
  rowPlay: 'jouer',
  rowStudied: 'étudié',
  recordsEmpty: "Rien d'enregistré. Mettez un réseau sous tension pour ouvrir le registre.",
  recStreak: 'série',
  recBestRun: 'record de série',
  recGrids: 'réseaux',
  recPerfect: 'parfaits',
  recFlawless: 'sans faute',
  recBeatEngineers: 'ingénieurs battus',
  recMedian: 'médiane',
  recTunedPast: 'mieux que le premier jet',
  recStudied: 'étudiés',
  recDrills: 'exercices',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'le peloton',
  fieldPlayed: (players: string) => `<b>${players}</b> joueurs`,
  fieldStanding: (pct: number, players: string) =>
    `vous battez <b>${pct}%</b> de <b>${players}</b> joueurs`,
  fieldYou: 'vous',
  fieldAverage: 'moyenne',
  fieldYoung: 'Classement en cours. Définitif demain.',
  fieldRanks: 'grades',
  yesterdayStanding: (pct: number, players: string) =>
    `Hier : vous avez battu ${pct}% de ${players} joueurs.`,
  yesterdayPlayed: (players: string, avg: string) => `Hier : ${players} joueurs${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW de moyenne`,

  // ---------- the reference's own vocabulary ----------
  topologies: FR_FR_TOPOLOGIES,
  ranks: FR_FR_RANKS,
  ranksFull: FR_FR_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'essai',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · premier jet`,
  shareBeatRef: 'meilleur réseau connu battu !',
  shareFlawless: '✨ sans faute',
  shareStudied: '📖 étudié',
  shareStreak: (days: number) => `🔥 série de ${days} jours`,
  shareChain: (n: number) => `🔗 ${n} grilles d'affilée`,

  // ---------- share actions ----------
  copyResult: 'Copier le résultat',
  shareResult: 'Partager le résultat',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Sans faute · 1 sur ${oneIn} aujourd'hui`,
  claimChain: (n: number) => `${n} grilles d'affilée`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · le casse-tête quotidien des lignes électriques',

  // ---------- the language picker ----------
  settingLanguage: 'Langue',
  langTitle: 'LANGUE',
  langLabel: 'Langue',

  // ---------- the controls row ----------
  helpBtn: 'Règles',
  practiceBtn: 'Essai',
  newMapBtn: 'Autre carte',
  archiveBtn: 'Archives',
  yardBtn: 'Exercices',
  todayBtn: "Aujourd'hui",
  navProfile: 'Profil',
  profileTitle: 'PROFIL',
  navLabel: 'Sections',
  yesterdayBtn: "Le réseau d'hier",
  nextGridBtn: 'Une grille de plus',
  moveOnTitle: 'IL RESTE DE LA MARGE',
  moveOnIntro: (gap: string, rank: string) =>
    `Il vous manque ${gap} MW pour ${rank} sur cette grille.`,
  moveOnCancel: 'Continuer à ajuster',
  moveOnConfirm: 'Grille suivante',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Plateau de Wirespan. Les flèches déplacent le curseur de relevé ; Entrée ou Espace pose ou retire un pylône.',

  // ---------- the operator readout ----------
  hudTowers: 'Pylônes',
  hudHouses: 'Maisons',
  hudOutput: 'Sortie',
  hudStreak: 'Série',

  // ---------- archive ----------
  archiveLabel: 'Archives',
  archiveTitle: 'ARCHIVES',
  close: 'Fermer',

  // ---------- drills overlay ----------
  yardLabel: 'Exercices',
  yardTitle: 'EXERCICES',
  yardIntro: 'Des leçons pour améliorer votre jeu.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Étudier la référence',
  studyTitle: 'ÉTUDIER LE RÉSEAU DES INGÉNIEURS',
  studyIntro:
    "Ce réseau n'est pas encore parfait, alors l'étudier marque le jour <b>📖 étudié</b> " +
    'dans vos archives et dans son partage. Un parfait étudié reste un parfait. Il le dit, voilà tout.',
  studyCancel: 'Pas encore',
  studyConfirm: "L'étudier",

  // ---------- settings ----------
  settingsLabel: 'Réglages',
  settingsTitle: 'RÉGLAGES',
  setSound: 'Son',
  setHaptics: 'Vibrations',
  setMotion: 'Réduire les animations',
  setContrast: 'Contraste élevé',
  setText: 'Texte plus grand',
  setUsage: "Données d'usage",
  privacyBtn: 'Politique de confidentialité',
  settingsDone: 'Terminé',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Envoyer un message',
  feedbackLabel: 'Envoyer un message',
  feedbackTitle: 'ENVOYER UN MESSAGE',
  feedbackIntro: 'Un message à la personne qui a construit ce jeu. Il ne va nulle part ailleurs.',
  feedbackKinds: FR_FR_FB_KINDS,
  feedbackPlaceholder: "Dites ce qui s'est passé.",
  feedbackMessageLabel: 'Votre message',
  feedbackLeft: (n: number) => `${n} restants`,
  feedbackContact: 'E-mail, si vous voulez une réponse',
  feedbackContactHint: 'vous@exemple.com',
  feedbackModes: FR_FR_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Part avec le message : réseau n° ${grid} · ${mode} · version ${build}. Rien d'autre.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Part avec le message : ${mode} · version ${build}. Rien d'autre.`,
  feedbackSend: 'Envoyer',
  feedbackSending: 'Envoi…',
  feedbackDone: 'Envoyé. Merci.',
  feedbackFailOffline:
    "L'envoi a échoué, et votre message est toujours là. Réessayez dans un instant.",
  feedbackFailThrottled: "Cela fait cinq messages aujourd'hui. La boîte rouvre demain.",
  feedbackFailInvalid: "Cela n'est pas passé. Vérifiez l'adresse, si vous en avez saisi une.",

  // ---------- how to play ----------
  helpLabel: 'Règles',
  helpTitle: 'LES RÈGLES DE WIRESPAN',
  helpIntro:
    "Acheminez le courant de la centrale jusqu'à <b>chaque maison</b> avec le moins de câble possible.",
  helpRuleBuild:
    '<b>Touchez pour poser ou retirer un pylône.</b> Les câbles relient tout ce qui est à 2 cases.',
  helpRuleGround:
    '<b>Les pylônes se posent en terrain dégagé.</b> Les câbles franchissent rivières et crêtes.',
  helpRuleShorter:
    '<b>Des câbles plus courts livrent plus de MW.</b> Continuez à déplacer les pylônes une fois ' +
    'tout allumé pour viser votre record.',
  helpRuleDaily:
    '<b>Le même réseau pour le monde entier, nouveau à minuit.</b> Le mode essai distribue un ' +
    'réseau neuf quand vous voulez.',
};

// ------------------------------------------------------------ de-DE --

// -------------------------------------------------------- German (de-DE) --

const DE_DE_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Die Häuser erleuchten',
    teaches:
      'Tippe trockenen Boden an, um einen Mast zu bauen. Strom fließt zu allem in Reichweite. Erleuchte alle drei Häuser.',
  },
  'tuning-rearrange': {
    name: 'Masten versetzen, Wert steigern',
    teaches:
      'Das Netz läuft, aber die Masten stehen schlecht. Bau einen ab, setz ihn besser und sieh zu, wie der Wert steigt.',
  },
  'diagonal-tax-weave': {
    name: 'Diagonalen kosten mehr',
    teaches:
      'Eine diagonale Leitung reicht nicht weiter als eine gerade, kostet aber 41% mehr. Begradige die Route und spare Leitung.',
  },
  'triangle-trap-fan': {
    name: 'Dreiecke vermeiden',
    teaches:
      'Zwischen jedem Mastenpaar in Reichweite entsteht eine Leitung, auch wenn sie nichts bringt. Drei Masten im Dreieck bekommen drei Leitungen. Brich das Dreieck auf, und eine davon verschwindet.',
  },
  'triangle-trap-delta': {
    name: 'Ein Mast ersetzt zwei',
    teaches:
      'Zwei Masten dicht beieinander bekommen zusätzlich zu ihren eigenen noch eine Leitung untereinander. Ein Mast in der Mitte macht die Arbeit des Paares ohne diese Extraleitung.',
  },
  'exact-spacing-footings': {
    name: 'Zwei Felder Abstand',
    teaches:
      'Masten mit genau zwei Feldern Abstand verbinden sich nur mit ihren Nachbarn. Enger, und es entstehen zusätzliche Leitungen. Halte den Abstand genau.',
  },
  'junction-neck': {
    name: 'Ein Mast, drei Häuser',
    teaches:
      'Ein einzelner Mast in Reichweite aller drei Häuser versorgt sie alle. Finde diesen Punkt, statt zu jedem Haus einen Abzweig zu bauen.',
  },
  'junction-approach': {
    name: 'Getrennte Wege kosten getrennt',
    teaches:
      'Ein eigener Weg zu jedem Haus verbraucht über die ganze Strecke eigene Leitung. Führe eine Leitung auf alle drei zu und lass den letzten Mast sie versorgen.',
  },
  'junction-late-split': {
    name: 'So spät wie möglich teilen',
    teaches:
      'Ziehen die Häuser in zwei Richtungen, muss sich die Leitung teilen. Führe eine gemeinsame Leitung so weit wie möglich und teile erst nah an den Häusern. Ein früher Abzweig bezahlt zwei lange Leitungen.',
  },
  'lake-narrows': {
    name: 'Die richtige Querung wählen',
    teaches:
      'Leitungen überspannen Wasser nur dort, wo es ein Feld breit ist. Dieses Wasser wird an zwei Stellen schmal. Die Querung auf einer Achse mit Kraftwerk und Häusern ergibt die kürzeste Route.',
  },
  'lake-west-arm': {
    name: 'Das Wasser umgehen',
    teaches: 'Das Wasser endet vor dem Kartenrand. Außen herum ist kürzer als jede Querung.',
  },
};

const DE_DE_TOPOLOGIES: Record<Topology, string> = {
  spine: 'das Rückgrat',
  'y-split': 'die Y-Gabel',
  backbranch: 'der hintere Abzweig',
  'river-kiss': 'der Flusskuss',
  crossbar: 'die Traverse',
};

const DE_DE_RANKS: Record<TierRank, string> = {
  superconductor: 'Supraleiter',
  master: 'Meister',
  journeyman: 'Geselle',
  apprentice: 'Lehrling',
};

const DE_DE_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Etwas ist kaputt',
  idea: 'Eine Idee',
  grid: 'Dieses Netz',
  other: 'Etwas anderes',
};

const DE_DE_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'täglich',
  archive: 'Archiv',
  practice: 'Training',
  drill: 'Übung',
};

const DE_DE_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Supraleiter',
  master: 'Leitungsmeister',
  journeyman: 'Leitungsgeselle',
  apprentice: 'Leitungslehrling',
};

const DE_DE: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'Masten brauchen trockenen Boden. Leitungen überspannen das Wasser.',
  denyMtn: 'Zu steil für einen Mast. Leitungen überspannen den Gipfel.',
  denyLimit: 'Keine Masten mehr. Bau erst einen ab.',
  gridFailed: 'Das Netz ist nicht gestartet. Versuch es mit Neuladen.',
  newBest: (mw: string) => `Neuer Bestwert: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Offline spielbereit.',
  resultCopied: 'Ergebnis kopiert.',
  copyBlocked: 'Kopieren blockiert. Ein Screenshot geht auch.',

  // ---------- the result card ----------
  perfectGrid: 'PERFEKTES NETZ!',
  lineBeaten: 'DIREKTLINIE BESIEGT',
  noteBeatEngineers: 'Du hast die Ingenieure besiegt.',
  noteGridDark: 'Netz dunkel. Bau neu auf, um weiterzufeilen.',
  takeN: (n: number) => `VERSUCH ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW bis ${rank}. Feil weiter.`,
  noteFlawless: 'Makellos. Nie einen Mast abgebaut.',
  noteLessWire: (pct: number) => `${pct}% weniger Leitung als die Direktlinie.`,
  noteLessWireTuning: (pct: number) => `${pct}% weniger Leitung als die Direktlinie. Feil weiter.`,
  legendReveal: 'Ist der Tag vorbei, liegt das Netz der Ingenieure zum Vergleich offen.',
  chipBest: 'Bestwert',
  chipRank: 'Rang',
  chipMoves: 'Züge',
  chipNaive: 'Direktlinie',
  firstTake: 'Erstversuch',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Nächstes Netz in <b>${wait}</b>.`,
  newGridReady: 'Neues Netz bereit.',
  /** The units `formatWait` builds "9h 41m" out of. */
  waitHours: 'h',
  waitMinutes: 'min',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Tagesnetz <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Training</strong> · alles erlaubt',
  daytagDrill: '<strong>Übung</strong>',

  // ---------- drills ----------
  drills: DE_DE_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` Die Direktlinie zeigt <b>${mw}</b> MW. Besiege sie.`,
  nextDrill: 'Nächste Übung',
  todaysGrid: 'Netz von heute',

  // ---------- the study reveal ----------
  revealShow: 'Netz der Ingenieure zeigen',
  revealHide: 'Netz der Ingenieure verbergen',

  // ---------- archive, drills list, records ----------
  rowPlay: 'spielen',
  rowStudied: 'studiert',
  recordsEmpty: 'Noch nichts erfasst. Setz ein Netz unter Spannung, um die Bilanz zu öffnen.',
  recStreak: 'Serie',
  recBestRun: 'längste Serie',
  recGrids: 'Netze',
  recPerfect: 'perfekt',
  recFlawless: 'makellos',
  recBeatEngineers: 'Ingenieure besiegt',
  recMedian: 'Median',
  recTunedPast: 'Erstversuch besiegt',
  recStudied: 'studiert',
  recDrills: 'Übungen',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'das Feld',
  fieldPlayed: (players: string) => `<b>${players}</b> gespielt`,
  fieldStanding: (pct: number, players: string) =>
    `besser als <b>${pct}%</b> von <b>${players}</b> Spielern`,
  fieldYou: 'du',
  fieldAverage: 'Schnitt',
  fieldYoung: 'Die Werte laufen noch ein. Endstand morgen.',
  fieldRanks: 'Ränge',
  yesterdayStanding: (pct: number, players: string) =>
    `Gestern: besser als ${pct}% von ${players} Spielern.`,
  yesterdayPlayed: (players: string, avg: string) => `Gestern: ${players} haben gespielt${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW im Schnitt`,

  // ---------- the reference's own vocabulary ----------
  topologies: DE_DE_TOPOLOGIES,
  ranks: DE_DE_RANKS,
  ranksFull: DE_DE_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'Training',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · Erstversuch`,
  shareBeatRef: 'bestes bekanntes Netz besiegt!',
  shareFlawless: '✨ makellos',
  shareStudied: '📖 studiert',
  shareStreak: (days: number) => `🔥 ${days}-Tage-Serie`,
  shareChain: (n: number) => `🔗 ${n} Netze am Stück`,

  // ---------- share actions ----------
  copyResult: 'Ergebnis kopieren',
  shareResult: 'Ergebnis teilen',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Makellos · 1 von ${oneIn} heute`,
  claimChain: (n: number) => `${n} Netze am Stück`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · das tägliche Leitungsrätsel',

  // ---------- the language picker ----------
  settingLanguage: 'Sprache',
  langTitle: 'SPRACHE',
  langLabel: 'Sprache',

  // ---------- the controls row ----------
  helpBtn: 'Anleitung',
  practiceBtn: 'Training',
  newMapBtn: 'Neue Karte',
  archiveBtn: 'Archiv',
  yardBtn: 'Übungen',
  todayBtn: 'Heute',
  navProfile: 'Profil',
  profileTitle: 'PROFIL',
  navLabel: 'Bereiche',
  yesterdayBtn: 'Netz von gestern',
  nextGridBtn: 'Noch ein Netz',
  moveOnTitle: 'HIER GEHT NOCH WAS',
  moveOnIntro: (gap: string, rank: string) => `Dir fehlen ${gap} MW bis ${rank} in diesem Netz.`,
  moveOnCancel: 'Weiter feilen',
  moveOnConfirm: 'Nächstes Netz',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Wirespan-Spielfeld. Pfeiltasten bewegen den Messcursor; Enter oder Leertaste setzt oder entfernt einen Mast.',

  // ---------- the operator readout ----------
  hudTowers: 'Masten',
  hudHouses: 'Häuser',
  hudOutput: 'Leistung',
  hudStreak: 'Serie',

  // ---------- archive ----------
  archiveLabel: 'Archiv',
  archiveTitle: 'ARCHIV',
  close: 'Schließen',

  // ---------- drills overlay ----------
  yardLabel: 'Übungen',
  yardTitle: 'ÜBUNGEN',
  yardIntro: 'Übungen für ein besseres Spiel.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Die Referenz studieren',
  studyTitle: 'DAS NETZ DER INGENIEURE STUDIEREN',
  studyIntro:
    'Dieses Netz ist noch nicht perfekt. Studieren markiert den Tag im Archiv und beim ' +
    'Teilen mit <b>📖 studiert</b>. Ein perfektes Netz bleibt perfekt, auch studiert. Es steht nur dabei.',
  studyCancel: 'Noch nicht',
  studyConfirm: 'Studieren',

  // ---------- settings ----------
  settingsLabel: 'Einstellungen',
  settingsTitle: 'EINSTELLUNGEN',
  setSound: 'Ton',
  setHaptics: 'Vibration',
  setMotion: 'Weniger Bewegung',
  setContrast: 'Hoher Kontrast',
  setText: 'Größerer Text',
  setUsage: 'Nutzungsdaten',
  privacyBtn: 'Datenschutz',
  settingsDone: 'Fertig',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Rückmeldung senden',
  feedbackLabel: 'Rückmeldung senden',
  feedbackTitle: 'RÜCKMELDUNG SENDEN',
  feedbackIntro: 'Eine Nachricht an die Person, die das gebaut hat. Sie geht nirgendwo anders hin.',
  feedbackKinds: DE_DE_FB_KINDS,
  feedbackPlaceholder: 'Sag, was passiert ist.',
  feedbackMessageLabel: 'Deine Nachricht',
  feedbackLeft: (n: number) => `${n} übrig`,
  feedbackContact: 'E-Mail, wenn du eine Antwort willst',
  feedbackContactHint: 'du@beispiel.de',
  feedbackModes: DE_DE_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Geht mit: Netz #${grid} · ${mode} · Build ${build}. Sonst nichts.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Geht mit: ${mode} · Build ${build}. Sonst nichts.`,
  feedbackSend: 'Senden',
  feedbackSending: 'Wird gesendet…',
  feedbackDone: 'Gesendet. Danke.',
  feedbackFailOffline:
    'Es ging nicht raus, und deine Nachricht steht noch hier. Versuch es gleich noch einmal.',
  feedbackFailThrottled: 'Das sind fünf Nachrichten heute. Morgen öffnet der Kasten wieder.',
  feedbackFailInvalid: 'Das ging nicht durch. Prüfe die Adresse, falls du eine eingetragen hast.',

  // ---------- how to play ----------
  helpLabel: 'Anleitung',
  helpTitle: 'SO SPIELST DU WIRESPAN',
  helpIntro: 'Leite Strom vom Kraftwerk zu <b>jedem Haus</b>, mit so wenig Leitung wie möglich.',
  helpRuleBuild:
    '<b>Tippen baut oder entfernt einen Mast.</b> Leitungen verbinden alles im Umkreis von 2 Feldern.',
  helpRuleGround:
    '<b>Masten stehen nur auf freiem Boden.</b> Leitungen überspannen Flüsse und Bergkämme.',
  helpRuleShorter:
    '<b>Kürzere Leitungen liefern mehr MW.</b> Bau weiter um, wenn alles leuchtet, ' +
    'und hol dir deinen Bestwert.',
  helpRuleDaily:
    '<b>Dasselbe Netz für die ganze Welt, neu um Mitternacht.</b> Das Training teilt ' +
    'jederzeit ein frisches Netz aus.',
};

// ------------------------------------------------------------ it-IT --

// -------------------------------------------------------- Italian (it-IT) --

const IT_IT_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Accendi le case',
    teaches:
      "Tocca il terreno asciutto per alzare un traliccio. L'energia arriva a tutto ciò che è a portata. Accendi tutte e tre le case.",
  },
  'tuning-rearrange': {
    name: 'Sposta i tralicci, alza il punteggio',
    teaches:
      'La rete funziona, ma i tralicci sono in punti scadenti. Smontane uno, mettilo in un punto migliore e guarda salire il punteggio.',
  },
  'diagonal-tax-weave': {
    name: 'Le diagonali costano di più',
    teaches:
      'Un cavo in diagonale non arriva più lontano di uno dritto, ma costa il 41% in più. Raddrizza il percorso per risparmiare cavo.',
  },
  'triangle-trap-fan': {
    name: 'Evita i triangoli',
    teaches:
      'Tra ogni coppia di tralicci a portata compare un cavo, anche quando non serve a nulla. Tre tralicci in triangolo ricevono tre cavi. Rompi il triangolo e uno di quei cavi sparisce.',
  },
  'triangle-trap-delta': {
    name: 'Un traliccio può sostituirne due',
    teaches:
      'Due tralicci vicini ricevono un cavo tra loro oltre alle proprie linee. Un solo traliccio in mezzo fa il lavoro della coppia senza quel cavo in più.',
  },
  'exact-spacing-footings': {
    name: 'A due caselle di distanza',
    teaches:
      'I tralicci distanti esattamente due caselle si collegano solo ai vicini. Più vicini di così compaiono cavi in più. Tieni la distanza esatta.',
  },
  'junction-neck': {
    name: 'Un traliccio, tre case',
    teaches:
      'Un solo traliccio a portata di tutte e tre le case può alimentarle tutte. Trova quel punto invece di costruire un ramo verso ogni casa.',
  },
  'junction-approach': {
    name: 'Ogni percorso separato si paga a parte',
    teaches:
      "Un percorso separato verso ogni casa consuma il proprio cavo per tutto il tragitto. Porta una sola linea verso tutte e tre e lascia che sia l'ultimo traliccio ad alimentarle.",
  },
  'junction-late-split': {
    name: 'Dividi il più tardi possibile',
    teaches:
      'Quando le case tirano in due direzioni, la linea deve dividersi. Porta una linea condivisa più lontano che puoi, poi dividila vicino alle case. Dividere presto paga due linee lunghe.',
  },
  'lake-narrows': {
    name: "Scegli l'attraversamento giusto",
    teaches:
      "I cavi attraversano l'acqua solo dove è larga una casella. Quest'acqua si stringe in due punti. Attraversare in linea con la centrale e le case dà il percorso più corto.",
  },
  'lake-west-arm': {
    name: "Gira intorno all'acqua",
    teaches:
      "L'acqua finisce prima del bordo della mappa. Girare intorno alla punta è più corto di qualsiasi attraversamento.",
  },
};

const IT_IT_TOPOLOGIES: Record<Topology, string> = {
  spine: 'la dorsale',
  'y-split': 'la Y',
  backbranch: 'il ramo di ritorno',
  'river-kiss': 'il bacio del fiume',
  crossbar: 'la traversa',
};

const IT_IT_RANKS: Record<TierRank, string> = {
  superconductor: 'Superconduttore',
  master: 'Maestro',
  journeyman: 'Lavorante',
  apprentice: 'Apprendista',
};

const IT_IT_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Qualcosa si è rotto',
  idea: "Un'idea",
  grid: 'Questa rete',
  other: 'Altro',
};

const IT_IT_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'quotidiana',
  archive: 'archivio',
  practice: 'pratica',
  drill: 'esercizio',
};

const IT_IT_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Superconduttore',
  master: 'Maestro di linea',
  journeyman: 'Lavorante di linea',
  apprentice: 'Apprendista di linea',
};

const IT_IT: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: "I tralicci vogliono terreno asciutto. I cavi attraversano l'acqua.",
  denyMtn: 'Troppo ripido per un traliccio. I cavi passano sopra la cima.',
  denyLimit: 'Tralicci finiti. Smontane uno prima.',
  gridFailed: 'La rete non è partita. Prova a ricaricare.',
  newBest: (mw: string) => `Nuovo record: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Pronto per giocare offline.',
  resultCopied: 'Risultato copiato.',
  copyBlocked: 'Copia bloccata. Funziona anche uno screenshot.',

  // ---------- the result card ----------
  perfectGrid: 'RETE PERFETTA!',
  lineBeaten: 'LINEA BATTUTA',
  noteBeatEngineers: 'Hai battuto gli ingegneri.',
  noteGridDark: 'Rete spenta. Ricostruisci per continuare a regolare.',
  takeN: (n: number) => `TENTATIVO ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW per ${rank}. Continua a regolare.`,
  noteFlawless: 'Impeccabile. Mai smontato un traliccio.',
  noteLessWire: (pct: number) => `${pct}% di cavo in meno della linea ovvia.`,
  noteLessWireTuning: (pct: number) =>
    `${pct}% di cavo in meno della linea ovvia. Continua a regolare.`,
  legendReveal: 'Finita la giornata, la rete degli ingegneri si apre allo studio.',
  chipBest: 'record',
  chipRank: 'grado',
  chipMoves: 'mosse',
  chipNaive: 'linea ovvia',
  firstTake: 'primo colpo',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Prossima rete tra <b>${wait}</b>.`,
  newGridReady: 'Nuova rete pronta.',
  /** The units `formatWait` builds "9h 41m" out of. */
  waitHours: 'h',
  waitMinutes: 'min',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Rete quotidiana <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Pratica</strong> · vale tutto',
  daytagDrill: '<strong>Esercizio</strong>',

  // ---------- drills ----------
  drills: IT_IT_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` La linea ovvia segna <b>${mw}</b> MW. Battila.`,
  nextDrill: 'Prossimo esercizio',
  todaysGrid: 'La rete di oggi',

  // ---------- the study reveal ----------
  revealShow: 'Mostra la rete degli ingegneri',
  revealHide: 'Nascondi la rete degli ingegneri',

  // ---------- archive, drills list, records ----------
  rowPlay: 'gioca',
  rowStudied: 'studiata',
  recordsEmpty: 'Ancora nessun dato. Metti in tensione una rete per aprire il registro.',
  recStreak: 'serie',
  recBestRun: 'serie record',
  recGrids: 'reti',
  recPerfect: 'perfette',
  recFlawless: 'impeccabili',
  recBeatEngineers: 'battuti gli ingegneri',
  recMedian: 'mediana',
  recTunedPast: 'migliorato il primo colpo',
  recStudied: 'studiate',
  recDrills: 'esercizi',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'il campo',
  fieldPlayed: (players: string) => `<b>${players}</b> giocatori`,
  fieldStanding: (pct: number, players: string) =>
    `hai battuto il <b>${pct}%</b> di <b>${players}</b> giocatori`,
  fieldYou: 'tu',
  fieldAverage: 'media',
  fieldYoung: 'Classifica ancora in arrivo. Definitiva domani.',
  fieldRanks: 'gradi',
  yesterdayStanding: (pct: number, players: string) =>
    `Ieri: hai battuto il ${pct}% di ${players} giocatori.`,
  yesterdayPlayed: (players: string, avg: string) => `Ieri: ${players} giocatori${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW di media`,

  // ---------- the reference's own vocabulary ----------
  topologies: IT_IT_TOPOLOGIES,
  ranks: IT_IT_RANKS,
  ranksFull: IT_IT_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'pratica',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · primo colpo`,
  shareBeatRef: 'battuta la migliore rete conosciuta!',
  shareFlawless: '✨ impeccabile',
  shareStudied: '📖 studiata',
  shareStreak: (days: number) => `🔥 serie di ${days} giorni`,
  shareChain: (n: number) => `🔗 ${n} reti di fila`,

  // ---------- share actions ----------
  copyResult: 'Copia risultato',
  shareResult: 'Condividi risultato',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Impeccabile · 1 su ${oneIn} oggi`,
  claimChain: (n: number) => `${n} reti di fila`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · il rompicapo quotidiano delle linee elettriche',

  // ---------- the language picker ----------
  settingLanguage: 'Lingua',
  langTitle: 'LINGUA',
  langLabel: 'Lingua',

  // ---------- the controls row ----------
  helpBtn: 'Come si gioca',
  practiceBtn: 'Pratica',
  newMapBtn: 'Nuova mappa',
  archiveBtn: 'Archivio',
  yardBtn: 'Esercizi',
  todayBtn: 'Oggi',
  navProfile: 'Profilo',
  profileTitle: 'PROFILO',
  navLabel: 'Sezioni',
  yesterdayBtn: 'La rete di ieri',
  nextGridBtn: "Un'altra rete",
  moveOnTitle: "C'È ANCORA MARGINE",
  moveOnIntro: (gap: string, rank: string) => `Ti mancano ${gap} MW per ${rank} su questa rete.`,
  moveOnCancel: 'Continua a regolare',
  moveOnConfirm: 'Rete successiva',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Tabellone di Wirespan. Le frecce muovono il cursore di rilievo; Invio o Spazio piazza o smonta un traliccio.',

  // ---------- the operator readout ----------
  hudTowers: 'Tralicci',
  hudHouses: 'Case',
  hudOutput: 'Consegna',
  hudStreak: 'Serie',

  // ---------- archive ----------
  archiveLabel: 'Archivio',
  archiveTitle: 'ARCHIVIO',
  close: 'Chiudi',

  // ---------- drills overlay ----------
  yardLabel: 'Esercizi',
  yardTitle: 'ESERCIZI',
  yardIntro: 'Lezioni per migliorare il tuo gioco.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Studia il riferimento',
  studyTitle: 'STUDIA LA RETE DEGLI INGEGNERI',
  studyIntro:
    'Questa rete non è ancora perfetta, quindi studiarla segna la giornata come <b>📖 studiata</b> ' +
    'nel tuo archivio e in ciò che condividi. Una perfetta studiata resta perfetta. Il registro lo annota.',
  studyCancel: 'Non ancora',
  studyConfirm: 'Studiala',

  // ---------- settings ----------
  settingsLabel: 'Impostazioni',
  settingsTitle: 'IMPOSTAZIONI',
  setSound: 'Suono',
  setHaptics: 'Vibrazione',
  setMotion: 'Riduci il movimento',
  setContrast: 'Contrasto elevato',
  setText: 'Testo più grande',
  setUsage: 'Dati di utilizzo',
  privacyBtn: 'Informativa privacy',
  settingsDone: 'Fatto',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Invia un messaggio',
  feedbackLabel: 'Invia un messaggio',
  feedbackTitle: 'INVIA UN MESSAGGIO',
  feedbackIntro: 'Un messaggio a chi ha costruito questo gioco. Non va altrove.',
  feedbackKinds: IT_IT_FB_KINDS,
  feedbackPlaceholder: 'Racconta cosa è successo.',
  feedbackMessageLabel: 'Il tuo messaggio',
  feedbackLeft: (n: number) => `${n} rimasti`,
  feedbackContact: 'Email, se vuoi una risposta',
  feedbackContactHint: 'tu@esempio.it',
  feedbackModes: IT_IT_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Parte con il messaggio: rete #${grid} · ${mode} · build ${build}. Nient'altro.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Parte con il messaggio: ${mode} · build ${build}. Nient'altro.`,
  feedbackSend: 'Invia',
  feedbackSending: 'Invio…',
  feedbackDone: 'Inviato. Grazie.',
  feedbackFailOffline: 'Non è partito, e il tuo messaggio è ancora qui. Riprova tra un momento.',
  feedbackFailThrottled: 'Sono cinque messaggi oggi. La casella riapre domani.',
  feedbackFailInvalid: "Non è passato. Controlla l'indirizzo, se ne hai scritto uno.",

  // ---------- how to play ----------
  helpLabel: 'Come si gioca',
  helpTitle: 'COME SI GIOCA A WIRESPAN',
  helpIntro: "Porta l'energia dalla centrale a <b>ogni casa</b> usando meno cavo possibile.",
  helpRuleBuild:
    '<b>Tocca per costruire o smontare un traliccio.</b> I cavi collegano tutto entro 2 caselle.',
  helpRuleGround: '<b>I tralicci vanno su terreno libero.</b> I cavi attraversano fiumi e creste.',
  helpRuleShorter:
    '<b>Cavi più corti consegnano più MW.</b> Continua a spostare i tralicci quando tutto è ' +
    'acceso, per arrivare al tuo record.',
  helpRuleDaily:
    '<b>La stessa rete per tutto il mondo, nuova a mezzanotte.</b> La modalità pratica dà una ' +
    'rete nuova quando vuoi.',
};

// ------------------------------------------------------------ ja-JP --

// ------------------------------------------------------- Japanese (ja-JP) --

const JA_JP_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: '家に灯をともす',
    teaches:
      '乾いた地面をタップして鉄塔を建てる。電気は届く範囲すべてに流れる。3軒の家すべてに灯をともす。',
  },
  'tuning-rearrange': {
    name: '鉄塔を動かして出力を上げる',
    teaches:
      '送電網は動いているが、鉄塔の位置が悪い。1基を撤去し、より良い場所に建て直すと出力が上がる。',
  },
  'diagonal-tax-weave': {
    name: '斜めは高くつく',
    teaches:
      '斜めの電線はまっすぐな電線より遠くへ届かないのに、電線は 41% 多く要る。経路をまっすぐにして電線を節約する。',
  },
  'triangle-trap-fan': {
    name: '三角形を避ける',
    teaches:
      '届く範囲にある鉄塔どうしには、役に立たなくても必ず電線が張られる。三角形に並んだ3基には3本。三角形を崩せばそのうち1本が消える。',
  },
  'triangle-trap-delta': {
    name: '1基で2基分',
    teaches:
      '近すぎる2基には、それぞれの線に加えて互いの間にも電線が張られる。真ん中の1基があれば、その余分な電線なしで2基分の仕事をこなす。',
  },
  'exact-spacing-footings': {
    name: '2マス離す',
    teaches:
      'ちょうど2マス離した鉄塔は隣としかつながらない。それより近いと余分な電線が張られる。間隔を正確に保つ。',
  },
  'junction-neck': {
    name: '1基で3軒',
    teaches:
      '3軒すべてに届く1基があれば、それだけで全部に電気が行く。家ごとに枝を伸ばさず、その一点を探す。',
  },
  'junction-approach': {
    name: '別々の経路は別々に払う',
    teaches:
      '家ごとに別の経路を引くと、その全長ぶんの電線をそれぞれ食う。3軒に向けて一本にまとめ、最後の1基から配る。',
  },
  'junction-late-split': {
    name: '分岐はできるだけ遅く',
    teaches:
      '家が二方向に散っていると、線は必ず分かれる。共用の一本を行けるところまで伸ばし、家の近くで分ける。早い分岐は長い線を2本分払う。',
  },
  'lake-narrows': {
    name: '渡る場所を選ぶ',
    teaches:
      '電線が水を渡れるのは幅1マスの場所だけ。この水は2か所で狭まる。発電所と家を結ぶ線上で渡ると経路が最短になる。',
  },
  'lake-west-arm': {
    name: '水を回り込む',
    teaches: '水は地図の端まで届いていない。先端を回り込むほうが、どこを渡るより短い。',
  },
};

const JA_JP_TOPOLOGIES: Record<Topology, string> = {
  spine: '背骨',
  'y-split': 'Y分岐',
  backbranch: '後ろ枝',
  'river-kiss': '川の口づけ',
  crossbar: '腕金',
};

const JA_JP_RANKS: Record<TierRank, string> = {
  superconductor: '超伝導',
  master: '親方',
  journeyman: '職人',
  apprentice: '見習い',
};

const JA_JP_FB_KINDS: Record<FeedbackKind, string> = {
  bug: '不具合',
  idea: 'アイデア',
  grid: 'この送電網',
  other: 'その他',
};

const JA_JP_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: '日次',
  archive: '記録',
  practice: '練習',
  drill: '訓練',
};

const JA_JP_RANKS_FULL: Record<TierRank, string> = {
  superconductor: '超伝導体',
  master: '送電線の親方',
  journeyman: '送電線の職人',
  apprentice: '送電線の見習い',
};

const JA_JP: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: '鉄塔は乾いた地面のみ。電線は水を渡れる。',
  denyMtn: '急すぎて鉄塔は建たない。電線は尾根を越えられる。',
  denyLimit: '鉄塔切れ。先に1基撤去。',
  gridFailed: '送電網を起動できず。再読み込みを試す。',
  newBest: (mw: string) => `最高記録更新 ${mw} MW。`,

  // ---------- chrome news ----------
  offlineReady: 'オフラインでも遊べる。',
  resultCopied: '結果をコピーした。',
  copyBlocked: 'コピーできず。画面の撮影でも足りる。',

  // ---------- the result card ----------
  perfectGrid: '完璧な送電網!',
  lineBeaten: '単純な線を突破',
  noteBeatEngineers: '技術者を上回った。',
  noteGridDark: '送電網が消灯。組み直せば調整を続けられる。',
  takeN: (n: number) => `${n}回目`,
  noteGapTo: (gap: string, rank: string) => `${rank}まであと ${gap} MW。調整を続ける。`,
  noteFlawless: '無傷。鉄塔を一度も撤去せず。',
  noteLessWire: (pct: number) => `単純な線より電線が ${pct}% 少ない。`,
  noteLessWireTuning: (pct: number) => `単純な線より電線が ${pct}% 少ない。調整を続ける。`,
  legendReveal: '一日が終われば、技術者自身の送電網を研究できる。',
  chipBest: '最高',
  chipRank: '等級',
  chipMoves: '手数',
  chipNaive: '単純な線',
  firstTake: '初回',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `次の送電網まで <b>${wait}</b>。`,
  newGridReady: '新しい送電網が届いた。',
  /** The units `formatWait` builds "9h 41m" out of. */
  waitHours: '時間',
  waitMinutes: '分',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `日次送電網 <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>練習</strong> · 何でもあり',
  daytagDrill: '<strong>訓練</strong>',

  // ---------- drills ----------
  drills: JA_JP_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}：</strong>${teaches}${goal}`,
  drillGoal: (mw: string) => ` 単純な線は<b>${mw}</b> MW。これを上回る。`,
  nextDrill: '次の訓練',
  todaysGrid: '今日の送電網',

  // ---------- the study reveal ----------
  revealShow: '技術者の送電網を見る',
  revealHide: '技術者の送電網を隠す',

  // ---------- archive, drills list, records ----------
  rowPlay: '遊ぶ',
  rowStudied: '学習済み',
  recordsEmpty: 'まだ記録がない。送電網に通電すると記録が始まる。',
  recStreak: '連続',
  recBestRun: '最長連続',
  recGrids: '送電網',
  recPerfect: '完璧',
  recFlawless: '無傷',
  recBeatEngineers: '技術者超え',
  recMedian: '中央値',
  recTunedPast: '初回超え',
  recStudied: '学習済み',
  recDrills: '訓練',

  // ---------- the field (GDD §4.7) ----------
  fieldName: '全体',
  fieldPlayed: (players: string) => `<b>${players}</b>人が参加`,
  fieldStanding: (pct: number, players: string) =>
    `<b>${players}</b>人中 <b>${pct}%</b> を上回った`,
  fieldYou: 'あなた',
  fieldAverage: '平均',
  fieldYoung: '順位はまだ集計中。確定は明日。',
  fieldRanks: '等級',
  yesterdayStanding: (pct: number, players: string) => `昨日 ${players}人中 ${pct}% を上回った。`,
  yesterdayPlayed: (players: string, avg: string) => `昨日は ${players}人が参加${avg}。`,
  yesterdayAvg: (mw: number) => ` · 平均 ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: JA_JP_TOPOLOGIES,
  ranks: JA_JP_RANKS,
  ranksFull: JA_JP_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: '練習',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · 初回`,
  shareBeatRef: '既知の最良送電網を超えた!',
  shareFlawless: '✨ 無傷',
  shareStudied: '📖 学習済み',
  shareStreak: (days: number) => `🔥 ${days}日連続`,
  shareChain: (n: number) => `🔗 ${n} 連続クリア`,

  // ---------- share actions ----------
  copyResult: '結果をコピー',
  shareResult: '結果を共有',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `無傷 · 今日の${oneIn}人に1人`,
  claimChain: (n: number) => `${n} 連続クリア`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · 毎日の送電線パズル',

  // ---------- the language picker ----------
  settingLanguage: '言語',
  langTitle: '言語',
  langLabel: '言語',

  // ---------- the controls row ----------
  helpBtn: '遊び方',
  practiceBtn: '練習',
  newMapBtn: '新しい地図',
  archiveBtn: '記録',
  yardBtn: '訓練',
  todayBtn: '今日',
  navProfile: 'プロフィール',
  profileTitle: 'プロフィール',
  navLabel: 'セクション',
  yesterdayBtn: '昨日の送電網',
  nextGridBtn: 'もう一つのグリッド',
  moveOnTitle: 'まだ余地があります',
  moveOnIntro: (gap: string, rank: string) => `このグリッドは${rank}まであと ${gap} MW です。`,
  moveOnCancel: '調整を続ける',
  moveOnConfirm: '次のグリッド',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Wirespan の盤面。矢印キーで測量カーソルを動かす。Enter キーまたはスペースキーで鉄塔を建てるか撤去する。',

  // ---------- the operator readout ----------
  hudTowers: '鉄塔',
  hudHouses: '家',
  hudOutput: '出力',
  hudStreak: '連続',

  // ---------- archive ----------
  archiveLabel: '記録',
  archiveTitle: '記録',
  close: '閉じる',

  // ---------- drills overlay ----------
  yardLabel: '訓練',
  yardTitle: '訓練',
  yardIntro: '腕を上げるための課題。',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: '参考解を学ぶ',
  studyTitle: '技術者の送電網を学ぶ',
  studyIntro:
    'この送電網はまだ完璧ではない。学ぶと、その日は記録にも共有にも<b>📖 学習済み</b>と付く。学習済みの完璧も完璧のまま。そう書いてあるだけ。',
  studyCancel: '今はやめる',
  studyConfirm: '学ぶ',

  // ---------- settings ----------
  settingsLabel: '設定',
  settingsTitle: '設定',
  setSound: '音',
  setHaptics: '振動',
  setMotion: '動きを減らす',
  setContrast: '高コントラスト',
  setText: '文字を大きく',
  setUsage: '利用データ',
  privacyBtn: 'プライバシーポリシー',
  settingsDone: '完了',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: '意見を送る',
  feedbackLabel: '意見を送る',
  feedbackTitle: '意見を送る',
  feedbackIntro: 'これを作った人への一通。ほかのどこにも行かない。',
  feedbackKinds: JA_JP_FB_KINDS,
  feedbackPlaceholder: '何があったかを書く。',
  feedbackMessageLabel: 'メッセージ',
  feedbackLeft: (n: number) => `残り${n}`,
  feedbackContact: 'メール（返信がほしいとき）',
  feedbackContactHint: 'you@example.com',
  feedbackModes: JA_JP_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `一緒に送るもの：送電網 #${grid}・${mode}・ビルド ${build}。ほかは何も送らない。`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `一緒に送るもの：${mode}・ビルド ${build}。ほかは何も送らない。`,
  feedbackSend: '送信',
  feedbackSending: '送信中…',
  feedbackDone: '送信した。ありがとう。',
  feedbackFailOffline: '送れなかった。文はここに残っている。少し置いてもう一度。',
  feedbackFailThrottled: '今日は五通まで。受付は明日また開く。',
  feedbackFailInvalid: '通らなかった。アドレスを書いたなら確かめる。',

  // ---------- how to play ----------
  helpLabel: '遊び方',
  helpTitle: 'WIRESPAN の遊び方',
  helpIntro: '発電所から<b>すべての家</b>へ、できるだけ少ない電線で電気を届ける。',
  helpRuleBuild: '<b>タップで鉄塔を建てる、または撤去する。</b>電線は2マス以内のものをつなぐ。',
  helpRuleGround: '<b>鉄塔は開けた地面にしか建たない。</b>電線は川も尾根も越えられる。',
  helpRuleShorter:
    '<b>電線が短いほど MW は増える。</b>すべて灯ったあとも並べ替えを続けて最高記録を狙う。',
  helpRuleDaily: '<b>送電網は世界共通、深夜0時に更新。</b>練習ならいつでも新しい送電網が引ける。',
};

// ------------------------------------------------------------ ko-KR --

// -------------------------------------------------------- Korean (ko-KR) --

const KO_KR_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: '세 집에 불 켜기',
    teaches:
      '마른 땅을 눌러 송전탑을 세우십시오. 전기는 닿는 범위 안의 모든 것으로 흐릅니다. 세 집 모두에 불을 켜십시오.',
  },
  'tuning-rearrange': {
    name: '송전탑을 옮겨 점수 올리기',
    teaches:
      '전력망은 돌아가지만 송전탑 자리가 나쁩니다. 하나를 철거하고 더 나은 자리에 세운 다음 점수가 오르는 것을 보십시오.',
  },
  'diagonal-tax-weave': {
    name: '더 비싼 대각선',
    teaches:
      '대각선 전선은 직선보다 멀리 가지 못하면서 41% 더 듭니다. 경로를 곧게 펴서 전선을 아끼십시오.',
  },
  'triangle-trap-fan': {
    name: '삼각형 피하기',
    teaches:
      '닿는 범위 안의 송전탑 두 기 사이에는 쓸모가 없어도 전선이 생깁니다. 삼각형을 이룬 송전탑 세 기에는 전선 세 가닥이 붙습니다. 삼각형을 깨면 그중 한 가닥이 사라집니다.',
  },
  'triangle-trap-delta': {
    name: '둘을 대신하는 송전탑 하나',
    teaches:
      '가까이 붙은 송전탑 두 기는 각자의 선로 말고도 서로를 잇는 전선까지 얻습니다. 가운데에 세운 송전탑 한 기면 그 여분 전선 없이 두 기 몫을 합니다.',
  },
  'exact-spacing-footings': {
    name: '정확히 두 칸 간격',
    teaches:
      '정확히 두 칸 간격으로 놓인 송전탑은 이웃하고만 이어집니다. 더 가까워지면 전선이 더 생깁니다. 간격을 정확히 지키십시오.',
  },
  'junction-neck': {
    name: '송전탑 하나로 세 집',
    teaches:
      '세 집 모두에 닿는 송전탑 한 기면 전부 밝힐 수 있습니다. 집마다 가지를 뻗는 대신 그 자리를 찾으십시오.',
  },
  'junction-approach': {
    name: '따로 낸 길, 따로 드는 비용',
    teaches:
      '집마다 따로 낸 길은 끝까지 제 전선을 씁니다. 선로 한 줄기를 세 집 쪽으로 보내고 마지막 송전탑이 세 집에 전기를 보내게 하십시오.',
  },
  'junction-late-split': {
    name: '최대한 늦게 갈라지기',
    teaches:
      '집들이 두 방향으로 당기면 선로는 갈라져야 합니다. 공용 선로를 갈 수 있는 데까지 끌고 간 다음 집 가까이에서 가르십시오. 일찍 가르면 긴 선로 두 줄기 값을 치릅니다.',
  },
  'lake-narrows': {
    name: '알맞은 건널목 고르기',
    teaches:
      '전선은 물이 한 칸 폭인 곳에서만 건널 수 있습니다. 이 물은 두 곳에서 좁아집니다. 발전소와 집을 잇는 직선 위에서 건너면 경로가 가장 짧습니다.',
  },
  'lake-west-arm': {
    name: '물을 돌아가기',
    teaches: '물은 지도 끝에 닿기 전에 끝납니다. 그 끝을 돌아가는 편이 어떤 건널목보다 짧습니다.',
  },
};

const KO_KR_TOPOLOGIES: Record<Topology, string> = {
  spine: '척추',
  'y-split': 'Y 분기',
  backbranch: '뒷가지',
  'river-kiss': '강 입맞춤',
  crossbar: '가로대',
};

const KO_KR_RANKS: Record<TierRank, string> = {
  superconductor: '초전도체',
  master: '명장',
  journeyman: '숙련',
  apprentice: '견습',
};

const KO_KR_FB_KINDS: Record<FeedbackKind, string> = {
  bug: '오류',
  idea: '아이디어',
  grid: '이 전력망',
  other: '기타',
};

const KO_KR_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: '일일',
  archive: '보관함',
  practice: '연습',
  drill: '훈련',
};

const KO_KR_RANKS_FULL: Record<TierRank, string> = {
  superconductor: '초전도체',
  master: '명장 선로공',
  journeyman: '숙련 선로공',
  apprentice: '견습 선로공',
};

const KO_KR: Strings = {
  // ---------- board nudges ----------
  denyWater: '송전탑은 마른 땅에 섭니다. 전선은 물을 건널 수 있습니다.',
  denyMtn: '송전탑을 세우기에는 너무 가파릅니다. 전선은 봉우리를 넘을 수 있습니다.',
  denyLimit: '송전탑이 없습니다. 먼저 하나를 철거하십시오.',
  gridFailed: '전력망이 시작되지 않았습니다. 새로 고쳐 보십시오.',
  newBest: (mw: string) => `신기록: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: '오프라인 준비 완료.',
  resultCopied: '결과 복사 완료.',
  copyBlocked: '복사가 막혔습니다. 스크린샷도 됩니다.',

  // ---------- the result card ----------
  perfectGrid: '완벽한 전력망!',
  lineBeaten: '단순 경로 돌파',
  noteBeatEngineers: '기술진을 넘어섰습니다.',
  noteGridDark: '전력망이 꺼졌습니다. 다시 세워 조율을 이어가십시오.',
  takeN: (n: number) => `시도 ${n}`,
  noteGapTo: (gap: string, rank: string) => `${rank}까지 ${gap} MW. 계속 조율하십시오.`,
  noteFlawless: '무결점. 송전탑을 한 기도 철거하지 않았습니다.',
  noteLessWire: (pct: number) => `단순 경로보다 전선을 ${pct}% 덜 썼습니다.`,
  noteLessWireTuning: (pct: number) =>
    `단순 경로보다 전선을 ${pct}% 덜 썼습니다. 계속 조율하십시오.`,
  legendReveal: '하루가 끝나면 기술진의 전력망을 살펴볼 수 있습니다.',
  chipBest: '최고',
  chipRank: '등급',
  chipMoves: '조작',
  chipNaive: '단순 경로',
  firstTake: '첫 시도',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `다음 전력망까지 <b>${wait}</b>.`,
  newGridReady: '새 전력망이 준비됐습니다.',
  waitHours: '시간',
  waitMinutes: '분',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `일일 전력망 <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>연습</strong> · 제한 없음',
  daytagDrill: '<strong>훈련</strong>',

  // ---------- drills ----------
  drills: KO_KR_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` 단순 경로는 <b>${mw}</b> MW입니다. 이를 넘으십시오.`,
  nextDrill: '다음 훈련',
  todaysGrid: '오늘의 전력망',

  // ---------- the study reveal ----------
  revealShow: '기술진의 전력망 보기',
  revealHide: '기술진의 전력망 숨기기',

  // ---------- archive, drills list, records ----------
  rowPlay: '시작',
  rowStudied: '열람',
  recordsEmpty: '아직 기록이 없습니다. 전력망을 가동하면 기록이 열립니다.',
  recStreak: '연속',
  recBestRun: '최장 연속',
  recGrids: '전력망',
  recPerfect: '완벽',
  recFlawless: '무결점',
  recBeatEngineers: '기술진 넘어섬',
  recMedian: '중간값',
  recTunedPast: '첫 시도 이후 조율',
  recStudied: '열람',
  recDrills: '훈련',

  // ---------- the field (GDD §4.7) ----------
  fieldName: '참가자',
  fieldPlayed: (players: string) => `<b>${players}</b>명 참여`,
  fieldStanding: (pct: number, players: string) =>
    `<b>${players}</b>명 가운데 <b>${pct}%</b>를 넘어섰습니다`,
  fieldYou: '나',
  fieldAverage: '평균',
  fieldYoung: '순위 집계 중입니다. 내일 확정됩니다.',
  fieldRanks: '등급',
  yesterdayStanding: (pct: number, players: string) =>
    `어제: ${players}명 가운데 ${pct}%를 넘어섰습니다.`,
  yesterdayPlayed: (players: string, avg: string) => `어제: ${players}명 참여${avg}.`,
  yesterdayAvg: (mw: number) => ` · 평균 ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: KO_KR_TOPOLOGIES,
  ranks: KO_KR_RANKS,
  ranksFull: KO_KR_RANKS_FULL,

  // ---------- the share text (GDD §6.3) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: '연습',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · 첫 시도`,
  shareBeatRef: '알려진 최고의 전력망을 넘어섰습니다!',
  shareFlawless: '✨ 무결점',
  shareStudied: '📖 열람',
  shareStreak: (days: number) => `🔥 ${days}일 연속`,
  shareChain: (n: number) => `🔗 ${n}개 연속`,

  // ---------- share actions ----------
  copyResult: '결과 복사',
  shareResult: '결과 공유',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `무결점 · 오늘 ${oneIn}명 중 1명`,
  claimChain: (n: number) => `${n}개 연속`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · 일일 송전선 퍼즐',

  // ---------- the language picker ----------
  settingLanguage: '언어',
  langTitle: '언어',
  langLabel: '언어',

  // ---------- the controls row ----------
  helpBtn: '게임 방법',
  practiceBtn: '연습',
  newMapBtn: '새 지도',
  archiveBtn: '보관함',
  yardBtn: '훈련',
  todayBtn: '오늘',
  navProfile: '프로필',
  profileTitle: '프로필',
  navLabel: '섹션',
  yesterdayBtn: '어제의 전력망',
  nextGridBtn: '그리드 하나 더',
  moveOnTitle: '아직 여지가 있습니다',
  moveOnIntro: (gap: string, rank: string) => `이 그리드는 ${rank}까지 ${gap} MW 남았습니다.`,
  moveOnCancel: '계속 조율',
  moveOnConfirm: '다음 그리드',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Wirespan 퍼즐 판. 화살표 키로 측량 커서를 옮기고 Enter 또는 Space로 송전탑을 세우거나 철거합니다.',

  // ---------- the operator readout ----------
  hudTowers: '송전탑',
  hudHouses: '집',
  hudOutput: '출력',
  hudStreak: '연속',

  // ---------- archive ----------
  archiveLabel: '보관함',
  archiveTitle: '보관함',
  close: '닫기',

  // ---------- drills overlay ----------
  yardLabel: '훈련',
  yardTitle: '훈련',
  yardIntro: '실력을 올리는 수업입니다.',

  // ---------- the one confirmation in the game ----------
  studyLabel: '기준 전력망 열람',
  studyTitle: '기술진의 전력망 열람',
  studyIntro:
    '이 전력망은 아직 완벽하지 않습니다. 지금 열람하면 이날은 보관함과 공유 문구에 <b>📖 열람</b>으로 ' +
    '남습니다. 열람한 완벽도 완벽입니다. 그렇게 적힐 뿐입니다.',
  studyCancel: '아직 아닙니다',
  studyConfirm: '열람합니다',

  // ---------- settings ----------
  settingsLabel: '설정',
  settingsTitle: '설정',
  setSound: '소리',
  setHaptics: '진동',
  setMotion: '동작 줄이기',
  setContrast: '고대비',
  setText: '큰 글자',
  setUsage: '사용 데이터',
  privacyBtn: '개인정보 처리방침',
  settingsDone: '완료',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: '의견 보내기',
  feedbackLabel: '의견 보내기',
  feedbackTitle: '의견 보내기',
  feedbackIntro: '이것을 만든 사람에게 가는 한 통입니다. 다른 어디로도 가지 않습니다.',
  feedbackKinds: KO_KR_FB_KINDS,
  feedbackPlaceholder: '무슨 일이 있었는지 적어 주십시오.',
  feedbackMessageLabel: '메시지',
  feedbackLeft: (n: number) => `${n}자 남음`,
  feedbackContact: '이메일, 답장을 원하시면',
  feedbackContactHint: 'you@example.com',
  feedbackModes: KO_KR_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `함께 보내는 것: 전력망 #${grid} · ${mode} · 빌드 ${build}. 그 밖에는 없습니다.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `함께 보내는 것: ${mode} · 빌드 ${build}. 그 밖에는 없습니다.`,
  feedbackSend: '보내기',
  feedbackSending: '보내는 중…',
  feedbackDone: '보냈습니다. 감사합니다.',
  feedbackFailOffline: '보내지 못했습니다. 메시지는 그대로 있습니다. 잠시 후 다시 시도하십시오.',
  feedbackFailThrottled: '오늘 다섯 통을 채웠습니다. 우편함은 내일 다시 열립니다.',
  feedbackFailInvalid: '통과하지 못했습니다. 주소를 적으셨다면 확인하십시오.',

  // ---------- how to play ----------
  helpLabel: '게임 방법',
  helpTitle: 'WIRESPAN 게임 방법',
  helpIntro: '발전소에서 <b>모든 집</b>까지 최대한 적은 전선으로 전기를 보내십시오.',
  helpRuleBuild:
    '<b>눌러서 송전탑을 세우거나 철거합니다.</b> 전선은 2칸 이내의 모든 것을 잇습니다.',
  helpRuleGround: '<b>송전탑은 트인 땅에만 세웁니다.</b> 전선은 강과 능선을 건널 수 있습니다.',
  helpRuleShorter:
    '<b>전선이 짧을수록 더 많은 MW를 보냅니다.</b> 불이 다 켜진 뒤에도 계속 자리를 바꿔 ' +
    '최고 기록을 세우십시오.',
  helpRuleDaily:
    '<b>전 세계가 같은 전력망, 자정마다 새로 나옵니다.</b> 연습은 언제든 새 전력망을 ' +
    '내줍니다.',
};

// ------------------------------------------------------------ zh-Hant --

// -------------------------------------------- Traditional Chinese (zh-Hant) --

const ZH_HANT_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: '點亮房屋',
    teaches: '點一下乾地就能立起一座鐵塔。電力會流向範圍內的一切。把三棟房屋都點亮。',
  },
  'tuning-rearrange': {
    name: '移動鐵塔，提高分數',
    teaches: '電網會通，但鐵塔的位置不好。拆掉一座，改放到更好的地方，看著分數往上走。',
  },
  'diagonal-tax-weave': {
    name: '斜線比較貴',
    teaches: '斜的電線伸得不比直的遠，卻要多花 41% 的電線。把路線拉直，省下電線。',
  },
  'triangle-trap-fan': {
    name: '避開三角形',
    teaches:
      '範圍內的每一對鐵塔之間都會出現電線，就算毫無幫助也一樣。三座鐵塔排成三角形就會有三條電線。打破三角形，其中一條就會消失。',
  },
  'triangle-trap-delta': {
    name: '一座鐵塔抵兩座',
    teaches:
      '兩座鐵塔靠得太近，除了各自的線路，中間還會多一條電線。改放一座在中間，就能做完那一對的工作，也省下多出來的電線。',
  },
  'exact-spacing-footings': {
    name: '相隔兩格',
    teaches: '鐵塔剛好隔兩格時，只會和相鄰的那一座相連。再近一點就會多出電線。間距要抓準。',
  },
  'junction-neck': {
    name: '一座鐵塔，三棟房屋',
    teaches: '一座鐵塔只要同時搆得到三棟房屋，就能全部供電。找出那個位置，不要每一棟都拉一條支線。',
  },
  'junction-approach': {
    name: '分開走就分開付',
    teaches:
      '每一棟房屋各走一條路，整段都要用自己的電線。拉一條線同時朝三棟過去，讓最後一座鐵塔供電給它們。',
  },
  'junction-late-split': {
    name: '越晚分岔越好',
    teaches:
      '房屋往兩個方向拉扯時，線路就得分岔。先把共用的一條線帶到最遠，再靠近房屋才分開。太早分岔，等於付兩條長線的錢。',
  },
  'lake-narrows': {
    name: '選對跨越點',
    teaches:
      '電線只能在水面寬一格的地方跨越。這片水域有兩處變窄。跨在電廠和房屋的連線上，路線最短。',
  },
  'lake-west-arm': {
    name: '繞過水面',
    teaches: '水域在地圖邊緣之前就結束了。繞過末端比任何跨越都短。',
  },
};

const ZH_HANT_TOPOLOGIES: Record<Topology, string> = {
  spine: '主幹線',
  'y-split': 'Y字分岔',
  backbranch: '回頭支線',
  'river-kiss': '吻河線',
  crossbar: '橫擔',
};

const ZH_HANT_RANKS: Record<TierRank, string> = {
  superconductor: '超導體',
  master: '師傅',
  journeyman: '技工',
  apprentice: '學徒',
};

const ZH_HANT_FB_KINDS: Record<FeedbackKind, string> = {
  bug: '出了問題',
  idea: '一個想法',
  grid: '這個電網',
  other: '其他',
};

const ZH_HANT_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: '每日',
  archive: '檔案庫',
  practice: '練習',
  drill: '訓練',
};

const ZH_HANT_RANKS_FULL: Record<TierRank, string> = {
  superconductor: '超導體',
  master: '架線師傅',
  journeyman: '架線技工',
  apprentice: '架線學徒',
};

const ZH_HANT: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: '鐵塔要蓋在乾地上。電線可以跨越水面。',
  denyMtn: '這裡太陡，蓋不了鐵塔。電線可以越過山頂。',
  denyLimit: '鐵塔用完了。先拆掉一座。',
  gridFailed: '電網啟動失敗。請重新載入。',
  newBest: (mw: string) => `最佳紀錄：${mw} MW。`,

  // ---------- chrome news ----------
  offlineReady: '離線也能玩。',
  resultCopied: '成績已複製。',
  copyBlocked: '複製被擋下。截圖也可以。',

  // ---------- the result card ----------
  perfectGrid: '完美電網!',
  lineBeaten: '超越直覺路線',
  noteBeatEngineers: '你贏過了工程師。',
  noteGridDark: '電網暗著。重新接起來才能繼續調校。',
  takeN: (n: number) => `第 ${n} 次`,
  noteGapTo: (gap: string, rank: string) => `距離${rank}還差 ${gap} MW。繼續調校。`,
  noteFlawless: '無瑕。全程沒拆過一座鐵塔。',
  noteLessWire: (pct: number) => `用的電線比直覺路線少 ${pct}%。`,
  noteLessWireTuning: (pct: number) => `用的電線比直覺路線少 ${pct}%。繼續調校。`,
  legendReveal: '一天結束後，工程師自己的電網就開放研究。',
  chipBest: '最佳',
  chipRank: '等級',
  chipMoves: '步數',
  chipNaive: '直覺路線',
  firstTake: '初讀',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `下一座電網還有<b>${wait}</b>。`,
  newGridReady: '新的電網準備好了。',
  waitHours: '小時',
  waitMinutes: '分',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `每日電網 <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>練習</strong> · 怎麼接都行',
  daytagDrill: '<strong>訓練</strong>',

  // ---------- drills ----------
  drills: ZH_HANT_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}：</strong>${teaches}${goal}`,
  drillGoal: (mw: string) => ` 直覺路線讀數 <b>${mw}</b> MW。超越它。`,
  nextDrill: '下一個訓練',
  todaysGrid: '今天的電網',

  // ---------- the study reveal ----------
  revealShow: '顯示工程師的電網',
  revealHide: '隱藏工程師的電網',

  // ---------- archive, drills list, records ----------
  rowPlay: '遊玩',
  rowStudied: '已研讀',
  recordsEmpty: '還沒有任何紀錄。讓一座電網通電就會開始記錄。',
  recStreak: '連續',
  recBestRun: '最長連續',
  recGrids: '電網',
  recPerfect: '完美',
  recFlawless: '無瑕',
  recBeatEngineers: '贏過工程師',
  recMedian: '中位數',
  recTunedPast: '調校超越初讀',
  recStudied: '已研讀',
  recDrills: '訓練',

  // ---------- the field (GDD §4.7) ----------
  fieldName: '全場',
  fieldPlayed: (players: string) => `<b>${players}</b> 人參與`,
  fieldStanding: (pct: number, players: string) =>
    `<b>${players}</b> 位玩家裡，你勝過了 <b>${pct}%</b>`,
  fieldYou: '你',
  fieldAverage: '平均',
  fieldYoung: '名次還在回報中。明天定案。',
  fieldRanks: '等級',
  yesterdayStanding: (pct: number, players: string) =>
    `昨天：${players} 位玩家裡，你勝過了 ${pct}%。`,
  yesterdayPlayed: (players: string, avg: string) => `昨天：${players} 人參與${avg}。`,
  yesterdayAvg: (mw: number) => ` · 平均 ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: ZH_HANT_TOPOLOGIES,
  ranks: ZH_HANT_RANKS,
  ranksFull: ZH_HANT_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: '練習',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · 初讀`,
  shareBeatRef: '超越了已知最佳電網!',
  shareFlawless: '✨ 無瑕',
  shareStudied: '📖 已研讀',
  shareStreak: (days: number) => `🔥 連續 ${days} 天`,
  shareChain: (n: number) => `🔗 連續 ${n} 張電網`,

  // ---------- share actions ----------
  copyResult: '複製成績',
  shareResult: '分享成績',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `無瑕 · 今天 ${oneIn} 人中有 1 人`,
  claimChain: (n: number) => `連續 ${n} 張電網`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · 每日輸電線解謎',

  // ---------- the language picker ----------
  settingLanguage: '語言',
  langTitle: '語言',
  langLabel: '語言',

  // ---------- the controls row ----------
  helpBtn: '玩法',
  practiceBtn: '練習',
  newMapBtn: '新地圖',
  archiveBtn: '檔案庫',
  yardBtn: '訓練',
  todayBtn: '今天',
  navProfile: '個人檔案',
  profileTitle: '個人檔案',
  navLabel: '區段',
  yesterdayBtn: '昨天的電網',
  nextGridBtn: '再來一張電網',
  moveOnTitle: '這張還有空間',
  moveOnIntro: (gap: string, rank: string) => `這張電網距離${rank}還差 ${gap} MW。`,
  moveOnCancel: '繼續調校',
  moveOnConfirm: '下一張電網',

  // ---------- the board, as keyboard chrome ----------
  boardLabel: 'Wirespan 解謎盤面。方向鍵移動測量游標；Enter 或空白鍵放置或拆除鐵塔。',

  // ---------- the operator readout ----------
  hudTowers: '鐵塔',
  hudHouses: '房屋',
  hudOutput: '輸出',
  hudStreak: '連續',

  // ---------- archive ----------
  archiveLabel: '檔案庫',
  archiveTitle: '檔案庫',
  close: '關閉',

  // ---------- drills overlay ----------
  yardLabel: '訓練',
  yardTitle: '訓練',
  yardIntro: '幫你把技術練得更好的課題。',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: '研讀參考解',
  studyTitle: '研讀工程師的電網',
  studyIntro:
    '這座電網還不完美，研讀之後，這一天在你的檔案庫和分享裡都會標上<b>📖 已研讀</b>。' +
    '研讀過的完美依然是完美，只是會註明。',
  studyCancel: '還不要',
  studyConfirm: '研讀',

  // ---------- settings ----------
  settingsLabel: '設定',
  settingsTitle: '設定',
  setSound: '音效',
  setHaptics: '震動',
  setMotion: '減少動態',
  setContrast: '高對比',
  setText: '放大文字',
  setUsage: '使用資料',
  privacyBtn: '隱私權政策',
  settingsDone: '完成',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: '傳送意見',
  feedbackLabel: '傳送意見',
  feedbackTitle: '傳送意見',
  feedbackIntro: '一則訊息，送給做出這個遊戲的人。不會去別的地方。',
  feedbackKinds: ZH_HANT_FB_KINDS,
  feedbackPlaceholder: '說說發生了什麼。',
  feedbackMessageLabel: '你的訊息',
  feedbackLeft: (n: number) => `剩 ${n}`,
  feedbackContact: '電子郵件，想要回覆的話',
  feedbackContactHint: 'you@example.com',
  feedbackModes: ZH_HANT_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `一起送出：電網 #${grid}・${mode}・版本 ${build}。其他都不送。`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `一起送出：${mode}・版本 ${build}。其他都不送。`,
  feedbackSend: '傳送',
  feedbackSending: '傳送中…',
  feedbackDone: '已送出。謝謝。',
  feedbackFailOffline: '沒送出去，你的訊息還在。過一會再試。',
  feedbackFailThrottled: '今天五則到了。信箱明天再開。',
  feedbackFailInvalid: '沒有通過。如果填了地址，檢查一下。',

  // ---------- how to play ----------
  helpLabel: '玩法',
  helpTitle: 'Wirespan 玩法',
  helpIntro: '用最少的電線，把電從電廠送到<b>每一棟房屋</b>。',
  helpRuleBuild: '<b>點一下就能蓋起或拆除鐵塔。</b>電線會連上 2 格內的一切。',
  helpRuleGround: '<b>鐵塔只能蓋在空地上。</b>電線可以跨越河流和山脊。',
  helpRuleShorter: '<b>電線越短，輸出的 MW 越多。</b>全部點亮之後繼續重排，把成績推到最好。',
  helpRuleDaily: '<b>全世界同一座電網，午夜換新。</b>練習隨時發一座新的電網。',
};

// ------------------------------------------------------------ nl-NL --

// -------------------------------------------------------------- Dutch (nl-NL) --

const NL_NL_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Geef de huizen licht',
    teaches:
      'Tik op droge grond om een mast te plaatsen. Stroom vloeit naar alles binnen bereik. Geef alle drie de huizen licht.',
  },
  'tuning-rearrange': {
    name: 'Verplaats masten, verhoog de score',
    teaches:
      'Het net werkt, maar de masten staan slecht. Haal er een neer, zet hem op een betere plek en kijk de score omhoog gaan.',
  },
  'diagonal-tax-weave': {
    name: 'Diagonalen kosten meer',
    teaches:
      'Een diagonale kabel reikt niet verder dan een rechte, maar kost 41% meer. Maak de route recht om kabel te sparen.',
  },
  'triangle-trap-fan': {
    name: 'Vermijd driehoeken',
    teaches:
      'Tussen elk paar masten binnen bereik verschijnt een kabel, ook als die niets oplevert. Drie masten in een driehoek krijgen drie kabels. Breek de driehoek en een van die kabels verdwijnt.',
  },
  'triangle-trap-delta': {
    name: 'Een mast kan er twee vervangen',
    teaches:
      'Twee masten dicht bij elkaar krijgen bovenop hun eigen lijnen ook een kabel tussen zich in. Een mast in het midden doet het werk van dat paar zonder die extra kabel.',
  },
  'exact-spacing-footings': {
    name: 'Twee vakjes uit elkaar',
    teaches:
      'Masten die precies twee vakjes uit elkaar staan, verbinden alleen met hun buren. Dichter bij elkaar verschijnen er extra kabels. Houd de afstand precies.',
  },
  'junction-neck': {
    name: 'Een mast, drie huizen',
    teaches:
      'Een enkele mast binnen bereik van alle drie de huizen voedt ze alle drie. Zoek die plek in plaats van een tak naar elk huis te bouwen.',
  },
  'junction-approach': {
    name: 'Aparte paden betaal je apart',
    teaches:
      'Een apart pad naar elk huis gebruikt de hele weg zijn eigen kabel. Leg een lijn naar alle drie en laat de laatste mast ze voeden.',
  },
  'junction-late-split': {
    name: 'Splits zo laat mogelijk',
    teaches:
      'Als de huizen twee kanten op trekken, moet de lijn splitsen. Breng een gedeelde lijn zo ver als het gaat en splits pas dicht bij de huizen. Vroeg splitsen betaalt twee lange lijnen.',
  },
  'lake-narrows': {
    name: 'Kies de juiste oversteek',
    teaches:
      'Kabels kruisen water alleen waar het een vakje breed is. Dit water versmalt op twee plekken. Oversteken op één lijn met de centrale en de huizen geeft de kortste route.',
  },
  'lake-west-arm': {
    name: 'Ga om het water heen',
    teaches:
      'Het water eindigt voor de rand van de kaart. Om het uiteinde heen is korter dan elke oversteek.',
  },
};

const NL_NL_TOPOLOGIES: Record<Topology, string> = {
  spine: 'de ruggengraat',
  'y-split': 'de Y-splitsing',
  backbranch: 'de achtertak',
  'river-kiss': 'de rivierkus',
  crossbar: 'de dwarsbalk',
};

const NL_NL_RANKS: Record<TierRank, string> = {
  superconductor: 'Supergeleider',
  master: 'Meester',
  journeyman: 'Gezel',
  apprentice: 'Leerling',
};

const NL_NL_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Er ging iets stuk',
  idea: 'Een idee',
  grid: 'Dit net',
  other: 'Iets anders',
};

const NL_NL_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'dagelijks',
  archive: 'archief',
  practice: 'oefenen',
  drill: 'les',
};

const NL_NL_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Supergeleider',
  master: 'Meester-netmonteur',
  journeyman: 'Gezel-netmonteur',
  apprentice: 'Leerling-netmonteur',
};

const NL_NL: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'Masten staan op droge grond. Kabels overspannen het water.',
  denyMtn: 'Te steil voor een mast. Kabels gaan over de top heen.',
  denyLimit: 'Geen masten meer. Haal er eerst een neer.',
  gridFailed: 'Het net startte niet. Probeer opnieuw te laden.',
  newBest: (mw: string) => `Nieuw record: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Klaar om offline te spelen.',
  resultCopied: 'Resultaat gekopieerd.',
  copyBlocked: 'Kopiëren geblokkeerd. Een screenshot werkt ook.',

  // ---------- the result card ----------
  perfectGrid: 'PERFECT NET!',
  lineBeaten: 'LIJN VERSLAGEN',
  noteBeatEngineers: 'Je hebt de ingenieurs verslagen.',
  noteGridDark: 'Net donker. Bouw opnieuw en stel bij.',
  takeN: (n: number) => `POGING ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW tot ${rank}. Blijf bijstellen.`,
  noteFlawless: 'Foutloos. Nooit een mast neergehaald.',
  noteLessWire: (pct: number) => `${pct}% minder kabel dan de simpele lijn.`,
  noteLessWireTuning: (pct: number) =>
    `${pct}% minder kabel dan de simpele lijn. Blijf bijstellen.`,
  legendReveal: 'Als de dag voorbij is, ligt het net van de ingenieurs open om te bestuderen.',
  chipBest: 'record',
  chipRank: 'rang',
  chipMoves: 'zetten',
  chipNaive: 'simpele lijn',
  firstTake: 'eerste meting',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Volgend net over <b>${wait}</b>.`,
  newGridReady: 'Nieuw net klaar.',
  /** The units `formatWait` builds "9 u 41 min" out of. */
  waitHours: ' u',
  waitMinutes: ' min',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Dagelijks net <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Oefenen</strong> · alles mag',
  daytagDrill: '<strong>Les</strong>',

  // ---------- drills ----------
  drills: NL_NL_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` De simpele lijn komt op <b>${mw}</b> MW. Doe het beter.`,
  nextDrill: 'Volgende les',
  todaysGrid: 'Net van vandaag',

  // ---------- the study reveal ----------
  revealShow: 'Toon het net van de ingenieurs',
  revealHide: 'Verberg het net van de ingenieurs',

  // ---------- archive, drills list, records ----------
  rowPlay: 'spelen',
  rowStudied: 'bestudeerd',
  recordsEmpty: 'Nog niets genoteerd. Zet een net onder spanning om het logboek te openen.',
  recStreak: 'reeks',
  recBestRun: 'langste reeks',
  recGrids: 'netten',
  recPerfect: 'perfect',
  recFlawless: 'foutloos',
  recBeatEngineers: 'ingenieurs verslagen',
  recMedian: 'mediaan',
  recTunedPast: 'eerste meting verbeterd',
  recStudied: 'bestudeerd',
  recDrills: 'lessen',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'het veld',
  fieldPlayed: (players: string) => `<b>${players}</b> gespeeld`,
  fieldStanding: (pct: number, players: string) =>
    `je verslaat <b>${pct}%</b> van <b>${players}</b> spelers`,
  fieldYou: 'jij',
  fieldAverage: 'gemiddeld',
  fieldYoung: 'De stand komt nog binnen. Morgen definitief.',
  fieldRanks: 'rangen',
  yesterdayStanding: (pct: number, players: string) =>
    `Gisteren: je versloeg ${pct}% van ${players} spelers.`,
  yesterdayPlayed: (players: string, avg: string) => `Gisteren: ${players} gespeeld${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW gemiddeld`,

  // ---------- the reference's own vocabulary ----------
  topologies: NL_NL_TOPOLOGIES,
  ranks: NL_NL_RANKS,
  ranksFull: NL_NL_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'oefenen',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · eerste meting`,
  shareBeatRef: 'het beste bekende net verslagen!',
  shareFlawless: '✨ foutloos',
  shareStudied: '📖 bestudeerd',
  shareStreak: (days: number) => `🔥 reeks van ${days} dagen`,
  shareChain: (n: number) => `🔗 ${n} netten op rij`,

  // ---------- share actions ----------
  copyResult: 'Resultaat kopiëren',
  shareResult: 'Resultaat delen',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Foutloos · 1 op ${oneIn} vandaag`,
  claimChain: (n: number) => `${n} netten op rij`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · de dagelijkse hoogspanningspuzzel',

  // ---------- the language picker ----------
  settingLanguage: 'Taal',
  langTitle: 'TAAL',
  langLabel: 'Taal',

  // ---------- the controls row ----------
  helpBtn: 'Spelregels',
  practiceBtn: 'Oefenen',
  newMapBtn: 'Nieuwe kaart',
  archiveBtn: 'Archief',
  yardBtn: 'Lessen',
  todayBtn: 'Vandaag',
  navProfile: 'Profiel',
  profileTitle: 'PROFIEL',
  navLabel: 'Secties',
  yesterdayBtn: 'Net van gisteren',
  nextGridBtn: 'Nog een net',
  moveOnTitle: 'HIER ZIT NOG REK IN',
  moveOnIntro: (gap: string, rank: string) => `Je zit ${gap} MW van ${rank} op dit net.`,
  moveOnCancel: 'Blijf bijstellen',
  moveOnConfirm: 'Volgend net',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Wirespan-speelbord. Pijltoetsen verplaatsen de meetcursor; Enter of spatie plaatst of verwijdert een mast.',

  // ---------- the operator readout ----------
  hudTowers: 'Masten',
  hudHouses: 'Huizen',
  hudOutput: 'Levering',
  hudStreak: 'Reeks',

  // ---------- archive ----------
  archiveLabel: 'Archief',
  archiveTitle: 'ARCHIEF',
  close: 'Sluiten',

  // ---------- drills overlay ----------
  yardLabel: 'Lessen',
  yardTitle: 'LESSEN',
  yardIntro: 'Lessen om beter te gaan spelen.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Referentie bestuderen',
  studyTitle: 'BESTUDEER HET NET VAN DE INGENIEURS',
  studyIntro:
    'Dit net is nog niet perfect, dus bestuderen markeert de dag als <b>📖 bestudeerd</b> ' +
    'in je archief en in wat je deelt. Een bestudeerd perfect net blijft perfect. Het staat er alleen bij.',
  studyCancel: 'Nog niet',
  studyConfirm: 'Bestuderen',

  // ---------- settings ----------
  settingsLabel: 'Instellingen',
  settingsTitle: 'INSTELLINGEN',
  setSound: 'Geluid',
  setHaptics: 'Trillen',
  setMotion: 'Minder beweging',
  setContrast: 'Hoog contrast',
  setText: 'Grotere tekst',
  setUsage: 'Gebruiksgegevens',
  privacyBtn: 'Privacybeleid',
  settingsDone: 'Klaar',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Feedback sturen',
  feedbackLabel: 'Feedback sturen',
  feedbackTitle: 'FEEDBACK STUREN',
  feedbackIntro: 'Eén bericht aan de persoon die dit gebouwd heeft. Het gaat nergens anders heen.',
  feedbackKinds: NL_NL_FB_KINDS,
  feedbackPlaceholder: 'Zeg wat er gebeurde.',
  feedbackMessageLabel: 'Je bericht',
  feedbackLeft: (n: number) => `${n} over`,
  feedbackContact: 'E-mail, als je antwoord wilt',
  feedbackContactHint: 'jij@voorbeeld.nl',
  feedbackModes: NL_NL_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Gaat mee: net #${grid} · ${mode} · build ${build}. Verder niets.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Gaat mee: ${mode} · build ${build}. Verder niets.`,
  feedbackSend: 'Sturen',
  feedbackSending: 'Versturen…',
  feedbackDone: 'Verstuurd. Bedankt.',
  feedbackFailOffline: 'Het ging niet weg, en je bericht staat er nog. Probeer het straks opnieuw.',
  feedbackFailThrottled: 'Dat zijn vijf berichten vandaag. De bus gaat morgen weer open.',
  feedbackFailInvalid: 'Het kwam niet door. Controleer het adres, als je er een invulde.',

  // ---------- how to play ----------
  helpLabel: 'Spelregels',
  helpTitle: 'SPELREGELS VAN WIRESPAN',
  helpIntro: 'Breng stroom van de centrale naar <b>elk huis</b> met zo min mogelijk draad.',
  helpRuleBuild:
    '<b>Tik om een mast te bouwen of weg te halen.</b> Kabels verbinden alles binnen 2 vakjes.',
  helpRuleGround:
    '<b>Masten moeten op open grond staan.</b> Kabels kruisen rivieren en bergkammen.',
  helpRuleShorter:
    '<b>Kortere kabels leveren meer MW.</b> Blijf masten verplaatsen als alles brandt, ' +
    'voor je record.',
  helpRuleDaily:
    '<b>Hetzelfde net voor de hele wereld, elke middernacht nieuw.</b> Oefenen geeft ' +
    'wanneer je wilt een nieuw net.',
};

// ------------------------------------------------------------ pl-PL --

// --------------------------------------------------------- Polish (pl-PL) --

const PL_PL_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Zapal domy',
    teaches:
      'Dotknij suchego gruntu, aby postawić słup. Prąd płynie do wszystkiego w zasięgu. Zapal wszystkie trzy domy.',
  },
  'tuning-rearrange': {
    name: 'Przestaw słupy, podnieś wynik',
    teaches:
      'Sieć działa, ale słupy stoją w kiepskich miejscach. Rozbierz jeden, postaw go lepiej i patrz, jak wynik rośnie.',
  },
  'diagonal-tax-weave': {
    name: 'Skosy kosztują więcej',
    teaches:
      'Przewód po skosie nie sięga dalej niż prosty, a kosztuje o 41% więcej. Wyprostuj trasę, żeby oszczędzić przewód.',
  },
  'triangle-trap-fan': {
    name: 'Unikaj trójkątów',
    teaches:
      'Przewód pojawia się między każdą parą słupów w zasięgu, nawet gdy nic nie daje. Trzy słupy w trójkącie dostają trzy przewody. Rozerwij trójkąt, a jeden z tych przewodów zniknie.',
  },
  'triangle-trap-delta': {
    name: 'Jeden słup zastąpi dwa',
    teaches:
      'Dwa słupy blisko siebie dostają przewód między sobą, poza własnymi liniami. Jeden słup pośrodku wykona robotę tej pary bez tego dodatkowego przewodu.',
  },
  'exact-spacing-footings': {
    name: 'Dwa pola odstępu',
    teaches:
      'Słupy oddalone dokładnie o dwa pola łączą się tylko z sąsiadami. Bliżej pojawiają się zbędne przewody. Pilnuj dokładnego odstępu.',
  },
  'junction-neck': {
    name: 'Jeden słup, trzy domy',
    teaches:
      'Pojedynczy słup w zasięgu wszystkich trzech domów zasili je wszystkie. Znajdź to miejsce zamiast budować odgałęzienie do każdego domu.',
  },
  'junction-approach': {
    name: 'Osobna trasa kosztuje osobno',
    teaches:
      'Osobna trasa do każdego domu zużywa własny przewód na całej długości. Poprowadź jedną linię ku wszystkim trzem i pozwól ostatniemu słupowi je zasilić.',
  },
  'junction-late-split': {
    name: 'Rozdziel jak najpóźniej',
    teaches:
      'Kiedy domy ciągną w dwie strony, linia musi się rozdzielić. Prowadź jedną wspólną linię tak daleko, jak się da, potem rozdziel ją blisko domów. Wczesny rozdział płaci za dwie długie linie.',
  },
  'lake-narrows': {
    name: 'Wybierz właściwe przejście',
    teaches:
      'Przewody przechodzą nad wodą tylko tam, gdzie ma ona szerokość jednego pola. Ta woda zwęża się w dwóch miejscach. Przejście w linii elektrowni i domów daje najkrótszą trasę.',
  },
  'lake-west-arm': {
    name: 'Obejdź wodę',
    teaches:
      'Woda kończy się przed krawędzią mapy. Obejście jej końca jest krótsze niż jakiekolwiek przejście.',
  },
};

const PL_PL_TOPOLOGIES: Record<Topology, string> = {
  spine: 'kręgosłup',
  'y-split': 'rozwidlenie Y',
  backbranch: 'odgałęzienie wsteczne',
  'river-kiss': 'pocałunek rzeki',
  crossbar: 'poprzeczka',
};

const PL_PL_RANKS: Record<TierRank, string> = {
  superconductor: 'Nadprzewodnik',
  master: 'Mistrz',
  journeyman: 'Czeladnik',
  apprentice: 'Uczeń',
};

const PL_PL_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Coś się zepsuło',
  idea: 'Pomysł',
  grid: 'Ta sieć',
  other: 'Coś innego',
};

const PL_PL_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'dnia',
  archive: 'archiwum',
  practice: 'trening',
  drill: 'ćwiczenie',
};

const PL_PL_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Nadprzewodnik',
  master: 'Mistrz monterski',
  journeyman: 'Czeladnik monterski',
  apprentice: 'Uczeń monterski',
};

const PL_PL: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'Słupy potrzebują suchego gruntu. Przewody przechodzą nad wodą.',
  denyMtn: 'Za stromo na słup. Przewody przechodzą nad szczytem.',
  denyLimit: 'Koniec słupów. Najpierw rozbierz jeden.',
  gridFailed: 'Sieć nie wystartowała. Spróbuj odświeżyć.',
  newBest: (mw: string) => `Nowy rekord: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Gotowe do gry offline.',
  resultCopied: 'Wynik skopiowany.',
  copyBlocked: 'Kopiowanie zablokowane. Zrzut ekranu też zadziała.',

  // ---------- the result card ----------
  perfectGrid: 'SIEĆ IDEALNA!',
  lineBeaten: 'LINIA POBITA',
  noteBeatEngineers: 'Pobiłeś inżynierów.',
  noteGridDark: 'Sieć bez napięcia. Odbuduj, żeby dostrajać dalej.',
  takeN: (n: number) => `PRÓBA ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW do rangi ${rank}. Dostrajaj dalej.`,
  noteFlawless: 'Bez skazy. Żaden słup nie został rozebrany.',
  noteLessWire: (pct: number) => `${pct}% mniej przewodu niż naiwna linia.`,
  noteLessWireTuning: (pct: number) => `${pct}% mniej przewodu niż naiwna linia. Dostrajaj dalej.`,
  legendReveal: 'Gdy dzień dobiegnie końca, sieć inżynierów otwiera się do wglądu.',
  chipBest: 'rekord',
  chipRank: 'stopień',
  chipMoves: 'ruchy',
  chipNaive: 'naiwna linia',
  firstTake: 'pierwsze podejście',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Następna sieć za <b>${wait}</b>.`,
  newGridReady: 'Nowa sieć gotowa.',
  waitHours: ' godz.',
  waitMinutes: ' min',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Sieć dnia <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Trening</strong> · wszystko wolno',
  daytagDrill: '<strong>Ćwiczenie</strong>',

  // ---------- drills ----------
  drills: PL_PL_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` Naiwna linia daje <b>${mw}</b> MW. Pobij ją.`,
  nextDrill: 'Następne ćwiczenie',
  todaysGrid: 'Dzisiejsza sieć',

  // ---------- the study reveal ----------
  revealShow: 'Pokaż sieć inżynierów',
  revealHide: 'Ukryj sieć inżynierów',

  // ---------- archive, drills list, records ----------
  rowPlay: 'graj',
  rowStudied: 'przestudiowana',
  recordsEmpty: 'Nic jeszcze nie zapisano. Załącz sieć pod napięcie, żeby otworzyć rejestr.',
  recStreak: 'seria',
  recBestRun: 'rekord serii',
  recGrids: 'sieci',
  recPerfect: 'idealne',
  recFlawless: 'bez skazy',
  recBeatEngineers: 'pobici inżynierowie',
  recMedian: 'mediana',
  recTunedPast: 'poprawione pierwsze podejście',
  recStudied: 'przestudiowane',
  recDrills: 'ćwiczenia',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'stawka',
  fieldPlayed: (players: string) => `zagrało <b>${players}</b>`,
  fieldStanding: (pct: number, players: string) =>
    `pobiłeś <b>${pct}%</b> z <b>${players}</b> graczy`,
  fieldYou: 'ty',
  fieldAverage: 'średnia',
  fieldYoung: 'Wyniki wciąż spływają. Jutro będą ostateczne.',
  fieldRanks: 'stopnie',
  yesterdayStanding: (pct: number, players: string) =>
    `Wczoraj: pobiłeś ${pct}% z ${players} graczy.`,
  yesterdayPlayed: (players: string, avg: string) => `Wczoraj: zagrało ${players}${avg}.`,
  yesterdayAvg: (mw: number) => ` · średnio ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: PL_PL_TOPOLOGIES,
  ranks: PL_PL_RANKS,
  ranksFull: PL_PL_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'trening',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · pierwsze podejście`,
  shareBeatRef: 'pobita najlepsza znana sieć!',
  shareFlawless: '✨ bez skazy',
  shareStudied: '📖 przestudiowana',
  shareStreak: (days: number) => `🔥 seria ${days} dni`,
  shareChain: (n: number) => `🔗 ${n} sieci z rzędu`,

  // ---------- share actions ----------
  copyResult: 'Kopiuj wynik',
  shareResult: 'Udostępnij wynik',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Bez skazy · 1 na ${oneIn} dzisiaj`,
  claimChain: (n: number) => `${n} sieci z rzędu`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · codzienna łamigłówka o liniach energetycznych',

  // ---------- the language picker ----------
  settingLanguage: 'Język',
  langTitle: 'JĘZYK',
  langLabel: 'Język',

  // ---------- the controls row ----------
  helpBtn: 'Jak grać',
  practiceBtn: 'Trening',
  newMapBtn: 'Nowa mapa',
  archiveBtn: 'Archiwum',
  yardBtn: 'Ćwiczenia',
  todayBtn: 'Dziś',
  navProfile: 'Profil',
  profileTitle: 'PROFIL',
  navLabel: 'Sekcje',
  yesterdayBtn: 'Wczorajsza sieć',
  nextGridBtn: 'Jeszcze jedna sieć',
  moveOnTitle: 'TU JEST JESZCZE ZAPAS',
  moveOnIntro: (gap: string, rank: string) => `Do rangi ${rank} brakuje ${gap} MW na tej sieci.`,
  moveOnCancel: 'Dostrajaj dalej',
  moveOnConfirm: 'Następna sieć',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Plansza Wirespan. Strzałki przesuwają kursor pomiarowy; Enter lub Spacja stawia albo rozbiera słup.',

  // ---------- the operator readout ----------
  hudTowers: 'Słupy',
  hudHouses: 'Domy',
  hudOutput: 'Moc',
  hudStreak: 'Seria',

  // ---------- archive ----------
  archiveLabel: 'Archiwum',
  archiveTitle: 'ARCHIWUM',
  close: 'Zamknij',

  // ---------- drills overlay ----------
  yardLabel: 'Ćwiczenia',
  yardTitle: 'ĆWICZENIA',
  yardIntro: 'Lekcje, które poprawią twoją grę.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Przestudiuj wzorzec',
  studyTitle: 'PRZESTUDIUJ SIEĆ INŻYNIERÓW',
  studyIntro:
    'Ta sieć nie jest jeszcze idealna, więc przestudiowanie oznaczy ją jako <b>📖 przestudiowaną</b> ' +
    'w archiwum i w udostępnionym wyniku. Idealna przestudiowana wciąż jest idealna. Tylko to odnotowuje.',
  studyCancel: 'Jeszcze nie',
  studyConfirm: 'Przestudiuj',

  // ---------- settings ----------
  settingsLabel: 'Ustawienia',
  settingsTitle: 'USTAWIENIA',
  setSound: 'Dźwięk',
  setHaptics: 'Wibracje',
  setMotion: 'Mniej ruchu',
  setContrast: 'Wysoki kontrast',
  setText: 'Większy tekst',
  setUsage: 'Dane o użyciu',
  privacyBtn: 'Polityka prywatności',
  settingsDone: 'Gotowe',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Wyślij wiadomość',
  feedbackLabel: 'Wyślij wiadomość',
  feedbackTitle: 'WYŚLIJ WIADOMOŚĆ',
  feedbackIntro: 'Jedna wiadomość do osoby, która to zbudowała. Nie idzie nigdzie indziej.',
  feedbackKinds: PL_PL_FB_KINDS,
  feedbackPlaceholder: 'Napisz, co się stało.',
  feedbackMessageLabel: 'Twoja wiadomość',
  feedbackLeft: (n: number) => `zostało ${n}`,
  feedbackContact: 'E-mail, jeśli chcesz odpowiedź',
  feedbackContactHint: 'ty@przyklad.pl',
  feedbackModes: PL_PL_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Leci razem: sieć #${grid} · ${mode} · wersja ${build}. Nic więcej.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Leci razem: ${mode} · wersja ${build}. Nic więcej.`,
  feedbackSend: 'Wyślij',
  feedbackSending: 'Wysyłanie…',
  feedbackDone: 'Wysłane. Dziękuję.',
  feedbackFailOffline: 'Nie wyszło, a twoja wiadomość nadal tu jest. Spróbuj za chwilę.',
  feedbackFailThrottled: 'To pięć wiadomości dzisiaj. Skrzynka otwiera się znowu jutro.',
  feedbackFailInvalid: 'Nie przeszło. Sprawdź adres, jeśli go wpisałeś.',

  // ---------- how to play ----------
  helpLabel: 'Jak grać',
  helpTitle: 'JAK GRAĆ W WIRESPAN',
  helpIntro: 'Poprowadź prąd z elektrowni do <b>każdego domu</b>, zużywając jak najmniej przewodu.',
  helpRuleBuild:
    '<b>Dotknij, aby postawić lub rozebrać słup.</b> Przewody łączą wszystko w promieniu 2 pól.',
  helpRuleGround:
    '<b>Słupy stoją tylko na otwartym gruncie.</b> Przewody przechodzą nad rzekami i graniami.',
  helpRuleShorter:
    '<b>Krótszy przewód dostarcza więcej MW.</b> Przestawiaj słupy dalej, gdy wszystko już świeci, ' +
    'i poprawiaj rekord.',
  helpRuleDaily:
    '<b>Ta sama sieć dla całego świata, nowa o północy.</b> Trening rozdaje świeżą sieć ' +
    'o dowolnej porze.',
};

// ------------------------------------------------------------ tr-TR --

// -------------------------------------------------------- Turkish (tr-TR) --

const TR_TR_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Evleri yak',
    teaches: 'Direk dikmek için kuru zemine dokun. Enerji menzildeki her şeye akar. Üç evi de yak.',
  },
  'tuning-rearrange': {
    name: 'Direkleri taşı, puanı yükselt',
    teaches:
      'Şebeke çalışıyor ama direkler kötü yerlerde duruyor. Birini kaldır, daha iyi bir yere dik ve puanın yükselişini izle.',
  },
  'diagonal-tax-weave': {
    name: 'Çapraz daha pahalı',
    teaches:
      'Çapraz kablo düz olandan daha uzağa uzanmaz ama %41 daha pahalıdır. Kablo tasarrufu için rotayı düzelt.',
  },
  'triangle-trap-fan': {
    name: 'Üçgenlerden kaçın',
    teaches:
      'Menzildeki her direk çifti arasında bir kablo belirir, hiçbir işe yaramasa bile. Üçgen dizilen üç direk üç kablo alır. Üçgeni boz, o kablolardan biri kaybolur.',
  },
  'triangle-trap-delta': {
    name: 'Bir direk ikisinin yerini tutar',
    teaches:
      'Yan yana duran iki direk, kendi hatlarına ek olarak aralarında bir kablo alır. Ortadaki tek bir direk, o fazladan kablo olmadan ikisinin işini görür.',
  },
  'exact-spacing-footings': {
    name: 'İki kare arayla',
    teaches:
      'Tam iki kare arayla dikilen direkler yalnızca komşularına bağlanır. Daha yakında fazladan kablolar belirir. Aralığı tam tut.',
  },
  'junction-neck': {
    name: 'Tek direk, üç ev',
    teaches:
      'Üç evin de menzilinde duran tek bir direk hepsini besleyebilir. Her eve ayrı bir dal çekmek yerine o noktayı bul.',
  },
  'junction-approach': {
    name: 'Ayrı yollar ayrı öder',
    teaches:
      'Her eve giden ayrı bir yol, baştan sona kendi kablosunu harcar. Üçüne birden tek bir hat çek, son direk onları beslesin.',
  },
  'junction-late-split': {
    name: 'Olabildiğince geç ayır',
    teaches:
      'Evler iki yöne çektiğinde hat ayrılmak zorunda kalır. Ortak tek hattı gidebildiği kadar götür, sonra evlere yakın bir yerde ayır. Erken ayrılan hat iki uzun hattın parasını öder.',
  },
  'lake-narrows': {
    name: 'Doğru geçidi seç',
    teaches:
      'Kablolar suyu yalnızca bir kare genişliğindeki yerde aşabilir. Bu su iki noktada daralıyor. Santral ve evlerle aynı hizada geçmek en kısa rotayı verir.',
  },
  'lake-west-arm': {
    name: 'Suyun etrafından dolaş',
    teaches: 'Su harita kenarına varmadan bitiyor. Uçtan dolaşmak her geçitten kısadır.',
  },
};

const TR_TR_TOPOLOGIES: Record<Topology, string> = {
  spine: 'omurga',
  'y-split': 'Y çatalı',
  backbranch: 'arka dal',
  'river-kiss': 'nehir öpücüğü',
  crossbar: 'travers',
};

const TR_TR_RANKS: Record<TierRank, string> = {
  superconductor: 'Süperiletken',
  master: 'Usta',
  journeyman: 'Kalfa',
  apprentice: 'Çırak',
};

const TR_TR_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Bir şey bozuldu',
  idea: 'Bir fikir',
  grid: 'Bu şebeke',
  other: 'Başka bir şey',
};

const TR_TR_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'günlük',
  archive: 'arşiv',
  practice: 'alıştırma',
  drill: 'ders',
};

const TR_TR_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Süperiletken',
  master: 'Usta Hat İşçisi',
  journeyman: 'Kalfa Hat İşçisi',
  apprentice: 'Çırak Hat İşçisi',
};

const TR_TR: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'Direkler kuru zemine dikilir. Kablolar suyu aşabilir.',
  denyMtn: 'Direk için fazla dik. Kablolar zirveyi aşabilir.',
  denyLimit: 'Direk kalmadı. Önce birini kaldır.',
  gridFailed: 'Şebeke başlatılamadı. Yeniden yüklemeyi dene.',
  newBest: (mw: string) => `Yeni rekor: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Çevrimdışı oynamaya hazır.',
  resultCopied: 'Sonuç kopyalandı.',
  copyBlocked: 'Kopyalama engellendi. Ekran görüntüsü de olur.',

  // ---------- the result card ----------
  perfectGrid: 'MÜKEMMEL ŞEBEKE!',
  lineBeaten: 'HAT GEÇİLDİ',
  noteBeatEngineers: 'Mühendisleri geçtin.',
  noteGridDark: 'Şebeke karanlık. İnce ayar için yeniden kur.',
  takeN: (n: number) => `DENEME ${n}`,
  noteGapTo: (gap: string, rank: string) => `${rank} için ${gap} MW. İnce ayara devam et.`,
  noteFlawless: 'Kusursuz. Hiç direk kaldırmadın.',
  noteLessWire: (pct: number) => `Kaba hattan %${pct} daha az kablo.`,
  noteLessWireTuning: (pct: number) => `Kaba hattan %${pct} daha az kablo. İnce ayara devam et.`,
  legendReveal: 'Gün bitince mühendislerin kendi şebekesi incelemeye açılır.',
  chipBest: 'rekor',
  chipRank: 'rütbe',
  chipMoves: 'hamle',
  chipNaive: 'kaba hat',
  firstTake: 'ilk okuma',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Yeni şebekeye <b>${wait}</b> kaldı.`,
  newGridReady: 'Yeni şebeke hazır.',
  /** The units `formatWait` builds "9 sa 41 dk" out of. */
  waitHours: ' sa',
  waitMinutes: ' dk',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Günlük şebeke <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Alıştırma</strong> · her şey serbest',
  daytagDrill: '<strong>Ders</strong>',

  // ---------- drills ----------
  drills: TR_TR_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` Kaba hat <b>${mw}</b> MW veriyor. Onu geç.`,
  nextDrill: 'Sonraki ders',
  todaysGrid: 'Bugünün şebekesi',

  // ---------- the study reveal ----------
  revealShow: 'Mühendislerin şebekesini göster',
  revealHide: 'Mühendislerin şebekesini gizle',

  // ---------- archive, drills list, records ----------
  rowPlay: 'oyna',
  rowStudied: 'incelendi',
  recordsEmpty: 'Henüz kayıt yok. Kaydı açmak için bir şebekeyi enerjilendir.',
  recStreak: 'seri',
  recBestRun: 'rekor seri',
  recGrids: 'şebeke',
  recPerfect: 'mükemmel',
  recFlawless: 'kusursuz',
  recBeatEngineers: 'mühendisleri geçti',
  recMedian: 'ortanca',
  recTunedPast: 'ilk okumayı geçti',
  recStudied: 'incelendi',
  recDrills: 'ders',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'saha',
  fieldPlayed: (players: string) => `<b>${players}</b> oynadı`,
  fieldStanding: (pct: number, players: string) =>
    `<b>${players}</b> oyuncunun %<b>${pct}</b> kadarını geçtin`,
  fieldYou: 'sen',
  fieldAverage: 'ortalama',
  fieldYoung: 'Sıralama hâlâ oluşuyor. Kesin sonuç yarın.',
  fieldRanks: 'rütbeler',
  yesterdayStanding: (pct: number, players: string) =>
    `Dün: ${players} oyuncunun %${pct} kadarını geçtin.`,
  yesterdayPlayed: (players: string, avg: string) => `Dün: ${players} oyuncu oynadı${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW ortalama`,

  // ---------- the reference's own vocabulary ----------
  topologies: TR_TR_TOPOLOGIES,
  ranks: TR_TR_RANKS,
  ranksFull: TR_TR_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'alıştırma',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · ilk okuma`,
  shareBeatRef: 'bilinen en iyi şebekeyi geçti!',
  shareFlawless: '✨ kusursuz',
  shareStudied: '📖 incelendi',
  shareStreak: (days: number) => `🔥 ${days} günlük seri`,
  shareChain: (n: number) => `🔗 üst üste ${n} şebeke`,

  // ---------- share actions ----------
  copyResult: 'Sonucu kopyala',
  shareResult: 'Sonucu paylaş',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Kusursuz · bugün ${oneIn} kişide 1`,
  claimChain: (n: number) => `Üst üste ${n} şebeke`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · günlük enerji hattı bulmacası',

  // ---------- the language picker ----------
  settingLanguage: 'Dil',
  langTitle: 'DİL',
  langLabel: 'Dil',

  // ---------- the controls row ----------
  helpBtn: 'Nasıl oynanır',
  practiceBtn: 'Alıştırma',
  newMapBtn: 'Yeni harita',
  archiveBtn: 'Arşiv',
  yardBtn: 'Dersler',
  todayBtn: 'Bugün',
  navProfile: 'Profil',
  profileTitle: 'PROFİL',
  navLabel: 'Bölümler',
  yesterdayBtn: 'Dünün şebekesi',
  nextGridBtn: 'Bir şebeke daha',
  moveOnTitle: 'BURADA HÂLÂ PAY VAR',
  moveOnIntro: (gap: string, rank: string) => `Bu şebekede ${rank} için ${gap} MW kaldı.`,
  moveOnCancel: 'İnce ayara devam',
  moveOnConfirm: 'Sonraki şebeke',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Wirespan bulmaca tahtası. Ok tuşları ölçüm imlecini taşır; Enter veya Boşluk direk diker ya da kaldırır.',

  // ---------- the operator readout ----------
  hudTowers: 'Direk',
  hudHouses: 'Ev',
  hudOutput: 'Çıkış',
  hudStreak: 'Seri',

  // ---------- archive ----------
  archiveLabel: 'Arşiv',
  archiveTitle: 'ARŞİV',
  close: 'Kapat',

  // ---------- drills overlay ----------
  yardLabel: 'Dersler',
  yardTitle: 'DERSLER',
  yardIntro: 'Oyununu geliştiren dersler.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Referansı incele',
  studyTitle: 'MÜHENDİSLERİN ŞEBEKESİNİ İNCELE',
  studyIntro:
    'Bu şebeke henüz mükemmel değil, bu yüzden incelemek günü arşivinde ve paylaşımında ' +
    '<b>📖 incelendi</b> diye işaretler. İncelenmiş bir mükemmel yine mükemmeldir. Yalnızca bunu belirtir.',
  studyCancel: 'Henüz değil',
  studyConfirm: 'İncele',

  // ---------- settings ----------
  settingsLabel: 'Ayarlar',
  settingsTitle: 'AYARLAR',
  setSound: 'Ses',
  setHaptics: 'Titreşim',
  setMotion: 'Hareketi azalt',
  setContrast: 'Yüksek kontrast',
  setText: 'Daha büyük yazı',
  setUsage: 'Kullanım verileri',
  privacyBtn: 'Gizlilik politikası',
  settingsDone: 'Tamam',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Geri bildirim gönder',
  feedbackLabel: 'Geri bildirim gönder',
  feedbackTitle: 'GERİ BİLDİRİM GÖNDER',
  feedbackIntro: 'Bunu yapan kişiye tek bir mesaj. Başka hiçbir yere gitmiyor.',
  feedbackKinds: TR_TR_FB_KINDS,
  feedbackPlaceholder: 'Ne olduğunu yaz.',
  feedbackMessageLabel: 'Mesajın',
  feedbackLeft: (n: number) => `${n} kaldı`,
  feedbackContact: 'E-posta, yanıt istersen',
  feedbackContactHint: 'sen@ornek.com',
  feedbackModes: TR_TR_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Birlikte gider: şebeke #${grid} · ${mode} · yapı ${build}. Başka bir şey yok.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Birlikte gider: ${mode} · yapı ${build}. Başka bir şey yok.`,
  feedbackSend: 'Gönder',
  feedbackSending: 'Gönderiliyor…',
  feedbackDone: 'Gönderildi. Teşekkürler.',
  feedbackFailOffline: 'Gitmedi, mesajın hâlâ burada. Az sonra yine dene.',
  feedbackFailThrottled: 'Bugün beş mesaj oldu. Kutu yarın yine açılıyor.',
  feedbackFailInvalid: 'Geçmedi. Adres yazdıysan kontrol et.',

  // ---------- how to play ----------
  helpLabel: 'Nasıl oynanır',
  helpTitle: 'WIRESPAN NASIL OYNANIR',
  helpIntro: 'Santralden <b>her eve</b> olabildiğince az kabloyla enerji taşı.',
  helpRuleBuild:
    '<b>Direk dikmek ya da kaldırmak için dokun.</b> Kablolar 2 kare içindeki her şeyi bağlar.',
  helpRuleGround: '<b>Direkler açık zemine dikilir.</b> Kablolar nehirleri ve sırtları aşabilir.',
  helpRuleShorter:
    '<b>Kısa kablo daha çok MW taşır.</b> Her şey yandıktan sonra direkleri yeniden dizerek ' +
    'rekorunu yükselt.',
  helpRuleDaily:
    '<b>Tüm dünyaya aynı şebeke, gece yarısı yenisi.</b> Alıştırma istediğin an yeni bir ' +
    'şebeke dağıtır.',
};

// ------------------------------------------------------------ id-ID --

// ------------------------------------------------------ Indonesian (id-ID) --

const ID_ID_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Nyalakan rumah',
    teaches:
      'Ketuk tanah kering untuk mendirikan menara. Listrik mengalir ke semua yang terjangkau. Nyalakan ketiga rumah.',
  },
  'tuning-rearrange': {
    name: 'Pindahkan menara, naikkan nilai',
    teaches:
      'Jaringan sudah menyala, tetapi menaranya berdiri di tempat yang buruk. Bongkar satu, pasang di tempat yang lebih baik, lalu lihat nilainya naik.',
  },
  'diagonal-tax-weave': {
    name: 'Diagonal lebih mahal',
    teaches:
      'Kabel diagonal tidak menjangkau lebih jauh daripada kabel lurus, tetapi biayanya 41% lebih besar. Luruskan jalurnya untuk menghemat kabel.',
  },
  'triangle-trap-fan': {
    name: 'Hindari segitiga',
    teaches:
      'Kabel muncul di antara setiap pasang menara yang saling terjangkau, bahkan ketika tidak ada gunanya. Tiga menara dalam segitiga mendapat tiga kabel. Patahkan segitiganya dan satu kabel itu hilang.',
  },
  'triangle-trap-delta': {
    name: 'Satu menara bisa menggantikan dua',
    teaches:
      'Dua menara yang berdekatan mendapat kabel di antara keduanya, di luar jalurnya masing-masing. Satu menara di tengah bisa melakukan tugas pasangan itu tanpa kabel tambahan.',
  },
  'exact-spacing-footings': {
    name: 'Berjarak dua kotak',
    teaches:
      'Menara yang berjarak tepat dua kotak hanya menyambung ke tetangganya. Lebih dekat sedikit, kabel tambahan muncul. Jaga jaraknya tetap tepat.',
  },
  'junction-neck': {
    name: 'Satu menara, tiga rumah',
    teaches:
      'Satu menara yang menjangkau ketiga rumah bisa menyalakan semuanya. Cari titik itu, alih-alih membangun cabang ke tiap rumah.',
  },
  'junction-approach': {
    name: 'Jalur terpisah dibayar terpisah',
    teaches:
      'Jalur terpisah ke tiap rumah memakai kabelnya sendiri di sepanjang perjalanan. Tarik satu jalur ke arah ketiganya, lalu biarkan menara terakhir yang memberi daya.',
  },
  'junction-late-split': {
    name: 'Bercabang selambat mungkin',
    teaches:
      'Ketika rumah menarik ke dua arah, jalurnya harus bercabang. Bawa satu jalur bersama sejauh mungkin, lalu bercabang dekat rumah. Bercabang terlalu awal berarti membayar dua jalur panjang.',
  },
  'lake-narrows': {
    name: 'Pilih penyeberangan yang tepat',
    teaches:
      'Kabel hanya bisa melintasi air di tempat yang lebarnya satu kotak. Air ini menyempit di dua tempat. Menyeberang sejajar dengan pembangkit dan rumah memberi jalur terpendek.',
  },
  'lake-west-arm': {
    name: 'Putari airnya',
    teaches:
      'Air berakhir sebelum tepi peta. Memutari ujungnya lebih pendek daripada menyeberang di mana pun.',
  },
};

const ID_ID_TOPOLOGIES: Record<Topology, string> = {
  spine: 'tulang punggung',
  'y-split': 'cabang Y',
  backbranch: 'cabang belakang',
  'river-kiss': 'ciuman sungai',
  crossbar: 'palang',
};

const ID_ID_RANKS: Record<TierRank, string> = {
  superconductor: 'Superkonduktor',
  master: 'Ahli',
  journeyman: 'Tukang',
  apprentice: 'Magang',
};

const ID_ID_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Ada yang rusak',
  idea: 'Sebuah ide',
  grid: 'Jaringan ini',
  other: 'Hal lain',
};

const ID_ID_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'harian',
  archive: 'arsip',
  practice: 'latihan',
  drill: 'pelajaran',
};

const ID_ID_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Superkonduktor',
  master: 'Ahli Listrik',
  journeyman: 'Tukang Listrik',
  apprentice: 'Magang Listrik',
};

const ID_ID: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'Menara butuh tanah kering. Kabel bisa melintasi air.',
  denyMtn: 'Terlalu curam untuk menara. Kabel bisa melewati puncak.',
  denyLimit: 'Menara habis. Bongkar satu dulu.',
  gridFailed: 'Jaringan gagal menyala. Coba muat ulang.',
  newBest: (mw: string) => `Rekor baru: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Siap dimainkan tanpa koneksi.',
  resultCopied: 'Hasil tersalin.',
  copyBlocked: 'Gagal menyalin. Tangkapan layar juga bisa.',

  // ---------- the result card ----------
  perfectGrid: 'JARINGAN SEMPURNA!',
  lineBeaten: 'JALUR TERLAMPAUI',
  noteBeatEngineers: 'Kamu mengalahkan insinyur.',
  noteGridDark: 'Jaringan padam. Bangun ulang untuk terus menyetel.',
  takeN: (n: number) => `PERCOBAAN ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW lagi ke ${rank}. Terus setel.`,
  noteFlawless: 'Mulus. Tidak pernah membongkar menara.',
  noteLessWire: (pct: number) => `Kabel ${pct}% lebih sedikit daripada jalur seadanya.`,
  noteLessWireTuning: (pct: number) =>
    `Kabel ${pct}% lebih sedikit daripada jalur seadanya. Terus setel.`,
  legendReveal: 'Setelah harinya usai, jaringan insinyur sendiri terbuka untuk dipelajari.',
  chipBest: 'terbaik',
  chipRank: 'peringkat',
  chipMoves: 'langkah',
  chipNaive: 'jalur seadanya',
  firstTake: 'bacaan pertama',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Jaringan berikutnya <b>${wait}</b> lagi.`,
  newGridReady: 'Jaringan baru siap.',
  /** The units `formatWait` builds "9h 41m" out of. */
  waitHours: ' jam',
  waitMinutes: ' mnt',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Jaringan harian <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Latihan</strong> · bebas',
  daytagDrill: '<strong>Pelajaran</strong>',

  // ---------- drills ----------
  drills: ID_ID_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` Jalur seadanya membaca <b>${mw}</b> MW. Lampaui itu.`,
  nextDrill: 'Pelajaran berikutnya',
  todaysGrid: 'Jaringan hari ini',

  // ---------- the study reveal ----------
  revealShow: 'Tampilkan jaringan insinyur',
  revealHide: 'Sembunyikan jaringan insinyur',

  // ---------- archive, drills list, records ----------
  rowPlay: 'main',
  rowStudied: 'dipelajari',
  recordsEmpty: 'Belum ada yang tercatat. Nyalakan jaringan untuk membuka catatan.',
  recStreak: 'beruntun',
  recBestRun: 'terpanjang',
  recGrids: 'jaringan',
  recPerfect: 'sempurna',
  recFlawless: 'mulus',
  recBeatEngineers: 'mengalahkan insinyur',
  recMedian: 'median',
  recTunedPast: 'melampaui bacaan pertama',
  recStudied: 'dipelajari',
  recDrills: 'pelajaran',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'peserta',
  fieldPlayed: (players: string) => `<b>${players}</b> bermain`,
  fieldStanding: (pct: number, players: string) =>
    `kamu mengalahkan <b>${pct}%</b> dari <b>${players}</b> pemain`,
  fieldYou: 'kamu',
  fieldAverage: 'rata-rata',
  fieldYoung: 'Hasil masih berdatangan. Final besok.',
  fieldRanks: 'peringkat',
  yesterdayStanding: (pct: number, players: string) =>
    `Kemarin: kamu mengalahkan ${pct}% dari ${players} pemain.`,
  yesterdayPlayed: (players: string, avg: string) => `Kemarin: ${players} orang bermain${avg}.`,
  yesterdayAvg: (mw: number) => ` · rata-rata ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: ID_ID_TOPOLOGIES,
  ranks: ID_ID_RANKS,
  ranksFull: ID_ID_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'latihan',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · bacaan pertama`,
  shareBeatRef: 'mengalahkan jaringan terbaik yang diketahui!',
  shareFlawless: '✨ mulus',
  shareStudied: '📖 dipelajari',
  shareStreak: (days: number) => `🔥 ${days} hari beruntun`,
  shareChain: (n: number) => `🔗 ${n} jaringan beruntun`,

  // ---------- share actions ----------
  copyResult: 'Salin hasil',
  shareResult: 'Bagikan hasil',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Mulus · 1 dari ${oneIn} hari ini`,
  claimChain: (n: number) => `${n} jaringan beruntun`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · teka-teki jaringan listrik harian',

  // ---------- the language picker ----------
  settingLanguage: 'Bahasa',
  langTitle: 'BAHASA',
  langLabel: 'Bahasa',

  // ---------- the controls row ----------
  helpBtn: 'Cara main',
  practiceBtn: 'Latihan',
  newMapBtn: 'Peta baru',
  archiveBtn: 'Arsip',
  yardBtn: 'Pelajaran',
  todayBtn: 'Hari ini',
  navProfile: 'Profil',
  profileTitle: 'PROFIL',
  navLabel: 'Bagian',
  yesterdayBtn: 'Jaringan kemarin',
  nextGridBtn: 'Satu jaringan lagi',
  moveOnTitle: 'MASIH ADA RUANG',
  moveOnIntro: (gap: string, rank: string) => `Kurang ${gap} MW lagi ke ${rank} di jaringan ini.`,
  moveOnCancel: 'Terus setel',
  moveOnConfirm: 'Jaringan berikutnya',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Papan teka-teki Wirespan. Tombol panah menggerakkan kursor survei; Enter atau Spasi memasang atau membongkar menara.',

  // ---------- the operator readout ----------
  hudTowers: 'Menara',
  hudHouses: 'Rumah',
  hudOutput: 'Keluaran',
  hudStreak: 'Beruntun',

  // ---------- archive ----------
  archiveLabel: 'Arsip',
  archiveTitle: 'ARSIP',
  close: 'Tutup',

  // ---------- drills overlay ----------
  yardLabel: 'Pelajaran',
  yardTitle: 'PELAJARAN',
  yardIntro: 'Pelajaran untuk mengasah permainanmu.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Pelajari referensinya',
  studyTitle: 'PELAJARI JARINGAN INSINYUR',
  studyIntro:
    'Jaringan ini belum sempurna, jadi mempelajarinya menandai hari itu <b>📖 dipelajari</b> ' +
    'di arsipmu dan di hasil yang kamu bagikan. Jaringan sempurna yang dipelajari tetap sempurna. Catatannya hanya menyebutkan itu.',
  studyCancel: 'Belum',
  studyConfirm: 'Pelajari',

  // ---------- settings ----------
  settingsLabel: 'Pengaturan',
  settingsTitle: 'PENGATURAN',
  setSound: 'Suara',
  setHaptics: 'Getaran',
  setMotion: 'Kurangi gerak',
  setContrast: 'Kontras tinggi',
  setText: 'Teks lebih besar',
  setUsage: 'Data penggunaan',
  privacyBtn: 'Kebijakan privasi',
  settingsDone: 'Selesai',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Kirim masukan',
  feedbackLabel: 'Kirim masukan',
  feedbackTitle: 'KIRIM MASUKAN',
  feedbackIntro: 'Satu pesan untuk orang yang membuat ini. Tidak pergi ke mana pun lagi.',
  feedbackKinds: ID_ID_FB_KINDS,
  feedbackPlaceholder: 'Ceritakan apa yang terjadi.',
  feedbackMessageLabel: 'Pesan Anda',
  feedbackLeft: (n: number) => `sisa ${n}`,
  feedbackContact: 'Email, kalau mau dijawab',
  feedbackContactHint: 'kamu@contoh.com',
  feedbackModes: ID_ID_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Ikut terkirim: jaringan #${grid} · ${mode} · build ${build}. Tidak ada yang lain.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Ikut terkirim: ${mode} · build ${build}. Tidak ada yang lain.`,
  feedbackSend: 'Kirim',
  feedbackSending: 'Mengirim…',
  feedbackDone: 'Terkirim. Terima kasih.',
  feedbackFailOffline: 'Tidak terkirim, dan pesanmu masih di sini. Coba lagi sebentar.',
  feedbackFailThrottled: 'Itu lima pesan hari ini. Kotaknya buka lagi besok.',
  feedbackFailInvalid: 'Tidak lolos. Periksa alamatnya, kalau kamu menulis satu.',

  // ---------- how to play ----------
  helpLabel: 'Cara main',
  helpTitle: 'CARA MAIN WIRESPAN',
  helpIntro:
    'Salurkan listrik dari pembangkit ke <b>setiap rumah</b> dengan kabel sesedikit mungkin.',
  helpRuleBuild:
    '<b>Ketuk untuk memasang atau membongkar menara.</b> Kabel menyambungkan apa pun dalam jarak 2 kotak.',
  helpRuleGround:
    '<b>Menara hanya berdiri di tanah terbuka.</b> Kabel bisa melintasi sungai dan punggung bukit.',
  helpRuleShorter:
    '<b>Kabel lebih pendek menyalurkan MW lebih besar.</b> Terus tata ulang setelah semuanya ' +
    'menyala untuk meraih nilai terbaikmu.',
  helpRuleDaily:
    '<b>Satu jaringan yang sama untuk seluruh dunia, baru setiap tengah malam.</b> Latihan ' +
    'membagikan jaringan baru kapan saja.',
};

// ------------------------------------------------------------ es-ES --

// -------------------------------------------------------- Spanish (es-ES) --

const ES_ES_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Enciende las casas',
    teaches:
      'Pulsa sobre suelo seco para levantar una torre. La energía fluye hacia todo lo que esté al alcance. Enciende las tres casas.',
  },
  'tuning-rearrange': {
    name: 'Mueve torres, sube la puntuación',
    teaches:
      'La red funciona, pero las torres están mal situadas. Quita una, colócala en un sitio mejor y mira cómo sube la puntuación.',
  },
  'diagonal-tax-weave': {
    name: 'Las diagonales cuestan más',
    teaches:
      'Un cable diagonal no llega más lejos que uno recto, pero cuesta un 41% más. Endereza la ruta para ahorrar cable.',
  },
  'triangle-trap-fan': {
    name: 'Evita los triángulos',
    teaches:
      'Aparece un cable entre cada par de torres al alcance, aunque no sirva de nada. Tres torres en triángulo reciben tres cables. Rompe el triángulo y uno de esos cables desaparece.',
  },
  'triangle-trap-delta': {
    name: 'Una torre puede sustituir a dos',
    teaches:
      'Dos torres juntas reciben un cable entre ellas además de sus propias líneas. Una sola torre en medio hace el trabajo del par sin ese cable de más.',
  },
  'exact-spacing-footings': {
    name: 'A dos casillas de distancia',
    teaches:
      'Las torres separadas exactamente dos casillas se conectan solo con sus vecinas. Más cerca aparecen cables de más. Mantén la separación exacta.',
  },
  'junction-neck': {
    name: 'Una torre, tres casas',
    teaches:
      'Una sola torre al alcance de las tres casas puede alimentarlas a todas. Encuentra ese punto en lugar de construir un ramal hacia cada casa.',
  },
  'junction-approach': {
    name: 'Cada camino aparte se paga aparte',
    teaches:
      'Un camino separado hacia cada casa gasta su propio cable todo el trayecto. Lleva una sola línea hacia las tres y deja que la última torre las alimente.',
  },
  'junction-late-split': {
    name: 'Divide lo más tarde posible',
    teaches:
      'Cuando las casas tiran en dos direcciones, la línea tiene que dividirse. Lleva una línea compartida lo más lejos que puedas y divídela cerca de las casas. Dividir pronto paga dos líneas largas.',
  },
  'lake-narrows': {
    name: 'Elige el cruce correcto',
    teaches:
      'Los cables cruzan el agua solo donde mide una casilla de ancho. Esta agua se estrecha en dos puntos. Cruzar en línea con la planta y las casas da la ruta más corta.',
  },
  'lake-west-arm': {
    name: 'Rodea el agua',
    teaches:
      'El agua termina antes del borde del mapa. Rodear la punta es más corto que cualquier cruce.',
  },
};

const ES_ES_TOPOLOGIES: Record<Topology, string> = {
  spine: 'la espina',
  'y-split': 'la Y',
  backbranch: 'el ramal trasero',
  'river-kiss': 'el beso del río',
  crossbar: 'el travesaño',
};

const ES_ES_RANKS: Record<TierRank, string> = {
  superconductor: 'Superconductor',
  master: 'Maestro',
  journeyman: 'Oficial',
  apprentice: 'Aprendiz',
};

const ES_ES_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Algo se ha roto',
  idea: 'Una idea',
  grid: 'Esta red',
  other: 'Otra cosa',
};

const ES_ES_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'diaria',
  archive: 'archivo',
  practice: 'práctica',
  drill: 'ejercicio',
};

const ES_ES_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Superconductor',
  master: 'Maestro de líneas',
  journeyman: 'Oficial de líneas',
  apprentice: 'Aprendiz de líneas',
};

const ES_ES: Strings = {
  // ---------- board nudges ----------
  denyWater: 'Las torres necesitan suelo seco. Los cables sí cruzan el agua.',
  denyMtn: 'Demasiado empinado para una torre. Los cables sí pasan sobre la cima.',
  denyLimit: 'No quedan torres. Quita una primero.',
  gridFailed: 'La red no ha arrancado. Prueba a recargar.',
  newBest: (mw: string) => `Nuevo récord: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Listo para jugar sin conexión.',
  resultCopied: 'Resultado copiado.',
  copyBlocked: 'No se ha podido copiar. Una captura de pantalla también sirve.',

  // ---------- the result card ----------
  perfectGrid: '¡RED PERFECTA!',
  lineBeaten: 'LÍNEA SUPERADA',
  noteBeatEngineers: 'Has superado a los ingenieros.',
  noteGridDark: 'Red apagada. Reconstruye para seguir afinando.',
  takeN: (n: number) => `INTENTO ${n}`,
  noteGapTo: (gap: string, rank: string) => `${gap} MW para ${rank}. Sigue afinando.`,
  noteFlawless: 'Impecable. No has quitado ninguna torre.',
  noteLessWire: (pct: number) => `Un ${pct}% menos de cable que la línea obvia.`,
  noteLessWireTuning: (pct: number) =>
    `Un ${pct}% menos de cable que la línea obvia. Sigue afinando.`,
  legendReveal: 'Al terminar el día, la red de los ingenieros se abre para estudiarla.',
  chipBest: 'mejor',
  chipRank: 'rango',
  chipMoves: 'movimientos',
  chipNaive: 'línea obvia',
  firstTake: 'primer intento',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Próxima red en <b>${wait}</b>.`,
  newGridReady: 'Nueva red lista.',
  waitHours: ' h',
  waitMinutes: ' min',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Red diaria <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Práctica</strong> · vale todo',
  daytagDrill: '<strong>Ejercicio</strong>',

  // ---------- drills ----------
  drills: ES_ES_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` La línea obvia marca <b>${mw}</b> MW. Supérala.`,
  nextDrill: 'Siguiente ejercicio',
  todaysGrid: 'La red de hoy',

  // ---------- the study reveal ----------
  revealShow: 'Ver la red de los ingenieros',
  revealHide: 'Ocultar la red de los ingenieros',

  // ---------- archive, drills list, records ----------
  rowPlay: 'jugar',
  rowStudied: 'estudiada',
  recordsEmpty: 'Aún no hay registros. Electrifica una red para abrir el historial.',
  recStreak: 'racha',
  recBestRun: 'mejor racha',
  recGrids: 'redes',
  recPerfect: 'perfectas',
  recFlawless: 'impecables',
  recBeatEngineers: 'superó a los ingenieros',
  recMedian: 'mediana',
  recTunedPast: 'mejoró su primer intento',
  recStudied: 'estudiadas',
  recDrills: 'ejercicios',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'el campo',
  fieldPlayed: (players: string) => `<b>${players}</b> jugaron`,
  fieldStanding: (pct: number, players: string) =>
    `has superado al <b>${pct}%</b> de <b>${players}</b> jugadores`,
  fieldYou: 'tú',
  fieldAverage: 'media',
  fieldYoung: 'Los resultados siguen llegando. Definitivo mañana.',
  fieldRanks: 'rangos',
  yesterdayStanding: (pct: number, players: string) =>
    `Ayer: superaste al ${pct}% de ${players} jugadores.`,
  yesterdayPlayed: (players: string, avg: string) => `Ayer: ${players} jugaron${avg}.`,
  yesterdayAvg: (mw: number) => ` · ${mw} MW de media`,

  // ---------- the reference's own vocabulary ----------
  topologies: ES_ES_TOPOLOGIES,
  ranks: ES_ES_RANKS,
  ranksFull: ES_ES_RANKS_FULL,

  // ---------- the share text (GDD §6.3) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'práctica',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · primer intento`,
  shareBeatRef: '¡superó la mejor red conocida!',
  shareFlawless: '✨ impecable',
  shareStudied: '📖 estudiada',
  shareStreak: (days: number) => `🔥 racha de ${days} días`,
  shareChain: (n: number) => `🔗 ${n} redes seguidas`,

  // ---------- share actions ----------
  copyResult: 'Copiar resultado',
  shareResult: 'Compartir resultado',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Impecable · 1 de cada ${oneIn} hoy`,
  claimChain: (n: number) => `${n} redes seguidas`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · el puzle diario de líneas eléctricas',

  // ---------- the language picker ----------
  settingLanguage: 'Idioma',
  langTitle: 'IDIOMA',
  langLabel: 'Idioma',

  // ---------- the controls row ----------
  helpBtn: 'Cómo jugar',
  practiceBtn: 'Práctica',
  newMapBtn: 'Nuevo mapa',
  archiveBtn: 'Archivo',
  yardBtn: 'Ejercicios',
  todayBtn: 'Hoy',
  navProfile: 'Perfil',
  profileTitle: 'PERFIL',
  navLabel: 'Secciones',
  yesterdayBtn: 'La red de ayer',
  nextGridBtn: 'Una red más',
  moveOnTitle: 'AÚN QUEDA MARGEN',
  moveOnIntro: (gap: string, rank: string) => `Te faltan ${gap} MW para ${rank} en esta red.`,
  moveOnCancel: 'Sigue afinando',
  moveOnConfirm: 'Siguiente red',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Tablero de Wirespan. Las flechas mueven el cursor de trazado; Intro o Espacio coloca o quita una torre.',

  // ---------- the operator readout ----------
  hudTowers: 'Torres',
  hudHouses: 'Casas',
  hudOutput: 'Entrega',
  hudStreak: 'Racha',

  // ---------- archive ----------
  archiveLabel: 'Archivo',
  archiveTitle: 'ARCHIVO',
  close: 'Cerrar',

  // ---------- drills overlay ----------
  yardLabel: 'Ejercicios',
  yardTitle: 'EJERCICIOS',
  yardIntro: 'Lecciones para mejorar tu juego.',

  // ---------- the one confirmation in the game ----------
  studyLabel: 'Estudiar la referencia',
  studyTitle: 'ESTUDIAR LA RED DE LOS INGENIEROS',
  studyIntro:
    'Esta red aún no es perfecta, así que estudiarla marca el día como <b>📖 estudiada</b> ' +
    'en tu archivo y en lo que compartas. Una perfecta estudiada sigue siendo perfecta. Solo lo dice.',
  studyCancel: 'Todavía no',
  studyConfirm: 'Estudiarla',

  // ---------- settings ----------
  settingsLabel: 'Ajustes',
  settingsTitle: 'AJUSTES',
  setSound: 'Sonido',
  setHaptics: 'Vibración',
  setMotion: 'Reducir movimiento',
  setContrast: 'Alto contraste',
  setText: 'Texto más grande',
  setUsage: 'Datos de uso',
  privacyBtn: 'Política de privacidad',
  settingsDone: 'Listo',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Enviar comentarios',
  feedbackLabel: 'Enviar comentarios',
  feedbackTitle: 'ENVIAR COMENTARIOS',
  feedbackIntro: 'Un mensaje para quien ha construido esto. No va a ninguna otra parte.',
  feedbackKinds: ES_ES_FB_KINDS,
  feedbackPlaceholder: 'Cuenta qué ha pasado.',
  feedbackMessageLabel: 'Tu mensaje',
  feedbackLeft: (n: number) => `quedan ${n}`,
  feedbackContact: 'Correo, si quieres respuesta',
  feedbackContactHint: 'tu@ejemplo.com',
  feedbackModes: ES_ES_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Se envía junto: red #${grid} · ${mode} · versión ${build}. Nada más.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Se envía junto: ${mode} · versión ${build}. Nada más.`,
  feedbackSend: 'Enviar',
  feedbackSending: 'Enviando…',
  feedbackDone: 'Enviado. Gracias.',
  feedbackFailOffline:
    'No se ha enviado, y tu mensaje sigue aquí. Vuelve a intentarlo en un momento.',
  feedbackFailThrottled: 'Son cinco mensajes hoy. El buzón vuelve a abrir mañana.',
  feedbackFailInvalid: 'No ha pasado. Revisa la dirección, si has escrito una.',

  // ---------- how to play ----------
  helpLabel: 'Cómo jugar',
  helpTitle: 'CÓMO JUGAR A WIRESPAN',
  helpIntro:
    'Lleva la energía desde la central hasta <b>todas las casas</b> con el menor cable posible.',
  helpRuleBuild:
    '<b>Pulsa para construir o quitar una torre.</b> Los cables conectan todo lo que esté a 2 casillas.',
  helpRuleGround:
    '<b>Las torres van en terreno despejado.</b> Los cables sí cruzan ríos y cordilleras.',
  helpRuleShorter:
    '<b>Menos cable entrega más MW.</b> Sigue recolocando torres cuando ya esté todo encendido ' +
    'para lograr tu mejor puntuación.',
  helpRuleDaily:
    '<b>La misma red para todo el mundo, nueva a medianoche.</b> La práctica reparte una red ' +
    'nueva cuando quieras.',
};

// ------------------------------------------------------------ zh-Hans --

// --------------------------------------------- Simplified Chinese (zh-Hans) --

const ZH_HANS_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: '点亮房屋',
    teaches: '点一下干地就能立起一座铁塔。电力会流向范围内的一切。把三栋房屋都点亮。',
  },
  'tuning-rearrange': {
    name: '挪动铁塔，提高分数',
    teaches: '电网通了，但铁塔的位置不好。拆掉一座，换个更好的地方，看着分数往上走。',
  },
  'diagonal-tax-weave': {
    name: '斜线更费线',
    teaches: '斜的电线伸得不比直的远，却要多花 41% 的电线。把路线拉直，省下电线。',
  },
  'triangle-trap-fan': {
    name: '避开三角形',
    teaches:
      '范围内的每一对铁塔之间都会出现电线，哪怕毫无用处也一样。三座铁塔排成三角形就有三条电线。打破三角形，其中一条就会消失。',
  },
  'triangle-trap-delta': {
    name: '一座铁塔顶两座',
    teaches:
      '两座铁塔挨得太近，除了各自的线路，中间还会多出一条电线。改放一座在中间，就能干完那一对的活，还省下多出来的电线。',
  },
  'exact-spacing-footings': {
    name: '相隔两格',
    teaches: '铁塔正好隔两格时，只会和相邻的那一座相连。再近一点就会多出电线。间距要卡准。',
  },
  'junction-neck': {
    name: '一座铁塔，三栋房屋',
    teaches: '一座铁塔只要同时够得着三栋房屋，就能全部供电。找出那个位置，别给每一栋都拉一条支线。',
  },
  'junction-approach': {
    name: '分头走就分头付',
    teaches:
      '每一栋房屋各走一条路，整段都得用自己的电线。拉一条线同时朝三栋过去，让最后一座铁塔给它们供电。',
  },
  'junction-late-split': {
    name: '越晚分叉越好',
    teaches:
      '房屋往两个方向拽的时候，线路就得分叉。先把共用的一条线带到最远，靠近房屋再分开。太早分叉，等于付两条长线的钱。',
  },
  'lake-narrows': {
    name: '选对跨越点',
    teaches:
      '电线只能在水面宽一格的地方跨过去。这片水域有两处变窄。跨在电厂和房屋的连线上，路线最短。',
  },
  'lake-west-arm': {
    name: '绕过水面',
    teaches: '水域在地图边缘之前就到头了。绕过末端比任何跨越都短。',
  },
};

const ZH_HANS_TOPOLOGIES: Record<Topology, string> = {
  spine: '主干线',
  'y-split': 'Y 字分叉',
  backbranch: '回头支线',
  'river-kiss': '吻河线',
  crossbar: '横担',
};

const ZH_HANS_RANKS: Record<TierRank, string> = {
  superconductor: '超导体',
  master: '师傅',
  journeyman: '技工',
  apprentice: '学徒',
};

const ZH_HANS_FB_KINDS: Record<FeedbackKind, string> = {
  bug: '出问题了',
  idea: '一个想法',
  grid: '这个电网',
  other: '其他',
};

const ZH_HANS_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: '每日',
  archive: '往期',
  practice: '练习',
  drill: '训练',
};

const ZH_HANS_RANKS_FULL: Record<TierRank, string> = {
  superconductor: '超导体',
  master: '架线师傅',
  journeyman: '架线技工',
  apprentice: '架线学徒',
};

const ZH_HANS: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: '铁塔要建在干地上。电线可以跨过水面。',
  denyMtn: '这里太陡，建不了铁塔。电线可以越过山顶。',
  denyLimit: '铁塔用完了。先拆掉一座。',
  gridFailed: '电网启动失败。请重新加载。',
  newBest: (mw: string) => `新纪录：${mw} MW。`,

  // ---------- chrome news ----------
  offlineReady: '离线也能玩。',
  resultCopied: '成绩已复制。',
  copyBlocked: '复制被拦下了。截图也行。',

  // ---------- the result card ----------
  perfectGrid: '完美电网!',
  lineBeaten: '超过直觉路线',
  noteBeatEngineers: '你赢过了工程师。',
  noteGridDark: '电网还黑着。重新接起来才能接着调。',
  takeN: (n: number) => `第 ${n} 次`,
  noteGapTo: (gap: string, rank: string) => `距离${rank}还差 ${gap} MW。接着调。`,
  noteFlawless: '无瑕。全程没拆过一座铁塔。',
  noteLessWire: (pct: number) => `用的电线比直觉路线少 ${pct}%。`,
  noteLessWireTuning: (pct: number) => `用的电线比直觉路线少 ${pct}%。接着调。`,
  legendReveal: '一天过完，工程师自己的电网就开放研究。',
  chipBest: '最好',
  chipRank: '等级',
  chipMoves: '步数',
  chipNaive: '直觉路线',
  firstTake: '首读',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `下一座电网还有<b>${wait}</b>。`,
  newGridReady: '新的电网准备好了。',
  waitHours: '小时',
  waitMinutes: '分',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `每日电网 <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>练习</strong> · 怎么接都行',
  daytagDrill: '<strong>训练</strong>',

  // ---------- drills ----------
  drills: ZH_HANS_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}：</strong>${teaches}${goal}`,
  drillGoal: (mw: string) => ` 直觉路线读数 <b>${mw}</b> MW。超过它。`,
  nextDrill: '下一个训练',
  todaysGrid: '今天的电网',

  // ---------- the study reveal ----------
  revealShow: '显示工程师的电网',
  revealHide: '隐藏工程师的电网',

  // ---------- archive, drills list, records ----------
  rowPlay: '开始',
  rowStudied: '已研读',
  recordsEmpty: '还没有任何记录。让一座电网通电就会开始记。',
  recStreak: '连续',
  recBestRun: '最长连续',
  recGrids: '电网',
  recPerfect: '完美',
  recFlawless: '无瑕',
  recBeatEngineers: '赢过工程师',
  recMedian: '中位数',
  recTunedPast: '调过了首读',
  recStudied: '已研读',
  recDrills: '训练',

  // ---------- the field (GDD §4.7) ----------
  fieldName: '全场',
  fieldPlayed: (players: string) => `<b>${players}</b> 人参与`,
  fieldStanding: (pct: number, players: string) =>
    `<b>${players}</b> 位玩家里，你胜过了 <b>${pct}%</b>`,
  fieldYou: '你',
  fieldAverage: '平均',
  fieldYoung: '名次还在汇总。明天定案。',
  fieldRanks: '等级',
  yesterdayStanding: (pct: number, players: string) =>
    `昨天：${players} 位玩家里，你胜过了 ${pct}%。`,
  yesterdayPlayed: (players: string, avg: string) => `昨天：${players} 人参与${avg}。`,
  yesterdayAvg: (mw: number) => ` · 平均 ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: ZH_HANS_TOPOLOGIES,
  ranks: ZH_HANS_RANKS,
  ranksFull: ZH_HANS_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: '练习',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · 首读`,
  shareBeatRef: '超过了已知最好的电网!',
  shareFlawless: '✨ 无瑕',
  shareStudied: '📖 已研读',
  shareStreak: (days: number) => `🔥 连续 ${days} 天`,
  shareChain: (n: number) => `🔗 连续 ${n} 张电网`,

  // ---------- share actions ----------
  copyResult: '复制成绩',
  shareResult: '分享成绩',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `无瑕 · 今天 ${oneIn} 人中有 1 人`,
  claimChain: (n: number) => `连续 ${n} 张电网`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · 每日输电线解谜',

  // ---------- the language picker ----------
  settingLanguage: '语言',
  langTitle: '语言',
  langLabel: '语言',

  // ---------- the controls row ----------
  helpBtn: '玩法',
  practiceBtn: '练习',
  newMapBtn: '新地图',
  archiveBtn: '往期',
  yardBtn: '训练',
  todayBtn: '今天',
  navProfile: '个人资料',
  profileTitle: '个人资料',
  navLabel: '分区',
  yesterdayBtn: '昨天的电网',
  nextGridBtn: '再来一张电网',
  moveOnTitle: '这张还有空间',
  moveOnIntro: (gap: string, rank: string) => `这张电网距离${rank}还差 ${gap} MW。`,
  moveOnCancel: '接着调',
  moveOnConfirm: '下一张电网',

  // ---------- the board, as keyboard chrome ----------
  boardLabel: 'Wirespan 谜题盘面。方向键移动测量光标；Enter 或空格键放置或拆除铁塔。',

  // ---------- the operator readout ----------
  hudTowers: '铁塔',
  hudHouses: '房屋',
  hudOutput: '输出',
  hudStreak: '连续',

  // ---------- archive ----------
  archiveLabel: '往期',
  archiveTitle: '往期',
  close: '关闭',

  // ---------- drills overlay ----------
  yardLabel: '训练',
  yardTitle: '训练',
  yardIntro: '帮你把手艺练得更好的课题。',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: '研读参考解',
  studyTitle: '研读工程师的电网',
  studyIntro:
    '这座电网还不完美，研读之后，这一天在你的往期和分享里都会标上<b>📖 已研读</b>。' +
    '研读过的完美依然是完美，只是会注明。',
  studyCancel: '还不要',
  studyConfirm: '研读',

  // ---------- settings ----------
  settingsLabel: '设置',
  settingsTitle: '设置',
  setSound: '音效',
  setHaptics: '振动',
  setMotion: '减少动效',
  setContrast: '高对比度',
  setText: '放大文字',
  setUsage: '使用数据',
  privacyBtn: '隐私政策',
  settingsDone: '完成',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: '发送反馈',
  feedbackLabel: '发送反馈',
  feedbackTitle: '发送反馈',
  feedbackIntro: '一条消息，发给做这个游戏的人。不会去别的地方。',
  feedbackKinds: ZH_HANS_FB_KINDS,
  feedbackPlaceholder: '说说发生了什么。',
  feedbackMessageLabel: '你的消息',
  feedbackLeft: (n: number) => `还剩 ${n}`,
  feedbackContact: '邮箱，想要回复的话',
  feedbackContactHint: 'you@example.com',
  feedbackModes: ZH_HANS_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `一起发出：电网 #${grid}・${mode}・版本 ${build}。别的都不发。`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `一起发出：${mode}・版本 ${build}。别的都不发。`,
  feedbackSend: '发送',
  feedbackSending: '发送中…',
  feedbackDone: '已发出。谢谢。',
  feedbackFailOffline: '没发出去，你的消息还在。过一会儿再试。',
  feedbackFailThrottled: '今天五条到头了。信箱明天再开。',
  feedbackFailInvalid: '没通过。如果填了地址，检查一下。',

  // ---------- how to play ----------
  helpLabel: '玩法',
  helpTitle: 'Wirespan 玩法',
  helpIntro: '用最少的电线，把电从电厂送到<b>每一栋房屋</b>。',
  helpRuleBuild: '<b>点一下就能建起或拆掉铁塔。</b>电线会连上 2 格内的一切。',
  helpRuleGround: '<b>铁塔只能建在空地上。</b>电线可以跨过河流和山脊。',
  helpRuleShorter: '<b>电线越短，输出的 MW 越多。</b>全部点亮之后接着重排，把成绩推到最好。',
  helpRuleDaily: '<b>全世界同一座电网，午夜换新。</b>练习随时发一座新电网。',
};

// ------------------------------------------------------------ vi-VN --

// ---------------------------------------------------- Vietnamese (vi-VN) --

const VI_VN_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'Thắp sáng các ngôi nhà',
    teaches:
      'Chạm vào đất khô để dựng một cột điện. Điện chạy tới mọi thứ trong tầm với. Thắp sáng cả ba ngôi nhà.',
  },
  'tuning-rearrange': {
    name: 'Dời cột, nâng điểm',
    teaches:
      'Lưới đã chạy, nhưng các cột đứng ở chỗ dở. Hạ một cột xuống, đặt vào chỗ tốt hơn, và nhìn điểm số đi lên.',
  },
  'diagonal-tax-weave': {
    name: 'Đường chéo tốn hơn',
    teaches:
      'Dây chéo vươn không xa hơn dây thẳng, nhưng tốn thêm 41% dây. Nắn thẳng lộ trình để tiết kiệm dây.',
  },
  'triangle-trap-fan': {
    name: 'Tránh hình tam giác',
    teaches:
      'Dây xuất hiện giữa mọi cặp cột trong tầm với, kể cả khi nó chẳng giúp gì. Ba cột xếp thành tam giác thì có ba sợi dây. Phá vỡ tam giác và một sợi biến mất.',
  },
  'triangle-trap-delta': {
    name: 'Một cột thay được hai',
    teaches:
      'Hai cột đứng sát nhau có thêm một sợi dây nối giữa chúng, ngoài đường dây riêng của mỗi cột. Một cột đặt ở giữa làm được việc của cả cặp mà không tốn sợi dây thừa.',
  },
  'exact-spacing-footings': {
    name: 'Cách nhau đúng hai ô',
    teaches:
      'Các cột cách nhau đúng hai ô chỉ nối với cột liền kề. Gần hơn một chút là dây thừa xuất hiện. Giữ khoảng cách cho đúng.',
  },
  'junction-neck': {
    name: 'Một cột, ba ngôi nhà',
    teaches:
      'Một cột duy nhất trong tầm với của cả ba ngôi nhà có thể cấp điện cho tất cả. Tìm ra chỗ đó thay vì dựng một nhánh tới từng nhà.',
  },
  'junction-approach': {
    name: 'Đi riêng thì trả riêng',
    teaches:
      'Một lộ trình riêng tới từng nhà tốn dây riêng suốt chặng. Chạy một đường chung hướng về cả ba, rồi để cột cuối cấp điện cho chúng.',
  },
  'junction-late-split': {
    name: 'Tách càng muộn càng tốt',
    teaches:
      'Khi các ngôi nhà kéo về hai hướng, đường dây buộc phải tách. Mang một đường chung đi xa hết mức, rồi tách sát các ngôi nhà. Tách sớm là trả tiền cho hai đường dài.',
  },
  'lake-narrows': {
    name: 'Chọn đúng chỗ vượt',
    teaches:
      'Dây chỉ vượt được nước ở chỗ rộng một ô. Vùng nước này hẹp lại ở hai chỗ. Vượt thẳng hàng với nhà máy và các ngôi nhà là lộ trình ngắn nhất.',
  },
  'lake-west-arm': {
    name: 'Đi vòng qua nước',
    teaches: 'Vùng nước kết thúc trước mép bản đồ. Đi vòng qua đầu mút ngắn hơn mọi lối vượt.',
  },
};

const VI_VN_TOPOLOGIES: Record<Topology, string> = {
  spine: 'xương sống',
  'y-split': 'nhánh chữ Y',
  backbranch: 'nhánh quặt',
  'river-kiss': 'nụ hôn sông',
  crossbar: 'thanh ngang',
};

const VI_VN_RANKS: Record<TierRank, string> = {
  superconductor: 'Siêu dẫn',
  master: 'Thợ cả',
  journeyman: 'Thợ chính',
  apprentice: 'Thợ học việc',
};

const VI_VN_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'Có gì đó hỏng',
  idea: 'Một ý tưởng',
  grid: 'Lưới này',
  other: 'Chuyện khác',
};

const VI_VN_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'hằng ngày',
  archive: 'lưu trữ',
  practice: 'luyện tập',
  drill: 'bài tập',
};

const VI_VN_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'Siêu dẫn',
  master: 'Thợ cả đường dây',
  journeyman: 'Thợ chính đường dây',
  apprentice: 'Thợ học việc đường dây',
};

const VI_VN: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'Cột điện cần đất khô. Dây thì bắc qua nước được.',
  denyMtn: 'Quá dốc để dựng cột. Dây thì vượt qua đỉnh được.',
  denyLimit: 'Hết cột rồi. Hạ một cột xuống trước đã.',
  gridFailed: 'Lưới điện không khởi động được. Thử tải lại trang.',
  newBest: (mw: string) => `Kỷ lục mới: ${mw} MW.`,

  // ---------- chrome news ----------
  offlineReady: 'Sẵn sàng chơi ngoại tuyến.',
  resultCopied: 'Đã chép kết quả.',
  copyBlocked: 'Việc chép bị chặn. Chụp màn hình cũng được.',

  // ---------- the result card ----------
  perfectGrid: 'LƯỚI HOÀN HẢO!',
  lineBeaten: 'ĐÃ VƯỢT TUYẾN',
  noteBeatEngineers: 'Bạn đã vượt các kỹ sư.',
  noteGridDark: 'Lưới đang tối. Dựng lại để chỉnh tiếp.',
  takeN: (n: number) => `LẦN ${n}`,
  noteGapTo: (gap: string, rank: string) => `Còn ${gap} MW đến ${rank}. Chỉnh tiếp đi.`,
  noteFlawless: 'Không tì vết. Chưa từng hạ một cột nào xuống.',
  noteLessWire: (pct: number) => `Ít hơn tuyến đơn giản ${pct}% dây.`,
  noteLessWireTuning: (pct: number) => `Ít hơn tuyến đơn giản ${pct}% dây. Chỉnh tiếp đi.`,
  legendReveal: 'Hết ngày, lưới của chính các kỹ sư mở ra để nghiên cứu.',
  chipBest: 'tốt nhất',
  chipRank: 'bậc',
  chipMoves: 'nước đi',
  chipNaive: 'tuyến đơn giản',
  firstTake: 'lượt đầu',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `Lưới kế tiếp sau <b>${wait}</b>.`,
  newGridReady: 'Lưới mới đã sẵn sàng.',
  waitHours: 'h',
  waitMinutes: 'p',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `Lưới hằng ngày <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>Luyện tập</strong> · thoải mái',
  daytagDrill: '<strong>Bài tập</strong>',

  // ---------- drills ----------
  drills: VI_VN_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` Tuyến đơn giản đọc được <b>${mw}</b> MW. Hãy vượt qua.`,
  nextDrill: 'Bài kế tiếp',
  todaysGrid: 'Lưới hôm nay',

  // ---------- the study reveal ----------
  revealShow: 'Hiện lưới của các kỹ sư',
  revealHide: 'Ẩn lưới của các kỹ sư',

  // ---------- archive, drills list, records ----------
  rowPlay: 'chơi',
  rowStudied: 'đã nghiên cứu',
  recordsEmpty: 'Chưa ghi được gì. Cấp điện cho một lưới để mở sổ.',
  recStreak: 'chuỗi',
  recBestRun: 'chuỗi dài nhất',
  recGrids: 'lưới',
  recPerfect: 'hoàn hảo',
  recFlawless: 'không tì vết',
  recBeatEngineers: 'vượt các kỹ sư',
  recMedian: 'trung vị',
  recTunedPast: 'chỉnh vượt lượt đầu',
  recStudied: 'đã nghiên cứu',
  recDrills: 'bài tập',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'toàn giải',
  fieldPlayed: (players: string) => `<b>${players}</b> đã chơi`,
  fieldStanding: (pct: number, players: string) =>
    `bạn vượt <b>${pct}%</b> trong <b>${players}</b> người chơi`,
  fieldYou: 'bạn',
  fieldAverage: 'trung bình',
  fieldYoung: 'Thứ hạng vẫn đang về. Chốt vào ngày mai.',
  fieldRanks: 'bậc',
  yesterdayStanding: (pct: number, players: string) =>
    `Hôm qua: bạn vượt ${pct}% trong ${players} người chơi.`,
  yesterdayPlayed: (players: string, avg: string) => `Hôm qua: ${players} người đã chơi${avg}.`,
  yesterdayAvg: (mw: number) => ` · trung bình ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: VI_VN_TOPOLOGIES,
  ranks: VI_VN_RANKS,
  ranksFull: VI_VN_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'luyện tập',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · lượt đầu`,
  shareBeatRef: 'đã vượt lưới tốt nhất từng biết!',
  shareFlawless: '✨ không tì vết',
  shareStudied: '📖 đã nghiên cứu',
  shareStreak: (days: number) => `🔥 chuỗi ${days} ngày`,
  shareChain: (n: number) => `🔗 ${n} lưới liên tiếp`,

  // ---------- share actions ----------
  copyResult: 'Chép kết quả',
  shareResult: 'Chia sẻ kết quả',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `Không tì vết · 1 trên ${oneIn} hôm nay`,
  claimChain: (n: number) => `${n} lưới liên tiếp`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · câu đố đường dây điện mỗi ngày',

  // ---------- the language picker ----------
  settingLanguage: 'Ngôn ngữ',
  langTitle: 'NGÔN NGỮ',
  langLabel: 'Ngôn ngữ',

  // ---------- the controls row ----------
  helpBtn: 'Cách chơi',
  practiceBtn: 'Luyện tập',
  newMapBtn: 'Bản đồ mới',
  archiveBtn: 'Lưu trữ',
  yardBtn: 'Bài tập',
  todayBtn: 'Hôm nay',
  navProfile: 'Hồ sơ',
  profileTitle: 'HỒ SƠ',
  navLabel: 'Mục',
  yesterdayBtn: 'Lưới hôm qua',
  nextGridBtn: 'Thêm một lưới',
  moveOnTitle: 'LƯỚI NÀY VẪN CÒN CHỖ',
  moveOnIntro: (gap: string, rank: string) => `Còn ${gap} MW nữa là đến ${rank} trên lưới này.`,
  moveOnCancel: 'Chỉnh tiếp',
  moveOnConfirm: 'Lưới tiếp theo',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Bảng câu đố Wirespan. Phím mũi tên di chuyển con trỏ khảo sát; Enter hoặc Space đặt hoặc gỡ một cột điện.',

  // ---------- the operator readout ----------
  hudTowers: 'Cột',
  hudHouses: 'Nhà',
  hudOutput: 'Công suất',
  hudStreak: 'Chuỗi',

  // ---------- archive ----------
  archiveLabel: 'Lưu trữ',
  archiveTitle: 'LƯU TRỮ',
  close: 'Đóng',

  // ---------- drills overlay ----------
  yardLabel: 'Bài tập',
  yardTitle: 'BÀI TẬP',
  yardIntro: 'Những bài học để bạn chơi hay hơn.',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'Nghiên cứu bài mẫu',
  studyTitle: 'NGHIÊN CỨU LƯỚI CỦA CÁC KỸ SƯ',
  studyIntro:
    'Lưới này chưa hoàn hảo, nên nghiên cứu nó sẽ đánh dấu ngày hôm nay là ' +
    '<b>📖 đã nghiên cứu</b> trong kho lưu trữ và trong phần chia sẻ của bạn. Một lưới ' +
    'hoàn hảo có nghiên cứu vẫn là hoàn hảo. Chỉ là nó nói ra điều đó.',
  studyCancel: 'Chưa đâu',
  studyConfirm: 'Nghiên cứu',

  // ---------- settings ----------
  settingsLabel: 'Cài đặt',
  settingsTitle: 'CÀI ĐẶT',
  setSound: 'Âm thanh',
  setHaptics: 'Rung',
  setMotion: 'Giảm chuyển động',
  setContrast: 'Tương phản cao',
  setText: 'Chữ lớn hơn',
  setUsage: 'Dữ liệu sử dụng',
  privacyBtn: 'Chính sách riêng tư',
  settingsDone: 'Xong',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'Gửi góp ý',
  feedbackLabel: 'Gửi góp ý',
  feedbackTitle: 'GỬI GÓP Ý',
  feedbackIntro: 'Một tin nhắn tới người đã làm ra trò chơi này. Nó không đi đâu khác.',
  feedbackKinds: VI_VN_FB_KINDS,
  feedbackPlaceholder: 'Kể xem chuyện gì đã xảy ra.',
  feedbackMessageLabel: 'Tin nhắn của bạn',
  feedbackLeft: (n: number) => `còn ${n}`,
  feedbackContact: 'Email, nếu bạn muốn có hồi âm',
  feedbackContactHint: 'you@example.com',
  feedbackModes: VI_VN_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `Gửi kèm: lưới #${grid} · ${mode} · bản dựng ${build}. Không gì khác.`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `Gửi kèm: ${mode} · bản dựng ${build}. Không gì khác.`,
  feedbackSend: 'Gửi',
  feedbackSending: 'Đang gửi…',
  feedbackDone: 'Đã gửi. Cảm ơn bạn.',
  feedbackFailOffline: 'Tin chưa gửi được, và nội dung của bạn vẫn còn đây. Thử lại sau một lát.',
  feedbackFailThrottled: 'Vậy là năm tin trong hôm nay. Hộp thư mở lại vào ngày mai.',
  feedbackFailInvalid: 'Tin không qua được. Kiểm tra lại địa chỉ, nếu bạn có nhập.',

  // ---------- how to play ----------
  helpLabel: 'Cách chơi',
  helpTitle: 'CÁCH CHƠI WIRESPAN',
  helpIntro: 'Đưa điện từ nhà máy tới <b>mọi ngôi nhà</b> bằng ít dây nhất có thể.',
  helpRuleBuild: '<b>Chạm để dựng hoặc gỡ một cột điện.</b> Dây nối mọi thứ trong vòng 2 ô.',
  helpRuleGround:
    '<b>Cột điện phải đứng trên đất trống.</b> Dây thì băng qua sông và dãy núi được.',
  helpRuleShorter:
    '<b>Dây ngắn hơn thì tải được nhiều MW hơn.</b> Cứ sắp xếp lại sau khi mọi thứ đã ' +
    'sáng để có kết quả tốt nhất.',
  helpRuleDaily:
    '<b>Cùng một lưới cho cả thế giới, mới lại lúc nửa đêm.</b> Chế độ luyện tập chia ' +
    'bài mới bất cứ lúc nào.',
};

// ------------------------------------------------------------ hi-IN --

// -------------------------------------------------------- Hindi (hi-IN) --

const HI_IN_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'घरों को रोशन कीजिए',
    teaches:
      'सूखी ज़मीन छूइए और एक टावर खड़ा हो जाएगा। बिजली पहुँच के भीतर हर चीज़ तक बहती है। तीनों घर रोशन कीजिए।',
  },
  'tuning-rearrange': {
    name: 'टावर हटाइए, स्कोर बढ़ाइए',
    teaches:
      'ग्रिड चल तो रहा है, पर टावर ख़राब जगहों पर खड़े हैं। एक गिराइए, बेहतर जगह लगाइए, और स्कोर चढ़ता देखिए।',
  },
  'diagonal-tax-weave': {
    name: 'तिरछी लाइन महँगी पड़ती है',
    teaches:
      'तिरछा तार सीधे तार से ज़्यादा दूर नहीं पहुँचता, पर 41% ज़्यादा महँगा पड़ता है। रास्ता सीधा कीजिए और तार बचाइए।',
  },
  'triangle-trap-fan': {
    name: 'तिकोन से बचिए',
    teaches:
      'पहुँच के भीतर हर जोड़ी टावरों के बीच तार आ जाता है, चाहे उससे कोई फ़ायदा न हो। तिकोन में खड़े तीन टावरों को तीन तार मिलते हैं। तिकोन तोड़िए और उनमें से एक तार ग़ायब हो जाएगा।',
  },
  'triangle-trap-delta': {
    name: 'एक टावर दो की जगह ले लेता है',
    teaches:
      'पास-पास खड़े दो टावरों को अपनी-अपनी लाइनों के ऊपर से एक तार और मिल जाता है। बीच में खड़ा एक टावर उस जोड़ी का काम बिना फ़ालतू तार के कर देता है।',
  },
  'exact-spacing-footings': {
    name: 'ठीक दो खाने की दूरी',
    teaches:
      'ठीक दो खाने की दूरी पर खड़े टावर सिर्फ़ अपने पड़ोसी से जुड़ते हैं। इससे ज़रा भी पास हुए तो फ़ालतू तार आ जाते हैं। दूरी ठीक रखिए।',
  },
  'junction-neck': {
    name: 'एक टावर, तीन घर',
    teaches:
      'तीनों घरों की पहुँच में खड़ा अकेला टावर तीनों को बिजली दे सकता है। हर घर तक अलग शाखा बनाने के बजाय वह जगह ढूँढिए।',
  },
  'junction-approach': {
    name: 'अलग रास्ते अलग दाम लेते हैं',
    teaches:
      'हर घर तक अलग रास्ता पूरे सफ़र में अपना तार लगाता है। एक ही लाइन तीनों की ओर ले जाइए, और आख़िरी टावर को उन्हें बिजली देने दीजिए।',
  },
  'junction-late-split': {
    name: 'जितनी देर से बँटे, उतना अच्छा',
    teaches:
      'जब घर दो दिशाओं में खींचते हैं, लाइन को बँटना ही पड़ता है। एक साझी लाइन जहाँ तक जा सके ले जाइए, फिर घरों के पास जाकर बाँटिए। जल्दी बँटवारा दो लंबी लाइनों का दाम चुकाता है।',
  },
  'lake-narrows': {
    name: 'सही घाट चुनिए',
    teaches:
      'तार पानी को वहीं पार कर सकते हैं जहाँ वह एक खाना चौड़ा हो। यह पानी दो जगह सिकुड़ता है। बिजलीघर और घरों की सीध में पार करना सबसे छोटा रास्ता है।',
  },
  'lake-west-arm': {
    name: 'पानी के चारों ओर से जाइए',
    teaches:
      'पानी नक्शे के किनारे से पहले ही ख़त्म हो जाता है। सिरे के चक्कर से जाना किसी भी पार से छोटा है।',
  },
};

const HI_IN_TOPOLOGIES: Record<Topology, string> = {
  spine: 'रीढ़',
  'y-split': 'वाई बँटवारा',
  backbranch: 'पिछली शाखा',
  'river-kiss': 'नदी का चुंबन',
  crossbar: 'आड़ी छड़',
};

const HI_IN_RANKS: Record<TierRank, string> = {
  superconductor: 'सुपरकंडक्टर',
  master: 'उस्ताद',
  journeyman: 'कारीगर',
  apprentice: 'शागिर्द',
};

const HI_IN_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'कुछ टूट गया',
  idea: 'एक सुझाव',
  grid: 'यह ग्रिड',
  other: 'कुछ और',
};

const HI_IN_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'रोज़ाना',
  archive: 'संग्रह',
  practice: 'अभ्यास',
  drill: 'तालीम',
};

const HI_IN_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'सुपरकंडक्टर',
  master: 'उस्ताद लाइनमैन',
  journeyman: 'कारीगर लाइनमैन',
  apprentice: 'शागिर्द लाइनमैन',
};

const HI_IN: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'टावर के लिए सूखी ज़मीन चाहिए। तार पानी के ऊपर से जा सकते हैं।',
  denyMtn: 'टावर के लिए यह ढलान बहुत तीखी है। तार चोटी के ऊपर से निकल सकते हैं।',
  denyLimit: 'टावर ख़त्म। पहले एक गिराइए।',
  gridFailed: 'ग्रिड चालू नहीं हो सका। पेज दोबारा लोड करके देखिए।',
  newBest: (mw: string) => `नया सर्वश्रेष्ठ: ${mw} MW।`,

  // ---------- chrome news ----------
  offlineReady: 'बिना इंटरनेट भी खेलने को तैयार।',
  resultCopied: 'नतीजा कॉपी हो गया।',
  copyBlocked: 'कॉपी रुक गई। स्क्रीनशॉट से भी काम चलेगा।',

  // ---------- the result card ----------
  perfectGrid: 'बेमिसाल ग्रिड!',
  lineBeaten: 'सीधी लाइन हारी',
  noteBeatEngineers: 'आपने इंजीनियरों को पीछे छोड़ दिया।',
  noteGridDark: 'ग्रिड अँधेरे में है। दोबारा जोड़िए तो कसना जारी रहेगा।',
  takeN: (n: number) => `प्रयास ${n}`,
  noteGapTo: (gap: string, rank: string) => `${rank} तक ${gap} MW। कसते रहिए।`,
  noteFlawless: 'बेदाग़। एक भी टावर कभी नहीं गिराया।',
  noteLessWire: (pct: number) => `सीधी लाइन से ${pct}% कम तार।`,
  noteLessWireTuning: (pct: number) => `सीधी लाइन से ${pct}% कम तार। कसते रहिए।`,
  legendReveal: 'दिन ख़त्म होते ही इंजीनियरों का अपना ग्रिड पढ़ने के लिए खुल जाता है।',
  chipBest: 'सर्वश्रेष्ठ',
  chipRank: 'दर्जा',
  chipMoves: 'चालें',
  chipNaive: 'सीधी लाइन',
  firstTake: 'पहली बार',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `अगला ग्रिड <b>${wait}</b> में।`,
  newGridReady: 'नया ग्रिड तैयार है।',
  waitHours: 'घं',
  waitMinutes: 'मि',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `रोज़ का ग्रिड <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>अभ्यास</strong> · जो मन आए',
  daytagDrill: '<strong>तालीम</strong>',

  // ---------- drills ----------
  drills: HI_IN_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` सीधी लाइन <b>${mw}</b> MW पढ़ती है। इसे मात दीजिए।`,
  nextDrill: 'अगली तालीम',
  todaysGrid: 'आज का ग्रिड',

  // ---------- the study reveal ----------
  revealShow: 'इंजीनियरों का ग्रिड दिखाइए',
  revealHide: 'इंजीनियरों का ग्रिड छिपाइए',

  // ---------- archive, drills list, records ----------
  rowPlay: 'खेलिए',
  rowStudied: 'पढ़ा',
  recordsEmpty: 'अभी कुछ दर्ज नहीं। एक ग्रिड में बिजली दौड़ाइए, रिकॉर्ड खुल जाएगा।',
  recStreak: 'लगातार',
  recBestRun: 'सबसे लंबा दौर',
  recGrids: 'ग्रिड',
  recPerfect: 'बेमिसाल',
  recFlawless: 'बेदाग़',
  recBeatEngineers: 'इंजीनियरों से आगे',
  recMedian: 'मध्यक',
  recTunedPast: 'पहली बार से बेहतर',
  recStudied: 'पढ़ा',
  recDrills: 'तालीम',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'मैदान',
  fieldPlayed: (players: string) => `<b>${players}</b> खेले`,
  fieldStanding: (pct: number, players: string) =>
    `<b>${players}</b> खिलाड़ियों में से <b>${pct}%</b> से आप आगे हैं`,
  fieldYou: 'आप',
  fieldAverage: 'औसत',
  fieldYoung: 'नतीजे अभी आ रहे हैं। कल पक्के होंगे।',
  fieldRanks: 'दर्जे',
  yesterdayStanding: (pct: number, players: string) =>
    `कल: ${players} खिलाड़ियों में से ${pct}% से आप आगे रहे।`,
  yesterdayPlayed: (players: string, avg: string) => `कल: ${players} लोग खेले${avg}।`,
  yesterdayAvg: (mw: number) => ` · औसत ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: HI_IN_TOPOLOGIES,
  ranks: HI_IN_RANKS,
  ranksFull: HI_IN_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'अभ्यास',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · पहली बार`,
  shareBeatRef: 'अब तक के सबसे बेहतर ग्रिड को मात दी!',
  shareFlawless: '✨ बेदाग़',
  shareStudied: '📖 पढ़ा',
  shareStreak: (days: number) => `🔥 ${days} दिन लगातार`,
  shareChain: (n: number) => `🔗 लगातार ${n} ग्रिड`,

  // ---------- share actions ----------
  copyResult: 'नतीजा कॉपी करें',
  shareResult: 'नतीजा साझा करें',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `बेदाग़ · आज ${oneIn} में से 1`,
  claimChain: (n: number) => `लगातार ${n} ग्रिड`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · रोज़ की बिजली-लाइन पहेली',

  // ---------- the language picker ----------
  settingLanguage: 'भाषा',
  langTitle: 'भाषा',
  langLabel: 'भाषा',

  // ---------- the controls row ----------
  helpBtn: 'कैसे खेलें',
  practiceBtn: 'अभ्यास',
  newMapBtn: 'नया नक्शा',
  archiveBtn: 'संग्रह',
  yardBtn: 'तालीम',
  todayBtn: 'आज',
  navProfile: 'प्रोफ़ाइल',
  profileTitle: 'प्रोफ़ाइल',
  navLabel: 'हिस्से',
  yesterdayBtn: 'कल का ग्रिड',
  nextGridBtn: 'एक और ग्रिड',
  moveOnTitle: 'यहाँ अभी गुंजाइश है',
  moveOnIntro: (gap: string, rank: string) => `इस ग्रिड पर ${rank} तक ${gap} MW बाकी हैं।`,
  moveOnCancel: 'कसते रहिए',
  moveOnConfirm: 'अगली ग्रिड',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'Wirespan पहेली का बोर्ड। तीर बटन सर्वे कर्सर हिलाते हैं; Enter या Space टावर खड़ा करता या हटाता है।',

  // ---------- the operator readout ----------
  hudTowers: 'टावर',
  hudHouses: 'घर',
  hudOutput: 'आउटपुट',
  hudStreak: 'लगातार',

  // ---------- archive ----------
  archiveLabel: 'संग्रह',
  archiveTitle: 'संग्रह',
  close: 'बंद करें',

  // ---------- drills overlay ----------
  yardLabel: 'तालीम',
  yardTitle: 'तालीम',
  yardIntro: 'खेल सुधारने के लिए सबक़।',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'नमूना पढ़िए',
  studyTitle: 'इंजीनियरों का ग्रिड पढ़िए',
  studyIntro:
    'यह ग्रिड अभी बेमिसाल नहीं है, इसलिए इसे पढ़ने पर यह दिन आपके संग्रह में और उसकी साझेदारी में ' +
    '<b>📖 पढ़ा</b> लिखा जाएगा। पढ़ा हुआ बेमिसाल फिर भी बेमिसाल है। बस वह यह बात कह देता है।',
  studyCancel: 'अभी नहीं',
  studyConfirm: 'पढ़िए',

  // ---------- settings ----------
  settingsLabel: 'सेटिंग',
  settingsTitle: 'सेटिंग',
  setSound: 'आवाज़',
  setHaptics: 'कंपन',
  setMotion: 'कम हलचल',
  setContrast: 'ऊँचा कंट्रास्ट',
  setText: 'बड़े अक्षर',
  setUsage: 'इस्तेमाल का डेटा',
  privacyBtn: 'निजता नीति',
  settingsDone: 'हो गया',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'राय भेजें',
  feedbackLabel: 'राय भेजें',
  feedbackTitle: 'राय भेजें',
  feedbackIntro: 'एक संदेश, सीधे उस इंसान तक जिसने यह बनाया। और कहीं नहीं जाता।',
  feedbackKinds: HI_IN_FB_KINDS,
  feedbackPlaceholder: 'बताइए क्या हुआ।',
  feedbackMessageLabel: 'आपका संदेश',
  feedbackLeft: (n: number) => `${n} बचे`,
  feedbackContact: 'ईमेल, अगर जवाब चाहिए',
  feedbackContactHint: 'you@example.com',
  feedbackModes: HI_IN_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `साथ भेजा: ग्रिड #${grid} · ${mode} · बिल्ड ${build}। और कुछ नहीं।`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `साथ भेजा: ${mode} · बिल्ड ${build}। और कुछ नहीं।`,
  feedbackSend: 'भेजें',
  feedbackSending: 'भेजा जा रहा है…',
  feedbackDone: 'भेज दिया। शुक्रिया।',
  feedbackFailOffline: 'यह गया नहीं, और आपका संदेश यहीं है। थोड़ी देर बाद फिर देखिए।',
  feedbackFailThrottled: 'आज के पाँच संदेश हो गए। डिब्बा कल फिर खुलेगा।',
  feedbackFailInvalid: 'यह नहीं गया। पता लिखा हो तो उसे जाँच लीजिए।',

  // ---------- how to play ----------
  helpLabel: 'कैसे खेलें',
  helpTitle: 'WIRESPAN कैसे खेलें',
  helpIntro: 'बिजलीघर से <b>हर घर</b> तक कम से कम तार लगाकर बिजली पहुँचाइए।',
  helpRuleBuild:
    '<b>टावर खड़ा करने या हटाने के लिए छुइए।</b> तार 2 खानों के भीतर की हर चीज़ जोड़ देते हैं।',
  helpRuleGround:
    '<b>टावर खुली ज़मीन पर ही बनते हैं।</b> तार नदियों और पहाड़ों के पार जा सकते हैं।',
  helpRuleShorter:
    '<b>छोटे तार ज़्यादा MW पहुँचाते हैं।</b> सब कुछ जल जाने के बाद भी टावर इधर-उधर करते ' +
    'रहिए, तभी सबसे अच्छा स्कोर मिलेगा।',
  helpRuleDaily:
    '<b>पूरी दुनिया के लिए एक ही ग्रिड, आधी रात को नया।</b> अभ्यास में जब चाहे नया ग्रिड ' +
    'मिल जाता है।',
};

// ------------------------------------------------------------ th-TH --

// ---------------------------------------------------------- Thai (th-TH) --

const TH_TH_DRILLS: Record<DrillId, DrillCopy> = {
  'first-light': {
    name: 'จุดไฟให้บ้าน',
    teaches:
      'แตะพื้นแห้งเพื่อตั้งเสาขึ้นมาต้นหนึ่ง ไฟจะไหลไปยังทุกอย่างที่อยู่ในระยะ จุดไฟให้ครบทั้งสามบ้าน',
  },
  'tuning-rearrange': {
    name: 'ย้ายเสา เพิ่มคะแนน',
    teaches:
      'โครงข่ายใช้ได้อยู่ แต่เสาตั้งอยู่ในจุดที่ไม่ดี รื้อลงต้นหนึ่ง วางในที่ที่ดีกว่า แล้วดูคะแนนไต่ขึ้น',
  },
  'diagonal-tax-weave': {
    name: 'แนวทแยงแพงกว่า',
    teaches: 'สายไฟแนวทแยงไปได้ไม่ไกลกว่าสายตรง แต่แพงกว่า 41% ดัดเส้นทางให้ตรงเพื่อประหยัดสายไฟ',
  },
  'triangle-trap-fan': {
    name: 'เลี่ยงรูปสามเหลี่ยม',
    teaches:
      'สายไฟจะโผล่ขึ้นระหว่างเสาทุกคู่ที่อยู่ในระยะ ต่อให้ไม่ช่วยอะไรเลยก็ตาม เสาสามต้นที่เรียงเป็นสามเหลี่ยมจะได้สายไฟสามเส้น ทลายสามเหลี่ยมนั้น แล้วสายหนึ่งเส้นจะหายไป',
  },
  'triangle-trap-delta': {
    name: 'เสาต้นเดียวแทนสองต้นได้',
    teaches:
      'เสาสองต้นที่อยู่ชิดกันจะมีสายไฟพาดระหว่างกันเพิ่มจากสายของตัวเอง เสาต้นเดียวตรงกลางทำงานแทนคู่นั้นได้โดยไม่ต้องเสียสายเพิ่ม',
  },
  'exact-spacing-footings': {
    name: 'ห่างกันสองช่อง',
    teaches:
      'เสาที่ห่างกันพอดีสองช่องจะเชื่อมกับเพื่อนบ้านเท่านั้น ใกล้กว่านั้นนิดเดียวสายไฟส่วนเกินก็โผล่ รักษาระยะให้พอดี',
  },
  'junction-neck': {
    name: 'เสาต้นเดียว สามบ้าน',
    teaches:
      'เสาต้นเดียวที่อยู่ในระยะของทั้งสามบ้านจ่ายไฟให้ครบได้ หาจุดนั้นให้เจอ แทนที่จะสร้างแขนงแยกไปทีละบ้าน',
  },
  'junction-approach': {
    name: 'แยกกันเดินก็จ่ายแยกกัน',
    teaches:
      'เส้นทางแยกไปแต่ละบ้านต้องใช้สายของตัวเองตลอดทาง เดินสายเส้นเดียวมุ่งไปหาทั้งสาม แล้วให้เสาต้นสุดท้ายจ่ายไฟให้พวกมัน',
  },
  'junction-late-split': {
    name: 'ยิ่งแยกช้ายิ่งดี',
    teaches:
      'เมื่อบ้านดึงไปคนละทาง สายก็ต้องแยก ลากสายร่วมเส้นเดียวไปให้ไกลที่สุด แล้วค่อยแยกใกล้ ๆ บ้าน แยกเร็วเกินไปคือจ่ายค่าสายยาวสองเส้น',
  },
  'lake-narrows': {
    name: 'เลือกจุดข้ามให้ถูก',
    teaches:
      'สายไฟข้ามน้ำได้เฉพาะตรงที่กว้างช่องเดียว ผืนน้ำนี้แคบลงอยู่สองจุด ข้ามให้อยู่ในแนวเดียวกับโรงไฟฟ้าและบ้านคือเส้นทางที่สั้นที่สุด',
  },
  'lake-west-arm': {
    name: 'อ้อมผืนน้ำไป',
    teaches: 'ผืนน้ำจบก่อนถึงขอบแผนที่ อ้อมปลายไปสั้นกว่าการข้ามทุกแบบ',
  },
};

const TH_TH_TOPOLOGIES: Record<Topology, string> = {
  spine: 'แนวสันหลัง',
  'y-split': 'แยกตัววาย',
  backbranch: 'แขนงย้อนกลับ',
  'river-kiss': 'จูบแม่น้ำ',
  crossbar: 'คานขวาง',
};

const TH_TH_RANKS: Record<TierRank, string> = {
  superconductor: 'ตัวนำยิ่งยวด',
  master: 'หัวหน้าช่าง',
  journeyman: 'ช่างฝีมือ',
  apprentice: 'ลูกมือ',
};

const TH_TH_FB_KINDS: Record<FeedbackKind, string> = {
  bug: 'มีอะไรพัง',
  idea: 'ไอเดียหนึ่ง',
  grid: 'โครงข่ายนี้',
  other: 'เรื่องอื่น',
};

const TH_TH_FB_MODES: Record<FeedbackContext['mode'], string> = {
  daily: 'ประจำวัน',
  archive: 'คลัง',
  practice: 'ซ้อม',
  drill: 'ฝึกหัด',
};

const TH_TH_RANKS_FULL: Record<TierRank, string> = {
  superconductor: 'ตัวนำยิ่งยวด',
  master: 'หัวหน้าช่างสายไฟ',
  journeyman: 'ช่างสายไฟฝีมือ',
  apprentice: 'ลูกมือช่างสายไฟ',
};

const TH_TH: Strings = {
  // ---------- board nudges (§10: state the physics and the fix) ----------
  denyWater: 'เสาต้องตั้งบนพื้นแห้ง สายไฟข้ามน้ำได้',
  denyMtn: 'ชันเกินกว่าจะตั้งเสา สายไฟข้ามยอดเขาได้',
  denyLimit: 'เสาหมดแล้ว รื้อสักต้นก่อน',
  gridFailed: 'โครงข่ายเริ่มไม่ติด ลองโหลดหน้าใหม่',
  newBest: (mw: string) => `สถิติใหม่ ${mw} MW`,

  // ---------- chrome news ----------
  offlineReady: 'พร้อมเล่นแบบออฟไลน์แล้ว',
  resultCopied: 'คัดลอกผลแล้ว',
  copyBlocked: 'การคัดลอกถูกปิดกั้น จับภาพหน้าจอก็ได้เหมือนกัน',

  // ---------- the result card ----------
  perfectGrid: 'โครงข่ายไร้ที่ติ!',
  lineBeaten: 'ชนะเส้นตรง',
  noteBeatEngineers: 'คุณเอาชนะวิศวกรได้แล้ว',
  noteGridDark: 'โครงข่ายยังมืดอยู่ ต่อใหม่แล้วค่อยปรับต่อ',
  takeN: (n: number) => `ครั้งที่ ${n}`,
  noteGapTo: (gap: string, rank: string) => `อีก ${gap} MW ถึง ${rank} ปรับต่อไป`,
  noteFlawless: 'ไร้ตำหนิ ไม่เคยรื้อเสาสักต้น',
  noteLessWire: (pct: number) => `ใช้สายไฟน้อยกว่าเส้นตรง ${pct}%`,
  noteLessWireTuning: (pct: number) => `ใช้สายไฟน้อยกว่าเส้นตรง ${pct}% ปรับต่อไป`,
  legendReveal: 'พอหมดวัน โครงข่ายของวิศวกรเองจะเปิดให้ศึกษา',
  chipBest: 'ดีสุด',
  chipRank: 'ระดับ',
  chipMoves: 'ตาเดิน',
  chipNaive: 'เส้นตรง',
  firstTake: 'ครั้งแรก',
  unitMW: 'MW',
  darkDash: '–',
  nextGridIn: (wait: string) => `โครงข่ายถัดไปอีก <b>${wait}</b>`,
  newGridReady: 'โครงข่ายใหม่พร้อมแล้ว',
  waitHours: 'ชม.',
  waitMinutes: 'น.',

  // ---------- the date tag ----------
  daytagDaily: (day: number, date: string) =>
    `โครงข่ายประจำวัน <strong>#${day}</strong><span class="d">${date}</span>`,
  daytagPractice: '<strong>ซ้อม</strong> · ตามใจเลย',
  daytagDrill: '<strong>ฝึกหัด</strong>',

  // ---------- drills ----------
  drills: TH_TH_DRILLS,
  drillLesson: (name: string, teaches: string, goal: string) =>
    `<strong>${name}:</strong> ${teaches}${goal}`,
  drillGoal: (mw: string) => ` เส้นตรงอ่านได้ <b>${mw}</b> MW เอาชนะให้ได้`,
  nextDrill: 'บทถัดไป',
  todaysGrid: 'โครงข่ายวันนี้',

  // ---------- the study reveal ----------
  revealShow: 'แสดงโครงข่ายของวิศวกร',
  revealHide: 'ซ่อนโครงข่ายของวิศวกร',

  // ---------- archive, drills list, records ----------
  rowPlay: 'เล่น',
  rowStudied: 'ศึกษาแล้ว',
  recordsEmpty: 'ยังไม่มีบันทึก จ่ายไฟให้โครงข่ายสักอันแล้วบันทึกจะเปิดขึ้น',
  recStreak: 'ต่อเนื่อง',
  recBestRun: 'ต่อเนื่องยาวสุด',
  recGrids: 'โครงข่าย',
  recPerfect: 'ไร้ที่ติ',
  recFlawless: 'ไร้ตำหนิ',
  recBeatEngineers: 'ชนะวิศวกร',
  recMedian: 'มัธยฐาน',
  recTunedPast: 'ปรับเหนือครั้งแรก',
  recStudied: 'ศึกษาแล้ว',
  recDrills: 'ฝึกหัด',

  // ---------- the field (GDD §4.7) ----------
  fieldName: 'ทั้งสนาม',
  fieldPlayed: (players: string) => `มีคนเล่น <b>${players}</b> คน`,
  fieldStanding: (pct: number, players: string) =>
    `คุณชนะ <b>${pct}%</b> จากผู้เล่น <b>${players}</b> คน`,
  fieldYou: 'คุณ',
  fieldAverage: 'เฉลี่ย',
  fieldYoung: 'อันดับยังทยอยเข้ามา สรุปพรุ่งนี้',
  fieldRanks: 'ระดับ',
  yesterdayStanding: (pct: number, players: string) =>
    `เมื่อวาน คุณชนะ ${pct}% จากผู้เล่น ${players} คน`,
  yesterdayPlayed: (players: string, avg: string) => `เมื่อวาน มีคนเล่น ${players} คน${avg}`,
  yesterdayAvg: (mw: number) => ` · เฉลี่ย ${mw} MW`,

  // ---------- the reference's own vocabulary ----------
  topologies: TH_TH_TOPOLOGIES,
  ranks: TH_TH_RANKS,
  ranksFull: TH_TH_RANKS_FULL,

  // ---------- the share text (GDD §6.3; its emoji belong there, §6) ----------
  shareTitle: (tag: string, glyph: string) => `Wirespan ${tag} ${glyph}`,
  sharePractice: 'ซ้อม',
  shareMW: (readings: string, target: string) => `⚡ ${readings}${target} MW`,
  shareMWFirstTake: (mw: string) => `⚡ ${mw} MW · ครั้งแรก`,
  shareBeatRef: 'ชนะโครงข่ายที่ดีที่สุดเท่าที่รู้จักแล้ว!',
  shareFlawless: '✨ ไร้ตำหนิ',
  shareStudied: '📖 ศึกษาแล้ว',
  shareStreak: (days: number) => `🔥 ต่อเนื่อง ${days} วัน`,
  shareChain: (n: number) => `🔗 ${n} กริดติดต่อกัน`,

  // ---------- share actions ----------
  copyResult: 'คัดลอกผล',
  shareResult: 'แชร์ผล',

  // ---------- the claim beside the share button (STYLEGUIDE §7) ----------
  claimFlawless: (oneIn: number) => `ไร้ตำหนิ · 1 ใน ${oneIn} วันนี้`,
  claimChain: (n: number) => `${n} กริดติดต่อกัน`,

  // ---------- the page itself ----------
  pageTitle: 'Wirespan · ปริศนาสายส่งไฟฟ้าประจำวัน',

  // ---------- the language picker ----------
  settingLanguage: 'ภาษา',
  langTitle: 'ภาษา',
  langLabel: 'ภาษา',

  // ---------- the controls row ----------
  helpBtn: 'วิธีเล่น',
  practiceBtn: 'ซ้อม',
  newMapBtn: 'แผนที่ใหม่',
  archiveBtn: 'คลัง',
  yardBtn: 'ฝึกหัด',
  todayBtn: 'วันนี้',
  navProfile: 'โปรไฟล์',
  profileTitle: 'โปรไฟล์',
  navLabel: 'ส่วนต่าง ๆ',
  yesterdayBtn: 'โครงข่ายเมื่อวาน',
  nextGridBtn: 'อีกหนึ่งกริด',
  moveOnTitle: 'กริดนี้ยังมีที่ให้ปรับ',
  moveOnIntro: (gap: string, rank: string) => `เหลืออีก ${gap} MW ถึง ${rank} บนกริดนี้`,
  moveOnCancel: 'ปรับต่อ',
  moveOnConfirm: 'กริดถัดไป',

  // ---------- the board, as keyboard chrome ----------
  boardLabel:
    'กระดานปริศนา Wirespan ปุ่มลูกศรเลื่อนเคอร์เซอร์สำรวจ Enter หรือ Space วางหรือรื้อเสา',

  // ---------- the operator readout ----------
  hudTowers: 'เสา',
  hudHouses: 'บ้าน',
  hudOutput: 'กำลังส่ง',
  hudStreak: 'ต่อเนื่อง',

  // ---------- archive ----------
  archiveLabel: 'คลัง',
  archiveTitle: 'คลัง',
  close: 'ปิด',

  // ---------- drills overlay ----------
  yardLabel: 'ฝึกหัด',
  yardTitle: 'ฝึกหัด',
  yardIntro: 'บทเรียนที่ช่วยให้คุณเล่นได้ดีขึ้น',

  // ---------- the one confirmation in the game (STYLEGUIDE §7) ----------
  studyLabel: 'ศึกษาแบบอ้างอิง',
  studyTitle: 'ศึกษาโครงข่ายของวิศวกร',
  studyIntro:
    'โครงข่ายนี้ยังไม่ไร้ที่ติ การศึกษามันจะทำให้วันนี้ถูกกำกับว่า <b>📖 ศึกษาแล้ว</b> ' +
    'ทั้งในคลังของคุณและในผลที่แชร์ ไร้ที่ติที่ศึกษามาก็ยังไร้ที่ติอยู่ดี เพียงแต่มันบอกไว้เท่านั้น',
  studyCancel: 'ยังก่อน',
  studyConfirm: 'ศึกษา',

  // ---------- settings ----------
  settingsLabel: 'ตั้งค่า',
  settingsTitle: 'ตั้งค่า',
  setSound: 'เสียง',
  setHaptics: 'สั่น',
  setMotion: 'ลดการเคลื่อนไหว',
  setContrast: 'คอนทราสต์สูง',
  setText: 'ตัวอักษรใหญ่ขึ้น',
  setUsage: 'ข้อมูลการใช้งาน',
  privacyBtn: 'นโยบายความเป็นส่วนตัว',
  settingsDone: 'เสร็จแล้ว',

  // ---------- the drop box (GDD §13) ----------
  feedbackBtn: 'ส่งความเห็น',
  feedbackLabel: 'ส่งความเห็น',
  feedbackTitle: 'ส่งความเห็น',
  feedbackIntro: 'ข้อความหนึ่งถึงคนที่สร้างเกมนี้ ไม่ไปที่อื่นเลย',
  feedbackKinds: TH_TH_FB_KINDS,
  feedbackPlaceholder: 'เล่ามาว่าเกิดอะไรขึ้น',
  feedbackMessageLabel: 'ข้อความของคุณ',
  feedbackLeft: (n: number) => `เหลือ ${n}`,
  feedbackContact: 'อีเมล ถ้าอยากได้คำตอบ',
  feedbackContactHint: 'you@example.com',
  feedbackModes: TH_TH_FB_MODES,
  feedbackAttached: (grid: number, mode: string, build: string) =>
    `ส่งไปด้วยกัน โครงข่าย #${grid} · ${mode} · บิลด์ ${build} ไม่มีอย่างอื่น`,
  feedbackAttachedNoGrid: (mode: string, build: string) =>
    `ส่งไปด้วยกัน ${mode} · บิลด์ ${build} ไม่มีอย่างอื่น`,
  feedbackSend: 'ส่ง',
  feedbackSending: 'กำลังส่ง…',
  feedbackDone: 'ส่งแล้ว ขอบคุณ',
  feedbackFailOffline: 'ยังส่งไม่ออก และข้อความของคุณยังอยู่ตรงนี้ อีกสักครู่ลองใหม่',
  feedbackFailThrottled: 'ครบห้าข้อความของวันนี้แล้ว กล่องจะเปิดอีกครั้งพรุ่งนี้',
  feedbackFailInvalid: 'ส่งไม่ผ่าน ถ้ากรอกที่อยู่ไว้ ลองตรวจดูอีกที',

  // ---------- how to play ----------
  helpLabel: 'วิธีเล่น',
  helpTitle: 'วิธีเล่น WIRESPAN',
  helpIntro: 'ส่งไฟจากโรงไฟฟ้าไปยัง<b>ทุกบ้าน</b>โดยใช้สายไฟน้อยที่สุด',
  helpRuleBuild: '<b>แตะเพื่อสร้างหรือรื้อเสา</b> สายไฟเชื่อมทุกอย่างที่อยู่ในระยะ 2 ช่อง',
  helpRuleGround: '<b>เสาต้องตั้งบนพื้นโล่ง</b> สายไฟข้ามแม่น้ำและสันเขาได้',
  helpRuleShorter:
    '<b>สายไฟยิ่งสั้น ยิ่งส่งได้หลาย MW</b> พอทุกอย่างสว่างแล้วก็จัดวางใหม่ต่อไป ' +
    'เพื่อทำคะแนนให้ดีที่สุด',
  helpRuleDaily:
    '<b>โครงข่ายเดียวกันทั้งโลก เปลี่ยนใหม่ตอนเที่ยงคืน</b> โหมดซ้อมแจกโครงข่ายใหม่ได้ทุกเมื่อ',
};

// ------------------------------------------------------------- the lookup --

/** Every language's copy, keyed by locale. Exported for the copy tests. */
export const DICTIONARIES: Record<Locale, Strings> = {
  'id-ID': ID_ID,
  'de-DE': DE_DE,
  'en-US': EN,
  'es-419': ES,
  'es-ES': ES_ES,
  'fr-FR': FR_FR,
  'it-IT': IT_IT,
  'nl-NL': NL_NL,
  'pl-PL': PL_PL,
  'pt-BR': PT_BR,
  'vi-VN': VI_VN,
  'tr-TR': TR_TR,
  'hi-IN': HI_IN,
  'th-TH': TH_TH,
  'ja-JP': JA_JP,
  'zh-Hans': ZH_HANS,
  'zh-Hant': ZH_HANT,
  'ko-KR': KO_KR,
};

/**
 * The dictionary the interface is speaking right now.
 *
 * A call, never a captured value: a table held in a module-level `const` goes
 * stale the moment the player switches language, silently and only for that
 * one string. Calling costs a property read and cannot go wrong.
 */
export function str(): Strings {
  return DICTIONARIES[locale()];
}

/** One static-markup assignment: what lands where, and as what. */
interface StaticEntry {
  sel: string;
  text?: string;
  html?: string;
  attr?: [name: string, value: string];
}

/** The copy index.html carries, in the language currently being spoken. */
function staticEntries(s: Strings): StaticEntry[] {
  return [
    // The browser tab and the app switcher read this, so it is copy like any
    // other. The <title> element is the seam: setting its text sets the tab.
    { sel: 'title', text: s.pageTitle },

    // controls row and card actions
    // the masthead's rules button is icon-only: the words ride its labels
    { sel: '#practiceBtn .setting-text b', text: s.practiceBtn },
    { sel: '#newMapBtn', text: s.newMapBtn },
    // the nav bar: each tab a labelled icon, the bar named for readers
    { sel: '#tabbar', attr: ['aria-label', s.navLabel] },
    { sel: '#navToday .tab-label', text: s.todayBtn },
    { sel: '#navArchive .tab-label', text: s.archiveBtn },
    { sel: '#navDrills .tab-label', text: s.yardBtn },
    { sel: '#navProfile .tab-label', text: s.navProfile },
    { sel: '#shareBtn', text: s.copyResult },
    { sel: '#nextDrillBtn', text: s.nextDrill },
    { sel: '#revealBtn', text: s.revealShow },
    { sel: '#yesterdayBtn', text: s.yesterdayBtn },
    { sel: '#nextGridBtn', text: s.nextGridBtn },
    { sel: '#moveOnTitle', text: s.moveOnTitle },
    { sel: '#moveOnCancel', text: s.moveOnCancel },
    { sel: '#moveOnConfirm', text: s.moveOnConfirm },
    // Not #winTitle: the head is a take counter now, computed per state and
    // rewritten by the panel on every show. A static pass over it would put
    // a fixed word where a moving number belongs (GDD §6.2).

    // the board is keyboard chrome too
    { sel: '#board', attr: ['aria-label', s.boardLabel] },

    // the operator readout
    { sel: '#towerChip .lbl', text: s.hudTowers },
    { sel: '#houseChip .lbl', text: s.hudHouses },
    { sel: '#mwChip .lbl', text: s.hudOutput },
    { sel: '#mwChip .unit', text: s.unitMW },
    { sel: '#streakChip .lbl', text: s.hudStreak },

    // archive
    { sel: '#viewArchive', attr: ['aria-label', s.archiveLabel] },
    { sel: '#viewArchive h2', text: s.archiveTitle },

    // drills
    { sel: '#viewDrills', attr: ['aria-label', s.yardLabel] },
    { sel: '#viewDrills h2', text: s.yardTitle },
    { sel: '#viewDrills .intro', text: s.yardIntro },

    // profile
    { sel: '#viewProfile', attr: ['aria-label', s.navProfile] },
    { sel: '#viewProfile h2', text: s.profileTitle },
    { sel: '#profileHelp .setting-text b', text: s.helpBtn },
    { sel: '#profileFeedback .setting-text b', text: s.feedbackBtn },
    { sel: '#profileSettings .setting-text b', text: s.settingsLabel },

    // the one confirmation in the game (STYLEGUIDE §7)
    { sel: '#studyOverlay', attr: ['aria-label', s.studyLabel] },
    { sel: '#studyOverlay h2', text: s.studyTitle },
    { sel: '#studyOverlay .intro', html: s.studyIntro },
    { sel: '#studyCancel', text: s.studyCancel },
    { sel: '#studyConfirm', text: s.studyConfirm },

    // settings
    { sel: '#helpBtn', attr: ['aria-label', s.helpBtn] },
    { sel: '#helpBtn', attr: ['title', s.helpBtn] },
    { sel: '#settingsBtn', attr: ['aria-label', s.settingsLabel] },
    { sel: '#settingsBtn', attr: ['title', s.settingsLabel] },
    { sel: '#settingsOverlay', attr: ['aria-label', s.settingsLabel] },
    { sel: '#settingsOverlay h2', text: s.settingsTitle },
    { sel: '#setSound .setting-text b', text: s.setSound },
    { sel: '#setHaptics .setting-text b', text: s.setHaptics },
    { sel: '#setMotion .setting-text b', text: s.setMotion },
    { sel: '#setContrast .setting-text b', text: s.setContrast },
    { sel: '#setText .setting-text b', text: s.setText },
    { sel: '#setUsage .setting-text b', text: s.setUsage },
    { sel: '#setLanguage .setting-text b', text: s.settingLanguage },
    { sel: '#feedbackBtn', text: s.feedbackBtn },
    { sel: '#privacyBtn', text: s.privacyBtn },
    { sel: '#settingsClose', text: s.settingsDone },

    // the drop box (GDD §13). The chips, the placeholders, the countdown and
    // the attached line are painted by FeedbackPanel, which owns their state.
    { sel: '#feedbackOverlay', attr: ['aria-label', s.feedbackLabel] },
    { sel: '#feedbackOverlay h2', text: s.feedbackTitle },
    { sel: '#fbIntro', text: s.feedbackIntro },
    { sel: '#fbText', attr: ['aria-label', s.feedbackMessageLabel] },
    { sel: '#fbContactLabel', text: s.feedbackContact },
    { sel: '#fbCancel', text: s.close },
    { sel: '#fbDoneClose', text: s.close },

    // language picker
    { sel: '#langOverlay', attr: ['aria-label', s.langLabel] },
    {
      sel: '#langOverlay h2',
      html: `<svg class="icon-glyph" aria-hidden="true"><use href="#iconGlobe" /></svg> ${s.langTitle}`,
    },
    { sel: '#langClose', text: s.close },

    // how to play
    { sel: '#helpOverlay', attr: ['aria-label', s.helpLabel] },
    { sel: '#helpOverlay h2', text: s.helpTitle },
    { sel: '#helpOverlay .intro', html: s.helpIntro },
    { sel: '#helpOverlay li:nth-child(1) div', html: s.helpRuleBuild },
    { sel: '#helpOverlay li:nth-child(2) div', html: s.helpRuleGround },
    { sel: '#helpOverlay li:nth-child(3) div', html: s.helpRuleShorter },
    { sel: '#helpOverlay li:nth-child(4) div', html: s.helpRuleDaily },
    { sel: '#yardOpenBtn', text: s.yardBtn },
    { sel: '#helpClose', text: s.close },
  ];
}

/**
 * Lay the current language's copy over the markup.
 *
 * Runs at boot and again on every language change, so it must stay
 * idempotent: every entry assigns, none appends. Selectors miss silently on
 * purpose — a moved element keeps its authored fallback rather than crashing
 * the boot, and the e2e suite reads the applied text, so a miss cannot hide
 * for long.
 */
export function applyStaticStrings(root: ParentNode = document): void {
  for (const entry of staticEntries(str())) {
    const el = root.querySelector(entry.sel);
    if (!el) continue;
    if (entry.text !== undefined) el.textContent = entry.text;
    if (entry.html !== undefined) el.innerHTML = entry.html;
    if (entry.attr) el.setAttribute(entry.attr[0], entry.attr[1]);
  }
}
